/**
 * TeachBud Lesson Generation Engine
 *
 * Each section is produced by a dedicated generator that creates content
 * specifically for the lesson's subject, grade, topic, objective, and
 * any additional teacher instructions — never generic placeholders.
 */

import { type LessonBlueprint, extractBlueprint } from './lesson-blueprint';

// ── Public types ───────────────────────────────────────────────────────────────

export type MultimediaMode = 'none' | 'video' | 'interactive' | 'both';

// ── ALIGNMENT RULE ────────────────────────────────────────────────────────────
// The Skill to Teach is the authoritative instructional target.
// Do not broaden it, replace it, reinterpret it, or substitute a related skill.
// Every instructional section (Warm-Up, Direct Instruction, Guided Practice,
// Independent Practice, Exit Ticket, Homework, Assessment, and Multimedia)
// must directly support the Skill to Teach. No section may introduce a
// different primary instructional skill.
// ─────────────────────────────────────────────────────────────────────────────

export interface LessonInputs {
  /**
   * The exact academic skill students are expected to learn — the single
   * authoritative instructional target. Passed UNCHANGED to every generation
   * and validation stage. The engine derives the lesson title, topic, learning
   * objective, vocabulary, examples, practice, and exit ticket from this field.
   *
   * Example: "Multiply a fraction by a whole number using visual models,
   *           repeated addition, and equations."
   */
  skillToTeach: string;
  grade: string;
  subject: string;
  standard: string;
  duration: string;
  instructions: string;
  /**
   * The specific strategy, method, or model the lesson must use.
   * Example: "partial quotients", "area model", "RACE strategy".
   * If omitted, auto-extracted from any "using X" phrase in skillToTeach.
   */
  requiredStrategy?: string;
  /**
   * Teacher-supplied learning objective. If omitted, defaults to
   * "Students will be able to [skillToTeach]."
   */
  learningObjective?: string;
  /** Rule 10: generate Below / On / Above tier versions of Independent Practice */
  tieredPractice?: boolean;
  /** Rule 11: embed multimedia resource recommendations */
  multimediaMode?: MultimediaMode;
  /** Curriculum source when lesson is generated from an uploaded curriculum */
  curriculumSource?: CurriculumSource;
}

// ── Structured Lesson Specification ───────────────────────────────────────────
//
// A LessonSpec is assembled from ALL user inputs before any generator runs.
// Every generator — lesson plan, activity sheet, assessment, presentation —
// must consume the same LessonSpec so content stays perfectly synchronized.

export interface LessonSpec {
  /** Primary instructional anchor — exact skill as entered, never altered */
  skillToTeach: string;
  /**
   * The required strategy/method/model (e.g. "partial quotients", "area model").
   * Extracted from "using X" in skillToTeach, or user-provided explicitly.
   * Passed to every generator and validator as a hard constraint.
   */
  requiredStrategy?: string;
  /** Learning objective — defaults to "Students will be able to [skill]." */
  learningObjective: string;
  grade: string;
  gradeLabel: string;
  subject: string;
  standard: string;
  /** Plain-language description of the standard (from known-standard lookup) */
  standardDescription?: string;
  duration: string;
  /** Additional teacher notes and preferences */
  instructions: string;
  tieredPractice?: boolean;
  /** Minimum problems per tier (used when tieredPractice is true) */
  tierMinProblems?: number;
  /** Maximum problems per tier (used when tieredPractice is true) */
  tierMaxProblems?: number;
  multimediaMode?: string;
  curriculumSource?: CurriculumSource;
}

/**
 * Build the single structured LessonSpec from raw LessonInputs.
 *
 * This is the one place where ALL user inputs are consolidated into a
 * canonical specification object. Every downstream generator (lesson plan,
 * activity sheet, assessment, presentation) receives this spec so they
 * remain perfectly synchronized — same skill, same strategy, same objective.
 */
export function buildLessonSpec(inputs: LessonInputs): LessonSpec {
  // Auto-extract required strategy from "using [the] X [strategy/method/model]" in skill
  let requiredStrategy = inputs.requiredStrategy?.trim();
  if (!requiredStrategy) {
    const m = inputs.skillToTeach.match(
      /\busing\s+(?:the\s+|a\s+)?([\w][\w\s\-\/]+?)(?=\s*(?:strategy|method|model|approach|algorithm|technique|formula)\b|\s*,|\s+and\s+[a-z]|\s*\.?\s*$)/i
    );
    if (m?.[1]) requiredStrategy = m[1].trim();
  }

  const g = inputs.grade;
  const gradeLabel =
    g === 'K' ? 'Kindergarten' :
    g === '1' ? '1st Grade' :
    g === '2' ? '2nd Grade' :
    g === '3' ? '3rd Grade' :
    g === '4' ? '4th Grade' :
    g === '5' ? '5th Grade' :
    g === '6' ? '6th Grade' :
    g === '7' ? '7th Grade' :
    g === '8' ? '8th Grade' :
    g === '9' ? '9th Grade' :
    g === '10' ? '10th Grade' :
    g === '11' ? '11th Grade' :
    g === '12' ? '12th Grade' : `Grade ${g}`;

  const skill = inputs.skillToTeach.trim().replace(/\.$/, '');
  const learningObjective =
    inputs.learningObjective?.trim() ||
    `Students will be able to ${skill.charAt(0).toLowerCase() + skill.slice(1)}.`;

  const standardDescription = lookupStandardPlainText(inputs.standard.trim()) || undefined;

  return {
    skillToTeach: inputs.skillToTeach.trim(),
    requiredStrategy,
    learningObjective,
    grade: inputs.grade,
    gradeLabel,
    subject: inputs.subject,
    standard: inputs.standard.trim(),
    standardDescription,
    duration: inputs.duration,
    instructions: inputs.instructions,
    tieredPractice: inputs.tieredPractice,
    multimediaMode: inputs.multimediaMode,
    curriculumSource: inputs.curriculumSource,
  };
}

export interface TieredPractice {
  belowLevel: string;
  onLevel: string;
  aboveLevel: string;
}

/** Result of validating one lesson section against the intended instructional target. */
export interface ValidationSectionResult {
  section: string;
  status: 'PASS' | 'FAIL';
  requiredConcept: string;
  excludedConceptsDetected: string[];
  hasPlaceholders: boolean;
  notes: string;
}

// ── Generation trace ──────────────────────────────────────────────────────────

/**
 * Internal record of how a lesson was produced.
 * Populated by assembleAiLesson() and surfaced in the developer report panel.
 * Never shown to students; safe to serialise alongside the lesson object.
 */
export interface GenerationTrace {
  /** Whether the lesson came from the AI endpoint or the local template generator. */
  lessonSource: 'ai-endpoint' | 'local-template';
  /** Whether lesson.spec was populated — always true for AI-generated lessons. */
  specPresent: boolean;
  /** Whether lesson.blueprint was extracted from the AI text — always true for AI lessons. */
  blueprintExtracted: boolean;
  /** Total InstructionalItems extracted across all blueprint sections. */
  blueprintItemCount: number;
  /** Alignment validation outcome as reported by the server. */
  validationResult?: 'passed' | 'corrected' | 'failed' | 'skipped';
  /** Section names that were auto-regenerated after failing alignment validation. */
  sectionsRegenerated?: string[];
  /** Wall-clock ms from start of AI API call to end of assembleAiLesson(). Set in LessonPlanner. */
  generationTimeMs?: number;
}

export interface GeneratedLesson {
  title: string;
  gradeLabel: string;
  grade: string;
  subject: string;
  topic: string;
  /** The authoritative instructional target — passed through unchanged from LessonInputs */
  skillToTeach: string;
  standard: string;
  duration: string;
  objective: string;
  successCriteria: string;
  materials: string;
  vocabulary: string;
  warmUp: string;
  directInstruction: string;
  guidedPractice: string;
  independentPractice: string;
  formativeAssessment: string;
  exitTicket: string;
  differentiation: string;
  extensionActivity: string;
  homework: string;
  reflectionNotes: string;
  /** Populated when tieredPractice input is true */
  tieredPractice?: TieredPractice;
  /** Populated when multimediaMode is not 'none' */
  multimediaResources?: string;
  /** Quick at-a-glance summary — generated for every lesson */
  lessonAtAGlance?: LessonAtAGlance;
  /** Lesson-specific teacher preparation tasks */
  teacherPrep?: string;
  /** Materials essential to teach the lesson as written */
  requiredMaterials?: string;
  /** Optional enrichment, differentiation, or multimedia materials */
  optionalMaterials?: string;
  /** Curriculum traceability (populated for curriculum-based lessons only) */
  curriculumSource?: CurriculumSource;
  /** Pre-computed section times guaranteed to sum to lesson duration */
  sectionTimes?: SectionTimes;
  /** Plain-language description of the standard when a recognized code is detected */
  standardPlainText?: string;
  /** Structured blueprint — single source of truth consumed by all companion generators */
  blueprint?: LessonBlueprint;
  /** Alignment and content-quality validation results (populated after every generation) */
  validationReport?: ValidationSectionResult[];
  /**
   * Structured specification assembled from all user inputs before generation.
   * Consumed by every companion generator (activity sheet, assessment, presentation)
   * to guarantee they all stay synchronized on the same skill, strategy, and objective.
   */
  spec?: LessonSpec;
  /**
   * Whether the AI alignment validator confirmed every key section teaches the
   * requested skill. false = one or more sections were auto-regenerated.
   * undefined = validator did not run (local generation or API pre-validation era).
   */
  alignmentValidated?: boolean;
  /** Sections that were auto-regenerated after failing alignment validation */
  regeneratedSections?: string[];
  /**
   * True when the blueprint completeness gate fired and one or more core sections
   * still had insufficient parseable items after automatic regeneration.
   * When true, companion resources (Activity Sheet, Presentation, Assessment) may
   * be incomplete. The teacher should regenerate the lesson before creating resources.
   * false = gate passed (either no issues found, or issues were fixed by regeneration).
   */
  blueprintIncomplete?: boolean;
  /**
   * The specific section keys that failed the blueprint completeness gate after
   * regeneration. Only populated when blueprintIncomplete === true.
   * Example: ['guidedPractice', 'independentPractice']
   */
  incompleteFields?: string[];
  /**
   * Section keys the teacher has chosen to hide in the lesson plan view.
   * Hidden sections are dimmed in the editor and excluded from print.
   * Hiding never deletes content from the lesson or LessonBlueprint.
   * Values are section keys matching the fields of GeneratedLesson (e.g. 'warmUp', 'guidedPractice').
   */
  hiddenSections?: string[];
  /** Internal generation trace — consumed by the developer report panel.
   *  Undefined for lessons loaded from saved resources or produced by the local generator. */
  trace?: GenerationTrace;
  /**
   * Standard selection source.
   * 'picked'   — teacher used the Standards Picker and selected a verified official standard.
   * 'manual'   — teacher typed the standard free-form (teacher-entered).
   * 'district' — teacher identified this as a district-created or district-adopted standard.
   * undefined  — standard was not entered (pre-picker lessons or empty field).
   */
  standardSource?: 'picked' | 'manual' | 'district';
  /** Standards framework when standardSource is 'picked' (e.g., 'CCSS') */
  standardFramework?: string;
  /** Standards version/year when standardSource is 'picked' (e.g., '2010') */
  standardVersion?: string;
  /** Exact standard code when standardSource is 'picked' (e.g., '3.OA.A.1') */
  standardCode?: string;
}

// ── Curriculum source traceability ───────────────────────────────────────────

export interface CurriculumSource {
  id?: number;
  title: string;
  publisher?: string;
  gradeLevel?: string;
  unitInfo?: string;
  fileName?: string;
  sourceUrl?: string;
  scope: 'my' | 'team' | 'school' | 'district';
}

// ── Lesson at a Glance ────────────────────────────────────────────────────────

export interface LessonAtAGlance {
  objective: string;
  mainStudentTasks: string[];
  mainTeacherActions: string[];
  requiredResources: string[];
  duration: string;
}

// ── Section times ─────────────────────────────────────────────────────────────

export interface SectionTimes {
  warmUp: number;
  directInstruction: number;
  /** Carved out of instructional time when multimediaMode is not 'none' */
  multimedia?: number;
  guidedPractice: number;
  independentPractice: number;
  exitTicket: number;
}

// ── Internal context ────────────────────────────────────────────────────────

interface Ctx {
  /** The exact skill from inputs — authoritative, never broadened or reinterpreted */
  skillToTeach: string;
  title: string;
  grade: string;
  gradeNum: number;
  gradeLabel: string;
  subject: string;
  /** Human-readable topic label derived from skillToTeach (strips "using …" method phrases) */
  topic: string;
  /** Full skillToTeach lowercased — the primary signal for all topic detection */
  tl: string;
  standard: string;
  dMin: number;
  /** The skill in sentence-completion form: "Students will be able to [obj]." */
  obj: string;
  instructions: string;
  instrNote: string;
  isK: boolean;
  isYoung: boolean; // K–2
  isElem: boolean;  // 3–5
  isMiddle: boolean; // 6–8
  isHigh: boolean;  // 9–12
  t: (pct: number) => number; // time allocation helper
}

/**
 * Derive a concise human-readable lesson title from a Skill to Teach statement.
 * Strips trailing "using X, Y, and Z" / "through X" method phrases — these
 * describe HOW the skill is taught, not WHAT the skill is.
 *
 * Examples:
 *   "Multiply a fraction by a whole number using visual models, repeated
 *    addition, and equations." → "Multiply a fraction by a whole number"
 *   "Compare fractions with unlike denominators using benchmark fractions."
 *    → "Compare fractions with unlike denominators"
 *   "Identify the main idea and supporting details in an informational text."
 *    → unchanged (no method phrase present)
 */
function deriveTitle(skill: string): string {
  const t = skill
    .replace(/,?\s+using\s+.+$/i, '')
    .replace(/,?\s+through\s+.+$/i, '')
    .replace(/\.$/, '')
    .trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function makeCtx(inputs: LessonInputs): Ctx {
  const { skillToTeach, grade, subject, standard, duration, instructions } = inputs;
  const gradeNum = grade === 'K' ? 0 : parseInt(grade, 10);
  const gradeLabel = grade === 'K' ? 'Kindergarten' : `Grade ${grade}`;
  const dMin = parseInt(duration, 10) || 45;

  // ── Derive title, topic, and obj from the single authoritative skill ────────
  // The skill is stored and passed through UNCHANGED. Everything else derives
  // from it — the lesson title, topic display label, and SWBAT objective are
  // all automatically correct once the skill is set precisely.
  const title = deriveTitle(skillToTeach);
  const topic = title; // display label — same as derived title

  // obj is the skill in sentence-completion form for "Students will be able to [obj]."
  // Strip any SWBAT prefix the teacher may have typed to prevent double-prefixing.
  const rawSkill = skillToTeach.replace(/\.$/, '').trim();
  const obj = rawSkill
    .replace(/^students\s+will\s+be\s+able\s+to\s+/i, '')
    .replace(/^students\s+will\s+/i, '')
    .trim() || skillToTeach;

  // instrNote is intentionally empty — additional instructions are generation
  // guidance only and must NEVER appear as literal text in the finished lesson.
  const instrNote = '';

  // tl is the full skillToTeach lowercased — the sole topic-detection signal.
  // Using the complete skill (not an abbreviated title) gives every topic
  // detector the maximum specificity: "multiply a fraction by a whole number
  // using visual models" contains all keywords needed to resolve the subtopic.
  const tl = skillToTeach.toLowerCase();

  // Strip any label contamination that can appear when the standard input field is
  // accidentally pre-filled with form-label text (e.g. "Skill to Teach CCSS 4.NF.A.1").
  const cleanStandard = standard
    .replace(/^skill\s+to\s+teach\s*:?\s*/i, '')
    .replace(/^academic\s+standard\s*:?\s*/i, '')
    .trim();

  return {
    skillToTeach, title, grade, gradeNum, gradeLabel, subject, topic,
    tl,
    standard: cleanStandard, dMin, obj, instructions, instrNote,
    isK: gradeNum === 0,
    isYoung: gradeNum <= 2,
    isElem: gradeNum >= 3 && gradeNum <= 5,
    isMiddle: gradeNum >= 6 && gradeNum <= 8,
    isHigh: gradeNum >= 9,
    t: (pct: number) => Math.round(dMin * pct),
  };
}

// ── Topic detection ─────────────────────────────────────────────────────────

type MathTopic =
  | 'fractions-compare' | 'fractions-add-sub' | 'fractions-multiply-divide'
  | 'fractions-equivalent' | 'fractions-general' | 'place-value' | 'decimals'
  | 'multiplication' | 'multiplication-partial-products'
  | 'division' | 'geometry-area-perimeter' | 'geometry-angles'
  | 'geometry-shapes' | 'algebra-expressions' | 'algebra-equations' | 'integers'
  | 'ratios-proportions' | 'percentages' | 'data-statistics' | 'measurement'
  | 'addition-subtraction' | 'number-sense' | 'general';

function mathTopic(tl: string): MathTopic {
  if ((tl.includes('compar') || tl.includes('order')) && tl.includes('fract')) return 'fractions-compare';
  if ((tl.includes('add') || tl.includes('subtract')) && tl.includes('fract')) return 'fractions-add-sub';
  // Equivalent-fraction check MUST precede the generic fraction-multiply check.
  // "Generate equivalent fractions by multiplying the numerator and denominator by the
  // same number" contains "multipl" AND "fract" — without this ordering it mis-routes
  // to fractions-multiply-divide. Any of the following patterns confirms equivalence intent:
  if (
    tl.includes('equivalent fract') ||
    tl.includes('equivalent fraction') ||
    (tl.includes('same number') && (tl.includes('numerator') || tl.includes('denominator')) && tl.includes('fract')) ||
    (tl.includes('same nonzero') && tl.includes('fract')) ||
    (tl.includes('same whole number') && tl.includes('fract')) ||
    (tl.includes('same factor') && tl.includes('fract') && (tl.includes('numerator') || tl.includes('denominator')))
  ) return 'fractions-equivalent';
  if ((tl.includes('multipl') || tl.includes('divid')) && tl.includes('fract')) return 'fractions-multiply-divide';
  if (tl.includes('fract') || tl.includes('numerator') || tl.includes('denominator')) return 'fractions-general';
  // ── Primary arithmetic operations ─────────────────────────────────────────
  // These must be checked BEFORE topic modifiers (place value, decimal, etc.).
  // Many CCSS/state standards include "using place value strategies" or "using
  // properties of operations" as method language even when the lesson is about
  // division or multiplication — not place value per se. Without this ordering,
  // "Divide a 4-digit number … explain using place value" routes to place-value.
  if (tl.includes('partial quot') || tl.includes('divis') || tl.includes('divid') || tl.includes('quotient') || tl.includes('remaind')) return 'division';
  if (tl.includes('partial product') && tl.includes('multipl')) return 'multiplication-partial-products';
  if (tl.includes('multipl') && !tl.includes('divid')) return 'multiplication';
  if (tl.includes('add') || tl.includes('subtract') || tl.includes('sum') || tl.includes('difference')) return 'addition-subtraction';
  // ── Topic modifiers (only reached when no primary operation matched above) ──
  if (tl.includes('place value') || tl.includes('place-value') || tl.includes('rounding') || tl.includes('round')) return 'place-value';
  if (tl.includes('decimal') || tl.includes('tenth') || tl.includes('hundredth')) return 'decimals';
  if (tl.includes('area') || tl.includes('perimeter') || tl.includes('circumference') || tl.includes('volume') || tl.includes('surface area')) return 'geometry-area-perimeter';
  if (tl.includes('angle') || tl.includes('supplementary') || tl.includes('complementary') || tl.includes('triangle') && tl.includes('degree')) return 'geometry-angles';
  if (tl.includes('shape') || tl.includes('polygon') || tl.includes('quadrilateral') || tl.includes('geometry') || tl.includes('circle')) return 'geometry-shapes';
  if (tl.includes('expression') || tl.includes('variable') || tl.includes('evaluate') || tl.includes('order of operations')) return 'algebra-expressions';
  if (tl.includes('equation') || tl.includes('solve') || tl.includes('inequality') || tl.includes('linear')) return 'algebra-equations';
  if (tl.includes('integer') || tl.includes('negative') || tl.includes('absolute value')) return 'integers';
  if (tl.includes('ratio') || tl.includes('proportion') || tl.includes('rate') || tl.includes('unit rate')) return 'ratios-proportions';
  if (tl.includes('percent') || tl.includes('%') || tl.includes('discount') || tl.includes('tax') || tl.includes('interest')) return 'percentages';
  if (tl.includes('data') || tl.includes('graph') || tl.includes('statistic') || tl.includes('mean') || tl.includes('median') || tl.includes('mode')) return 'data-statistics';
  if (tl.includes('measure') || tl.includes('length') || tl.includes('weight') || tl.includes('capacity') || tl.includes('conver')) return 'measurement';
  if (tl.includes('number') || tl.includes('count') || tl.includes('digit')) return 'number-sense';
  return 'general';
}

type ELATopic =
  | 'main-idea' | 'authors-purpose' | 'theme' | 'text-structure' | 'inference'
  | 'narrative' | 'opinion-argument' | 'informational' | 'grammar-parts-of-speech'
  | 'grammar-punctuation' | 'grammar-sentences' | 'figurative-language'
  | 'vocabulary-context' | 'poetry' | 'compare-contrast' | 'summarizing' | 'general';

function elaTopic(tl: string): ELATopic {
  if (tl.includes('main idea') || tl.includes('supporting detail')) return 'main-idea';
  if (tl.includes("author's purpose") || tl.includes('author purpose')) return 'authors-purpose';
  if (tl.includes('theme') || (tl.includes('moral') && tl.includes('story'))) return 'theme';
  if (tl.includes('text structure') || tl.includes('text feature')) return 'text-structure';
  if (tl.includes('infer') || tl.includes('draw conclusion')) return 'inference';
  if (tl.includes('narrative') || tl.includes('plot') || tl.includes('story element') || tl.includes('fiction') || tl.includes('character development')) return 'narrative';
  if (tl.includes('opinion') || tl.includes('argument') || tl.includes('persuasive') || tl.includes('claim') || tl.includes('counterclaim')) return 'opinion-argument';
  if (tl.includes('informational') || tl.includes('nonfiction') || tl.includes('expository') || tl.includes('research writing')) return 'informational';
  if (tl.includes('noun') || tl.includes('verb') || tl.includes('adjective') || tl.includes('adverb') || tl.includes('pronoun') || tl.includes('parts of speech')) return 'grammar-parts-of-speech';
  if (tl.includes('punctuation') || tl.includes('comma') || tl.includes('apostrophe') || tl.includes('quotation mark')) return 'grammar-punctuation';
  if (tl.includes('sentence') || tl.includes('fragment') || tl.includes('run-on') || tl.includes('compound') || tl.includes('complex sentence')) return 'grammar-sentences';
  if (tl.includes('figurative') || tl.includes('simile') || tl.includes('metaphor') || tl.includes('idiom') || tl.includes('personification') || tl.includes('hyperbole') || tl.includes('alliteration')) return 'figurative-language';
  if (tl.includes('vocabulary') || tl.includes('context clue') || tl.includes('word meaning') || tl.includes('prefix') || tl.includes('suffix') || tl.includes('root word')) return 'vocabulary-context';
  if (tl.includes('poem') || tl.includes('poetry') || tl.includes('rhyme') || tl.includes('stanza') || tl.includes('haiku') || tl.includes('sonnet')) return 'poetry';
  if ((tl.includes('compare') || tl.includes('contrast')) && !tl.includes('fract')) return 'compare-contrast';
  if (tl.includes('summari') || tl.includes('retell') || tl.includes('paraphrase')) return 'summarizing';
  return 'general';
}

type ScienceTopic =
  | 'cells' | 'ecosystems' | 'adaptations' | 'life-cycles' | 'plants'
  | 'weather-climate' | 'rocks-minerals' | 'solar-system' | 'forces-motion'
  | 'energy' | 'matter-states' | 'water-cycle' | 'human-body'
  | 'scientific-method' | 'food-chains' | 'genetics' | 'general';

function sciTopic(tl: string): ScienceTopic {
  // ── Specific subtopics that belong inside broader umbrella categories ─────
  // These MUST be checked before their umbrella parents so that a lesson
  // titled "Food Chains and Food Webs" with topic "Ecosystems" resolves to
  // 'food-chains', not 'ecosystems'. Same principle applies to water-cycle
  // vs weather-climate. Umbrella catches below only fire when no specific
  // subtopic keyword is present anywhere in the combined instructional target.

  // Subtopics of Ecosystems/Life Science
  if (tl.includes('food chain') || tl.includes('food web') || tl.includes('producer') || tl.includes('consumer') || tl.includes('decomposer') || tl.includes('predator') || tl.includes('prey')) return 'food-chains';
  if (tl.includes('adapt') || tl.includes('camouflage') || tl.includes('survival of the fittest') || (tl.includes('survival') && !tl.includes('ecosystem'))) return 'adaptations';
  if (tl.includes('life cycle') || tl.includes('lifecycle') || tl.includes('metamorphosis')) return 'life-cycles';
  if (tl.includes('photosynthesis') || tl.includes('pollination') || tl.includes('chlorophyll') || (tl.includes('plant') && !tl.includes('ecosystem'))) return 'plants';
  if (tl.includes('genetic') || tl.includes('dna') || tl.includes('trait') || tl.includes('inherit') || tl.includes('dominant') || tl.includes('recessive')) return 'genetics';
  // Umbrella: Ecosystems (fires only when no specific subtopic matched above)
  if (tl.includes('ecosystem') || tl.includes('biome') || tl.includes('habitat') || (tl.includes('community') && tl.includes('organism'))) return 'ecosystems';

  // Subtopic of Weather/Climate
  if (tl.includes('water cycle') || tl.includes('evaporation') || tl.includes('condensation') || (tl.includes('precipitation') && !tl.includes('weather'))) return 'water-cycle';
  // Umbrella: Weather/Climate
  if (tl.includes('weather') || tl.includes('climate') || tl.includes('atmosphere') || tl.includes('cloud') || tl.includes('storm') || tl.includes('hurricane') || tl.includes('tornado')) return 'weather-climate';

  // Other specific topics (no umbrella ordering issues)
  if (tl.includes('cell') || tl.includes('organelle') || tl.includes('mitosis') || tl.includes('membrane')) return 'cells';
  if (tl.includes('rock') || tl.includes('mineral') || tl.includes('soil') || tl.includes('erosion') || tl.includes('igneous') || tl.includes('metamorphic') || tl.includes('sedimentary')) return 'rocks-minerals';
  if (tl.includes('solar system') || tl.includes('planet') || tl.includes('moon') || tl.includes('star') || tl.includes('space') || tl.includes('orbit') || tl.includes('galaxy')) return 'solar-system';
  if (tl.includes('force') || tl.includes('motion') || tl.includes('newton') || tl.includes('friction') || tl.includes('gravity') || tl.includes('velocity') || tl.includes('acceleration') || tl.includes('push') || tl.includes('pull')) return 'forces-motion';
  if (tl.includes('energy') || tl.includes('kinetic') || tl.includes('potential') || tl.includes('thermal') || tl.includes('sound') || tl.includes('light') || tl.includes('electrical')) return 'energy';
  if (tl.includes('matter') || tl.includes('solid') || tl.includes('liquid') || tl.includes('gas') || tl.includes('state') || tl.includes('particle') || tl.includes('atom') || tl.includes('molecule')) return 'matter-states';
  if (tl.includes('human body') || tl.includes('digestive') || tl.includes('respiratory') || tl.includes('circulatory') || tl.includes('skeletal') || tl.includes('muscular') || tl.includes('nervous') || tl.includes('organ system')) return 'human-body';
  if (tl.includes('scientific method') || tl.includes('hypothesis') || (tl.includes('experiment') && !tl.includes('cell'))) return 'scientific-method';
  if (tl.includes('plant') || tl.includes('seed') || tl.includes('root')) return 'plants'; // catch-all for plain 'plant' in ecosystem context
  return 'general';
}

type SSTopic =
  | 'ancient-civilizations' | 'american-revolution' | 'civil-war' | 'westward-expansion'
  | 'immigration' | 'world-war' | 'civil-rights' | 'us-government' | 'geography'
  | 'economics' | 'native-american' | 'colonial-america' | 'reconstruction'
  | 'industrial-revolution' | 'general';

function ssTopic(tl: string): SSTopic {
  if (tl.includes('ancient') || tl.includes('egypt') || tl.includes('greece') || tl.includes('rome') || tl.includes('mesopotamia') || tl.includes('greek') || tl.includes('roman')) return 'ancient-civilizations';
  if ((tl.includes('american revolution') || tl.includes('independence')) && !tl.includes('civil')) return 'american-revolution';
  if (tl.includes('civil war') || tl.includes('confedera') || (tl.includes('slavery') && !tl.includes('abolit')) || tl.includes('lincoln') || tl.includes('emancipation')) return 'civil-war';
  if (tl.includes('westward') || tl.includes('manifest destiny') || tl.includes('frontier') || tl.includes('pioneer') || tl.includes('oregon trail') || tl.includes('gold rush')) return 'westward-expansion';
  if (tl.includes('immigr') || (tl.includes('migration') && !tl.includes('bird')) || tl.includes('ellis island') || tl.includes('citizenship') || tl.includes('culture') && tl.includes('american')) return 'immigration';
  if (tl.includes('world war') || tl.includes('ww1') || tl.includes('ww2') || tl.includes('wwi') || tl.includes('wwii') || tl.includes('holocaust') || tl.includes('d-day')) return 'world-war';
  if (tl.includes('civil rights') || tl.includes('martin luther king') || tl.includes('rosa parks') || tl.includes('segregat') || tl.includes('discrimination') && tl.includes('race')) return 'civil-rights';
  if (tl.includes('government') || tl.includes('constitution') || tl.includes('branch') && tl.includes('govern') || tl.includes('congress') || tl.includes('democracy') || tl.includes('bill of rights') || tl.includes('amendment')) return 'us-government';
  if (tl.includes('geography') || tl.includes('map') || tl.includes('continent') || tl.includes('region') || tl.includes('landform') || tl.includes('latitude') || tl.includes('longitude')) return 'geography';
  if (tl.includes('econom') || tl.includes('supply') || tl.includes('demand') || tl.includes('trade') || tl.includes('market') || (tl.includes('resource') && tl.includes('goods'))) return 'economics';
  if (tl.includes('native american') || tl.includes('indigenous') || tl.includes('tribe') || tl.includes('cherokee') || tl.includes('sioux') || tl.includes('iroquois')) return 'native-american';
  if (tl.includes('colonial') || tl.includes('colony') || tl.includes('pilgrim') || tl.includes('jamestown') || tl.includes('thirteen colonies')) return 'colonial-america';
  if (tl.includes('reconstruction') || tl.includes('freedman')) return 'reconstruction';
  if (tl.includes('industrial revolution') || (tl.includes('factory') && tl.includes('histor'))) return 'industrial-revolution';
  return 'general';
}

// ═══════════════════════════════════════════════════════════════════════════════
// MATH GENERATORS
// ═══════════════════════════════════════════════════════════════════════════════

function generateMathVocabulary(ctx: Ctx): string {
  const type = mathTopic(ctx.tl);
  const { topic } = ctx;

  const vocabSets: Record<string, string[]> = {
    'fractions-compare': [
      'Numerator — The top number in a fraction; tells how many equal parts are being counted. Example: In ³⁄₄, the numerator is 3.',
      'Denominator — The bottom number in a fraction; tells the total number of equal parts in the whole. Example: In ³⁄₄, the denominator is 4.',
      'Equivalent fractions — Fractions that represent the same value even though they look different. Example: ¹⁄₂ = ²⁄₄ = ⁴⁄₈.',
      'Benchmark fraction — A common reference fraction such as 0, ¹⁄₂, or 1 used to estimate or compare other fractions.',
      'Common denominator — A shared multiple of two denominators used to compare fractions directly. Example: 12 is a common denominator for ¹⁄₃ and ¹⁄₄.',
      'is greater than (>) — Symbol showing the fraction on the left is larger in value. Example: ³⁄₄ > ¹⁄₄.',
      'is less than (<) — Symbol showing the fraction on the left is smaller in value. Example: ¹⁄₃ < ²⁄₃.',
    ],
    'fractions-add-sub': [
      'Numerator — The top number in a fraction; the part being counted.',
      'Denominator — The bottom number; shows the total equal parts in the whole.',
      'Common denominator — A shared multiple of two or more denominators; required before adding or subtracting unlike fractions.',
      'Least common denominator (LCD) — The smallest common denominator. Example: LCD of ¹⁄₄ and ¹⁄₆ is 12.',
      'Equivalent fractions — Fractions with the same value written in different forms; used to create common denominators.',
      'Mixed number — A whole number combined with a proper fraction. Example: 2¹⁄₃.',
      'Improper fraction — A fraction where the numerator is greater than or equal to the denominator. Example: ⁷⁄₃.',
      'Simplest form — A fraction where the numerator and denominator share no common factor other than 1. Example: ²⁄₆ simplified is ¹⁄₃.',
    ],
    'fractions-multiply-divide': [
      'Numerator — The top number of a fraction; the part being counted.',
      'Denominator — The bottom number; the total equal parts.',
      'Product — The result of multiplying. Example: ²⁄₃ × ³⁄₄ = ⁶⁄₁₂ = ¹⁄₂.',
      'Reciprocal — A fraction flipped upside down; used when dividing fractions. Example: the reciprocal of ²⁄₃ is ³⁄₂.',
      'Simplify — Reducing a fraction to its lowest terms by dividing numerator and denominator by their GCF.',
      'GCF (Greatest Common Factor) — The largest factor shared by two numbers. Example: GCF of 6 and 8 is 2.',
      'Mixed number — A whole number combined with a fraction, such as 1³⁄₄.',
    ],
    'fractions-equivalent': [
      'Equivalent fractions — Fractions that name the same part of a whole, even though the numerator and denominator differ. Example: ¹⁄₂ = ²⁄₄ = ³⁄₆.',
      'Numerator — The top number of a fraction.',
      'Denominator — The bottom number of a fraction.',
      'Multiply (scaling) — Multiplying numerator and denominator by the same non-zero number produces an equivalent fraction.',
      'Divide (simplifying) — Dividing numerator and denominator by their GCF gives the simplest form.',
      'Simplest form — An equivalent fraction where the GCF of numerator and denominator is 1.',
      'GCF (Greatest Common Factor) — The largest number that divides evenly into two numbers.',
    ],
    'place-value': [
      `Digit — Any of the symbols 0–9 used to write numbers.`,
      `Place value — The value of a digit based on its position in a number. Example: in 3,472, the 4 is in the hundreds place, so its value is 400.`,
      `Period — A group of three digits separated by a comma in a large number (ones, thousands, millions, etc.).`,
      `Standard form — Writing a number using digits. Example: 45,638.`,
      `Expanded form — Writing a number to show the value of each digit. Example: 40,000 + 5,000 + 600 + 30 + 8.`,
      `Word form — Writing a number in words. Example: forty-five thousand, six hundred thirty-eight.`,
      `Rounding — Approximating a number to a given place value using the halfway-point rule.`,
    ],
    'decimals': [
      `Decimal — A number that uses a decimal point to separate the whole number from the fractional part.`,
      `Decimal point — The dot separating the whole-number part from the decimal part of a number.`,
      `Tenths — The first place to the right of the decimal point; represents parts out of 10. Example: 0.3 = ³⁄₁₀.`,
      `Hundredths — The second place to the right of the decimal point; represents parts out of 100. Example: 0.07 = ⁷⁄₁₀₀.`,
      `Thousandths — The third place to the right; represents parts out of 1,000.`,
      `Equivalent decimals — Decimals that name the same value. Example: 0.5 = 0.50 = 0.500.`,
      `Comparing decimals — Determining which decimal is greater, using place value column by column.`,
    ],
    'multiplication': ctx.gradeNum <= 4 ? [
      `Factor — A number being multiplied. Example: in 6 × 7, both 6 and 7 are factors.`,
      `Product — The result of multiplying two or more factors. Example: 6 × 7 = 42.`,
      `Array — Objects arranged in equal rows and columns to model multiplication.`,
      `Skip counting — Counting by equal groups (e.g., 4, 8, 12, 16) to find a product.`,
      `Commutative property — The order of factors doesn't change the product. Example: 3 × 8 = 8 × 3.`,
      `Distributive property — A factor can be broken apart to make multiplication easier. Example: 6 × 14 = 6 × 10 + 6 × 4 = 84.`,
    ] : [
      `Factor — A number being multiplied in a multiplication problem.`,
      `Product — The result of multiplication.`,
      `Distributive property — Breaking a factor into parts: a(b + c) = ab + ac. Example: 7 × 23 = 7 × 20 + 7 × 3 = 161.`,
      `Commutative property — Changing the order of factors doesn't change the product.`,
      `Associative property — Grouping factors differently doesn't change the product. Example: (2 × 3) × 5 = 2 × (3 × 5).`,
      `Partial products — An algorithm that multiplies each place-value part separately before adding.`,
      `Estimate — An approximate answer found by rounding factors before multiplying.`,
    ],
    'division': [
      `Dividend — The number being divided. Example: in 48 ÷ 6, 48 is the dividend.`,
      `Divisor — The number by which you divide. Example: in 48 ÷ 6, 6 is the divisor.`,
      `Quotient — The result of dividing. Example: 48 ÷ 6 = 8; 8 is the quotient.`,
      `Remainder — What is left over when a number cannot be divided evenly. Example: 25 ÷ 4 = 6 remainder 1.`,
      `Long division — An algorithm for dividing multi-digit numbers step by step: divide, multiply, subtract, bring down.`,
      `Inverse operations — Multiplication and division undo each other. Example: 6 × 8 = 48, so 48 ÷ 8 = 6.`,
    ],
    'geometry-area-perimeter': [
      `Perimeter — The total distance around the outside of a shape; found by adding all side lengths.`,
      `Area — The amount of surface inside a shape; measured in square units (cm², m², in², ft²).`,
      `Base — One side of a shape used as a reference for calculating area or perimeter.`,
      `Height — The perpendicular distance from the base to the opposite side or vertex.`,
      `Formula — A rule expressed with symbols or numbers. Example: Area of rectangle = length × width (A = lw).`,
      ctx.tl.includes('circle') || ctx.tl.includes('circumference')
        ? 'Circumference — The perimeter of a circle; C = πd or C = 2πr.'
        : 'Square unit — The unit for measuring area (e.g., 1 cm × 1 cm = 1 cm²).',
      ctx.tl.includes('volume') || ctx.tl.includes('surface')
        ? 'Volume — The amount of space inside a 3-D shape; measured in cubic units (cm³, ft³).'
        : 'Composite figure — A shape made up of two or more simpler shapes.',
    ],
    'geometry-angles': [
      `Angle — The figure formed by two rays that share a common endpoint called the vertex.`,
      `Vertex — The corner point of an angle or shape.`,
      `Degree (°) — The unit for measuring angles; a full turn is 360°.`,
      `Right angle — An angle that measures exactly 90°.`,
      `Acute angle — An angle that measures less than 90°.`,
      `Obtuse angle — An angle that measures more than 90° but less than 180°.`,
      `Supplementary angles — Two angles whose measures add up to 180°.`,
      `Complementary angles — Two angles whose measures add up to 90°.`,
    ],
    'algebra-equations': [
      `Variable — A letter that represents an unknown value. Example: In 2x + 3 = 11, x is the variable.`,
      `Equation — A mathematical statement showing two expressions are equal. Example: 3x − 5 = 10.`,
      `Solution — The value of the variable that makes an equation true. Example: x = 5.`,
      `Inverse operations — Operations that undo each other; used to isolate the variable (addition/subtraction; multiplication/division).`,
      `Coefficient — The number multiplied by a variable. Example: In 4x, the coefficient is 4.`,
      `Constant — A fixed value in an expression or equation (a number with no variable).`,
      `Isolate the variable — Rearranging an equation so the variable is alone on one side.`,
    ],
    'integers': [
      `Integer — Any whole number, its opposite, or zero. Examples: …−3, −2, −1, 0, 1, 2, 3…`,
      `Positive integer — An integer greater than zero; located to the right of 0 on a number line.`,
      `Negative integer — An integer less than zero; located to the left of 0 on a number line.`,
      `Opposite integers — Two integers the same distance from 0 but on different sides. Example: −5 and 5.`,
      `Absolute value — A number's distance from 0; always non-negative. Example: |−7| = 7; |7| = 7.`,
      `Sum — The result of addition. Adding integers with opposite signs requires comparing absolute values.`,
      `Number line — A visual tool showing integers in order that helps with adding and subtracting integers.`,
    ],
    'ratios-proportions': [
      `Ratio — A comparison of two quantities using division; can be written as a:b, a/b, or "a to b."`,
      `Rate — A ratio comparing two quantities with different units. Example: 60 miles per hour.`,
      `Unit rate — A rate with a denominator of 1. Example: $3.50 per pound.`,
      `Proportion — An equation stating that two ratios are equal. Example: ²⁄₃ = ⁴⁄₆.`,
      `Cross-multiplication — A method for solving proportions by multiplying across the equals sign.`,
      `Equivalent ratios — Ratios that simplify to the same value. Example: 2:4 = 1:2.`,
      `Scale — A ratio between the measurements in a diagram and the actual measurements.`,
    ],
    'percentages': [
      `Percent (%) — A ratio out of 100; the word literally means "per hundred." Example: 45% = 45/100 = 0.45.`,
      `Decimal equivalent — A percent written as a decimal. Example: 35% = 0.35.`,
      `Fraction equivalent — A percent written as a fraction in simplest form. Example: 25% = ¹⁄₄.`,
      `Part — The amount that is a percentage of the whole.`,
      `Whole — The total amount (100%); the base of the percent calculation.`,
      `Percent of change — Shows how much a quantity increased or decreased: (change ÷ original) × 100.`,
      `Discount — A percent decrease in price; calculated as percent × original price.`,
    ],
    'data-statistics': [
      `Data — Collected information used for analysis.`,
      `Mean — The average; sum of all data values ÷ number of values.`,
      `Median — The middle value when data is ordered from least to greatest.`,
      `Mode — The value that appears most often in a data set.`,
      `Range — The difference between the greatest and least values in a data set.`,
      `Outlier — A data point that is significantly higher or lower than the others.`,
      ctx.gradeNum >= 6
        ? 'Mean Absolute Deviation (MAD) — The average distance of each data point from the mean; measures spread.'
        : 'Bar graph — A graph using rectangular bars to compare amounts across categories.',
    ],
    'measurement': [
      `Unit of measurement — A standard quantity used to measure. Examples: centimeter, gram, second.`,
      `Metric system — A base-10 measurement system using meters, grams, and liters.`,
      `Customary system — The U.S. measurement system using feet, pounds, and gallons.`,
      `Conversion — Changing a measurement from one unit to another. Example: 1 yard = 3 feet = 36 inches.`,
      `Perimeter — The distance around a shape, measured in linear units.`,
      `Area — The surface inside a shape, measured in square units.`,
      ctx.gradeNum >= 6
        ? 'Precision — How exact a measurement is; determined by the smallest unit of the measuring tool.'
        : 'Estimate — An approximate measurement made using reference objects or rounding.',
    ],
    'addition-subtraction': ctx.isYoung ? [
      `Add — To join two or more groups together to find a total.`,
      `Sum — The answer to an addition problem.`,
      `Subtract — To take away or find the difference between two numbers.`,
      `Difference — The answer to a subtraction problem.`,
      `Plus (+) — The symbol for addition.`,
      `Minus (−) — The symbol for subtraction.`,
      `Number line — A tool for counting forward (adding) or backward (subtracting).`,
    ] : [
      `Sum — The result of adding two or more numbers.`,
      `Difference — The result of subtracting one number from another.`,
      `Regrouping — Exchanging a unit in one place value for 10 units in the next smaller place (borrowing/carrying).`,
      `Inverse operations — Addition and subtraction undo each other. Example: 345 + 128 = 473, so 473 − 128 = 345.`,
      `Estimate — Finding an approximate sum or difference by rounding.`,
      `Commutative property — Changing the order of addends doesn't change the sum. Example: 47 + 23 = 23 + 47.`,
    ],
  };

  const terms = vocabSets[type] ?? [
    `${topic} — A key concept in today's lesson; students will define it using examples from class.`,
    `Algorithm — A step-by-step process for solving a mathematical problem.`,
    `Estimate — An approximate answer based on rounding or known benchmarks.`,
    `Verify — Checking that an answer is reasonable and correct.`,
    `Expression — A mathematical phrase with numbers and/or variables but no equals sign.`,
    `Pattern — A repeating or predictable sequence of numbers or shapes.`,
  ];

  return terms.join('\n');
}

function generateMathWarmUp(ctx: Ctx): string {
  const { topic, obj, t, instrNote, gradeNum } = ctx;
  const type = mathTopic(ctx.tl);

  const warmUps: Partial<Record<MathTopic, string>> = {
    'fractions-compare': `Display two large rectangles of the same size on the board — one shaded to show ³⁄₈ and one shaded to show ⁵⁄₈. Ask: "Which rectangle has more shaded? How did you figure that out?" Give students 60 seconds to discuss with a partner. Then display a new pair: ²⁄₃ and ³⁄₄. Ask: "Is this pair as easy to compare? What makes it harder?" Take 2–3 responses. Record both fraction pairs on the board and say: "Today we're going to learn four strategies for comparing any two fractions — even when the denominators are different."${instrNote}`,
    'fractions-add-sub': `Write on the board: "A recipe uses ¹⁄₄ cup of oil and ²⁄₄ cup of water. How much liquid is that in total?" Give students 2 minutes to try it with a partner. Debrief: "What did you do to find the answer?" Then show: "A recipe needs ¹⁄₃ cup of sugar and ¹⁄₂ cup of brown sugar. How much sugar is that total?" Ask: "Is this problem easier or harder — and why?" Surface the idea that unlike denominators require extra steps. Say: "Today's lesson will show you exactly how to handle those."${instrNote}`,
    'fractions-multiply-divide': `Ask students: "If I have ¹⁄₂ of a pizza and I give my friend ¹⁄₃ of what I have, what fraction of the whole pizza did my friend get?" Let students estimate with thumbs up/sideways/down for ¹⁄₆, ¹⁄₅, ²⁄₅. Reveal the answer (¹⁄₆) and say: "You just thought about multiplying fractions. Today we'll learn why ¹⁄₂ × ¹⁄₃ = ¹⁄₆ and practice this pattern with many examples."${instrNote}`,
    'fractions-equivalent': `Show students three fraction bars: one shaded ¹⁄₂, one shaded ²⁄₄, one shaded ⁴⁄₈. Ask: "Are these the same or different? How do you know?" Let a few students share. Say: "These fractions look different but they're all the same amount — we call them equivalent fractions. Today you'll learn how to find and prove that fractions are equivalent without drawing pictures every time."${instrNote}`,
    'place-value': `Write the number 4,726 on the board. Ask four different students: "What is the value of the 4? The 7? The 2? The 6?" Then change the number to 47,260 and ask: "What happened to the value of each digit?" Guide students to notice that each digit shifted one place to the left, multiplying its value by 10. State: "Today we're exploring how the position of a digit in a number determines its value."${instrNote}`,
    'decimals': `Show students a dollar bill and some coins. Ask: "If I have $1.47, how do I know which part is dollars and which is cents?" Connect to place value: "The 4 is in the tenths place — that's 4 dimes — and the 7 is in the hundredths place — that's 7 pennies." Write 1.47 on the board and ask students to name the value of each digit. Transition: "Today we'll work with decimal numbers — and you'll use the same place-value thinking you use with money."${instrNote}`,
    'multiplication': gradeNum <= 4 ? `Show a 4 × 6 array on the board (rows and columns of dots). Ask: "How many dots without counting one by one?" Take strategies: skip counting, adding equal groups, knowing a multiplication fact. Establish: "The number of rows times the number of columns gives the total — that's multiplication." Reveal the answer (24) and write 4 × 6 = 24 on the board. Say: "Today we'll use this idea to solve multiplication problems about ${topic}."${instrNote}` :
      `Display on the board: 47 × 23 = ? Give students 90 seconds to estimate. Share estimates and strategies (rounding to 50 × 20 = 1,000). Say: "A good estimate helps us check whether our exact answer is reasonable. Today we'll calculate the exact product and connect it to the area model and distributive property."${instrNote}`,
    'multiplication-partial-products': gradeNum <= 4
      ? `Write on the board: 34 × 6 = ? Give students 60 seconds to estimate. Ask: "How did you get your estimate?" Share 2–3 strategies. Then ask: "What if I split 34 into 30 and 4? What would 30 × 6 equal? What would 4 × 6 equal? What if we added those two results?" Give students a moment to try. Say: "You just used partial products — breaking a multiplication problem into smaller, manageable parts and then combining them. Today you will use this strategy with any 2-digit number."${instrNote}`
      : `Write on the board: 47 × 36 = ? Ask students to estimate (e.g. 50 × 36 ≈ 1,800 or 47 × 40 ≈ 1,880). Then ask: "If we split 36 into 30 and 6, what two multiplication problems would we need to solve?" (47 × 30 and 47 × 6.) Say: "The partial products strategy breaks both factors into their place-value parts. Today you will use the area model to organize and find all four partial products."${instrNote}`,
    'division': `Write "96 students, 8 equal groups — how many per group?" Ask students to solve mentally and share strategies (repeated subtraction, known facts, drawing a model). Establish that the answer (12) is the quotient. Then write "135 ÷ 7" and ask: "Is this as easy to solve mentally? Today we'll use the long division algorithm to break down larger problems step by step."${instrNote}`,
    'geometry-area-perimeter': `Show a small square tile. Ask: "If I arranged 12 of these tiles in a rectangle, what shapes could I make?" Let students sketch possibilities (1×12, 2×6, 3×4). Ask: "Which rectangle has the smallest perimeter? Which has the largest? Why?" Connect: "Today we'll define area and perimeter precisely and learn formulas so we don't have to count every tile."${instrNote}`,
    'geometry-angles': `Hold up a sheet of paper folded in half twice to create a corner. Ask: "What do you notice about this corner? What is its measure?" Establish 90° as a right angle. Then show an acute corner and an obtuse corner made from two pencils. Ask: "How do these angles compare to 90°?" Introduce today's goal: measuring and classifying angles and exploring how angle relationships work.${instrNote}`,
    'algebra-equations': `Write on the board: ☐ + 15 = 32. Ask: "What number goes in the box to make this true? How did you find it?" Students share (subtraction, guess-and-check). Then write x + 15 = 32. Say: "In algebra, we use a letter called a variable instead of a box. Today you'll learn a formal strategy — using inverse operations — to solve equations like this one every time."${instrNote}`,
    'integers': `Draw a number line on the board from −10 to 10. Ask: "Where is −4? Which is larger: −4 or −7?" Have students explain using a real-world context (temperature, floors in a building, yardage in football). Then ask: "What's the distance from −4 to 0? From 4 to 0?" Introduce absolute value. Transition: "Today we'll add and subtract integers — and this number line will be our most powerful tool."${instrNote}`,
    'ratios-proportions': `Show two packages: "8 markers for $4" and "12 markers for $6." Ask: "Which is the better deal — or are they the same?" Give students 2 minutes to decide and explain. Surface the idea of unit rate ($0.50 per marker each). Say: "You just used proportional reasoning. Today we'll formalize this with ratios and proportions so we can solve any comparison problem systematically."${instrNote}`,
    'percentages': `Show a store sign: "25% OFF!" Ask: "If a shirt costs $40, how much money do you save?" Give students 90 seconds to work it out. Share strategies. Then show "15% tip on a $28 restaurant bill." Ask: "Same idea — how would you approach it?" Say: "Percents appear in daily life all the time. Today we'll build strategies for calculating percent of a number, discount, and percent of change."${instrNote}`,
    'data-statistics': `Display a small data set: 7 students' test scores: 82, 91, 76, 95, 88, 91, 40. Ask: "Which single number best represents this class's performance?" Let students argue for different choices. Guide toward mean, median, and mode — and ask: "What happens to the mean if we remove the score of 40? Why?" Establish that today's lesson is about finding and interpreting measures of center.${instrNote}`,
  };

  return warmUps[type] ??
    `Pose a short real-world problem connected to ${topic} on the board. Give students 2 minutes to attempt it individually, then 90 seconds to discuss with a partner. Cold-call 2–3 students for strategies. Record key ideas and bridge to today's goal: students will ${obj}. Ask: "What do you already know that might help you with ${topic}?"${instrNote}`;
}

function generateMathDirectInstruction(ctx: Ctx): string {
  const { topic, gradeNum, instrNote } = ctx;
  const type = mathTopic(ctx.tl);

  const steps: Partial<Record<MathTopic, string[]>> = {
    'fractions-compare': [
      `Introduce four comparison strategies using anchor chart headings: "Same Denominator," "Same Numerator," "Benchmark Fraction (½)," and "Common Denominator." Explain that choosing the right strategy depends on what the fractions have in common.`,
      `Strategy 1 — Same Denominator: "When the denominators match, compare the numerators — the bigger numerator means the bigger fraction." Worked example: ⁵⁄₈ vs ³⁄₈. Draw fraction bars to confirm. Ask: "Why does this work? What do the bars show?" (Each slice is the same size, so more slices = more.) Write the symbol: ⁵⁄₈ > ³⁄₈.`,
      `Strategy 2 — Same Numerator: "When the numerators match, the fraction with the smaller denominator is larger — larger denominator means smaller pieces." Worked example: ³⁄₅ vs ³⁄₇. Ask: "If you split a pizza into 5 pieces vs 7 pieces, which slice is bigger?" Write: ³⁄₅ > ³⁄₇. Draw slices to confirm.`,
      `Strategy 3 — Benchmark Fraction (½): "Decide if each fraction is less than, equal to, or greater than ½. Then compare." Worked example: compare ⁵⁄₉ and ³⁄₈. Is ⁵⁄₉ > ½? Half of 9 is 4.5, and 5 > 4.5, so yes. Is ³⁄₈ > ½? Half of 8 is 4, and 3 < 4, so no. Therefore ⁵⁄₉ > ³⁄₈.`,
      `Strategy 4 — Common Denominator (for trickier pairs): "Find a number both denominators divide into evenly, rewrite both fractions, then compare numerators." Worked example: ²⁄₃ vs ³⁄₄. LCM of 3 and 4 is 12. ²⁄₃ = ⁸⁄₁₂; ³⁄₄ = ⁹⁄₁₂. Since 8 < 9, we write ²⁄₃ < ³⁄₄. Post the step-by-step on the anchor chart for student reference.${instrNote}`,
    ],
    'fractions-add-sub': [
      `Begin with like denominators to build the pattern. "To add fractions with the same denominator, add the numerators and keep the denominator." Worked example: ²⁄₇ + ³⁄₇ = ⁵⁄₇. Draw fraction bars to confirm. Ask: "Why do we keep the denominator the same?" (Same-size pieces, only the count changes.)`,
      `Transition to unlike denominators. "We cannot add fractions until they use the same-size pieces — we need a common denominator." Worked example: ¹⁄₃ + ¹⁄₄. Find LCD: LCM of 3 and 4 = 12. Rewrite: ¹⁄₃ = ⁴⁄₁₂; ¹⁄₄ = ³⁄₁₂. Add: ⁴⁄₁₂ + ³⁄₁₂ = ⁷⁄₁₂. Draw fraction strips to verify.`,
      `Subtraction with unlike denominators: Same process. Worked example: ⁵⁄₆ − ¹⁄₄. LCD = 12. Rewrite: ¹⁰⁄₁₂ − ³⁄₁₂ = ⁷⁄₁₂. Remind students to check if the answer can be simplified.`,
      gradeNum >= 5
        ? `Mixed numbers: Show 2¹⁄₃ + 1¹⁄₂. Strategy: convert to improper fractions (⁷⁄₃ + ³⁄₂), find LCD = 6, rewrite (¹⁴⁄₆ + ⁹⁄₆ = ²³⁄₆), convert back to mixed number 3⁵⁄₆. Alternatively, add wholes and fractions separately. Show both methods.${instrNote}`
        : `Simplifying: After adding, always check the result. Show ⁶⁄₈: "Can we simplify? GCF of 6 and 8 is 2, so ⁶⁄₈ = ³⁄₄." Post the full process as a reference anchor chart.${instrNote}`,
    ],
    'fractions-equivalent': [
      `Show three pre-drawn fraction bars on the board — all the same total width: ¹⁄₂ (1 of 2 parts shaded), ²⁄₄ (2 of 4 shaded), ³⁄₆ (3 of 6 shaded). Ask: "What is the same? What is different?" Establish: the shaded area is identical in all three, so ¹⁄₂ = ²⁄₄ = ³⁄₆. Write on the board: "Equivalent fractions name the same part of the same whole." Say: "Today you will create equivalent fractions by multiplying BOTH the numerator and the denominator by the same nonzero whole number."`,
      `Demonstrate the scaling rule. Write on the board: ¹⁄₂ = (1×2)/(2×2) = ²⁄₄. Draw: take the ¹⁄₂ rectangle and draw one vertical line through each half — now 4 equal parts, 2 shaded. Ask: "How many total parts now?" (4.) "How many are shaded?" (2.) "What changed?" (More pieces — numerator and denominator both doubled.) "What stayed the same?" (The amount shaded.) Emphasize: "Multiplying both parts by the same number is like cutting every existing piece into equal smaller pieces — the shaded area never changes."`,
      `Worked Example 2 — multiplier of 3: ¹⁄₃ = (1×3)/(3×3) = ³⁄₉. Draw: rectangle in thirds, 1 shaded, then cut each third into 3 → 9 parts, 3 shaded. Ask: "What multiplier did we use?" (3.) "If we use ×4 instead: ¹⁄₃ = ⁴⁄₁₂?" (Yes — show model.) Key: any nonzero whole-number multiplier produces a valid equivalent fraction.`,
      `Worked Example 3 — numerator greater than 1: ²⁄₃ = (2×4)/(3×4) = ⁸⁄₁₂. Ask: "If each third is cut into 4 equal parts, how many total parts?" (12.) "The 2 shaded thirds each become 4 smaller parts — how many are shaded now?" (8.) Confirm: ²⁄₃ = ⁸⁄₁₂. Post anchor chart: Step 1 — Choose any nonzero whole-number multiplier. Step 2 — Multiply BOTH numerator and denominator by it. Step 3 — The fraction value is unchanged; only the number of pieces changes.${instrNote}`,
    ],
    'multiplication': gradeNum <= 4 ? [
      `Connect to repeated addition and skip counting: "4 groups of 6 is the same as 6 + 6 + 6 + 6 = 24, or skip-counting by 6 four times." Show on number line and array.`,
      `Introduce the commutative property with an array: rotate a 4×6 array to show 6×4 — same total dots, same product. "Does the order of factors matter? No — we get 24 either way."`,
      `Model the distributive property for a harder fact: 7 × 8. Think of it as (7 × 5) + (7 × 3) = 35 + 21 = 56. Draw the split on grid paper.`,
      `Worked examples with narrated thinking: "7 × 9: I know 7 × 10 = 70. Subtract one group of 7: 70 − 7 = 63." Try two more: 8 × 6 and 9 × 8. For each, ask students to supply the next step before revealing it.${instrNote}`,
    ] : [
      `Review and extend: connect ${topic} to the distributive property using the area model. Example: 47 × 23. Draw a rectangle split into 40×20, 40×3, 7×20, 7×3. Calculate each part (800, 120, 140, 21) and add: 1,081.`,
      `Introduce the standard algorithm side by side with the area model: "The algorithm is a shortcut that records the same partial products compactly." Walk through 47 × 23 column-by-column with regrouping narration.`,
      `Worked example with 3-digit number: 312 × 14. Demonstrate both area model and algorithm. Check with estimation: 300 × 14 = 4,200 ✓.`,
      `Discuss when to estimate vs. when to compute exactly. Show a word problem where only an estimate is needed.${instrNote}`,
    ],
    'multiplication-partial-products': gradeNum <= 4 ? [
      `Introduce partial products by decomposing the 2-digit factor into tens and ones. "Partial products means breaking a number into its parts, multiplying each part separately, and then adding the results." Worked Example 1: 34 × 6.\n  Step 1 — Decompose: 34 = 30 + 4. Draw a split rectangle with two sections labeled 30 × 6 and 4 × 6.\n  Step 2 — Partial products: 30 × 6 = 180 (tens); 4 × 6 = 24 (ones).\n  Step 3 — Add: 180 + 24 = 204. So 34 × 6 = 204.\n  Estimate check: 30 × 6 = 180, so 204 is close and reasonable.`,
      `Worked Example 2 — fully guided: 46 × 7.\n  Decompose: 46 = 40 + 6.\n  40 × 7 = 280 (tens partial product); 6 × 7 = 42 (ones partial product).\n  Sum: 280 + 42 = 322.\n  Ask: "Why do we multiply 40 × 7 and not just 4 × 7?" (The tens digit represents 40, not 4 — its value is determined by its place.)`,
      `Worked Example 3 — students supply each step: 58 × 4.\n  "How should we decompose 58?" (50 + 8). "What is the tens partial product?" (50 × 4 = 200). "What is the ones partial product?" (8 × 4 = 32). "What is the final product?" (200 + 32 = 232).\n  Record the three-step anchor chart: (1) Decompose by place value. (2) Multiply each part. (3) Add the partial products.`,
      `Estimation and reasonableness: "Before solving 73 × 8, estimate: 70 × 8 = 560. Now solve: 70 × 8 = 560; 3 × 8 = 24; 560 + 24 = 584. Is 584 close to 560? Yes — the answer is reasonable." Post the three-step Partial Products algorithm on the anchor chart for student reference.${instrNote}`,
    ] : [
      `Introduce partial products for 2-digit × 2-digit using the area model. Example: 34 × 26.\n  Draw a rectangle split into four sections: 30×20, 30×6, 4×20, 4×6.\n  Partial products: 600, 180, 80, 24.\n  Sum: 600 + 180 + 80 + 24 = 884.\n  "Each section of the rectangle is one partial product — all four are needed for the complete product."`,
      `Connect the area model to the written partial products algorithm. For 34 × 26, record as:\n     34\n  ×  26\n  ——\n   204  ← 34 × 6: (30×6=180) + (4×6=24) = 204\n + 680  ← 34 × 20: (30×20=600) + (4×20=80) = 680\n  ——\n   884\n"Each row records one partial product: first the ones digit of the multiplier, then the tens digit."`,
      `Worked Example 2 — student participation: 57 × 43.\n  Area model: 50×40=2,000; 50×3=150; 7×40=280; 7×3=21.\n  Sum: 2,000 + 150 + 280 + 21 = 2,451.\n  Estimate check: 60 × 40 = 2,400. Close ✓`,
      `Worked Example 3 — students complete independently: 62 × 35.\n  Set up the four-section rectangle, label each section, calculate each partial product, add.\n  (Answer: 60×30=1,800; 60×5=300; 2×30=60; 2×5=10; total = 2,170.)\n  "How does the estimate 60 × 35 = 2,100 compare?" (Very close — consistent with the exact answer.)${instrNote}`,
    ],
    'division': [
      `Review the relationship between multiplication and division: "Division undoes multiplication. If 8 × 9 = 72, then 72 ÷ 9 = 8." Practice 5 fact-family turnarounds together.`,
      `Introduce the long division algorithm with a 2-digit dividend: 84 ÷ 6. Teach the four-step loop — Divide, Multiply, Subtract, Bring down — using the mnemonic "Does McDonald's Sell Burgers?" Work through each step aloud, narrating every decision.`,
      `Extend to 3-digit dividend: 324 ÷ 7. Estimate first (320 ÷ 7 ≈ 45). Work through algorithm step by step; result is 46 remainder 2. Verify: 46 × 7 + 2 = 324 ✓.`,
      `Discuss remainders and what they mean in context: "If 25 students go on 4 equal teams, you get 6 remainder 1 — that means one team gets an extra person." Pose: what would you do with a remainder if you were sharing 23 cookies among 4 friends?${instrNote}`,
    ],
    'geometry-area-perimeter': [
      `Define perimeter: "The total distance around the outside of a shape." Find perimeter of a rectangle with length 8 cm and width 5 cm: P = 8 + 5 + 8 + 5 = 26 cm. Introduce shortcut: P = 2l + 2w = 2(8) + 2(5) = 26 cm. Try an irregular polygon by adding all side lengths.`,
      `Define area: "The number of square units inside a shape." Show grid paper: count squares inside a 4 × 6 rectangle = 24. Connect to multiplication: length × width = area. A = lw = 6 × 4 = 24 cm².`,
      `Worked example: A garden is 12 m long and 7 m wide. Find perimeter (P = 2(12) + 2(7) = 38 m) and area (A = 12 × 7 = 84 m²). Discuss which measurement tells you how much fencing you need vs. how much sod.`,
      gradeNum >= 5
        ? `Composite figures: Show an L-shaped room. Guide students to split it into two rectangles, find each area, and add them together. Extension: find the missing side lengths using known measurements.${instrNote}`
        : `Real-world problem: "A square tile is 4 inches on each side. How many tiles fit along a 36-inch wall?" (Perimeter thinking.) "How many tiles cover a 36 × 24-inch floor?" (Area thinking.) Work through both together.${instrNote}`,
    ],
    'algebra-equations': [
      `Define an equation: "A mathematical statement that two expressions are equal — like a balanced scale." Draw a balance scale: left side has 2x + 3, right side has 11. "To find x, we must keep the scale balanced — whatever we do to one side, we do to the other."`,
      `Solve a one-step equation: x + 15 = 32. Step 1: identify the operation (addition). Step 2: use the inverse operation (subtract 15 from both sides). x + 15 − 15 = 32 − 15 → x = 17. Step 3: verify by substituting: 17 + 15 = 32 ✓.`,
      `Solve a two-step equation: 3x − 5 = 16. Step 1: add 5 to both sides → 3x = 21. Step 2: divide both sides by 3 → x = 7. Step 3: verify: 3(7) − 5 = 21 − 5 = 16 ✓. Post the two-step flow on the anchor chart: "Undo addition/subtraction first, then multiplication/division."`,
      `Word problem to equation: "A number doubled, plus 4, equals 18. Write and solve the equation." Model thinking aloud: "Let n = the unknown number. 2n + 4 = 18 → 2n = 14 → n = 7." Check in context.${instrNote}`,
    ],
    'ratios-proportions': [
      `Define ratio: "A comparison of two quantities by division." Three ways to write a ratio — 3:5, ³⁄₅, "3 to 5." Emphasize order matters. Example: "In a class of 28 students, 12 are wearing glasses. What is the ratio of glasses-wearers to non-glasses-wearers?" (12:16, simplified to 3:4.)`,
      `Equivalent ratios and proportion: "A proportion is two equal ratios." Set up: ²⁄₃ = ?/₁₅. Explain cross-multiplication: 2 × 15 = 3 × n → n = 10. Verify: ²⁄₃ = ¹⁰⁄₁₅ ✓.`,
      `Unit rate: "Divide to find how much per 1 unit." Example: 180 miles in 3 hours → 60 miles per hour. Pose: "$7.50 for 5 bananas — what's the unit price?" ($1.50 each.)`,
      `Proportion word problem: "If 4 tickets cost $22, how much do 10 tickets cost?" Set up the proportion: ⁴⁄₂₂ = ¹⁰⁄ₙ. Cross-multiply: 4n = 220 → n = 55. The tickets cost $55.${instrNote}`,
    ],
    'integers': [
      `Introduce the number line from −10 to 10. Place integers and discuss: "Negative integers are less than zero — they're real quantities in contexts like temperature, elevation below sea level, and debt."`,
      `Adding integers — same sign: "Add the absolute values and keep the sign." Worked: (−4) + (−5) = −9. Model on number line: start at 0, move left 4, then left 5 more.`,
      `Adding integers — opposite signs: "Subtract the smaller absolute value from the larger and keep the sign of the number with the greater absolute value." Worked: (−7) + 3 = −4 (|−7| > |3|, difference = 4, keep the negative). Number line confirmation.`,
      `Subtracting integers: "To subtract, add the opposite (keep-change-change: KCC)." Worked: 5 − (−3) = 5 + 3 = 8. Worked: −4 − 6 = −4 + (−6) = −10. Show real-world: "Temperature was −4°F; it dropped 6 more degrees → −10°F."${instrNote}`,
    ],
    'percentages': [
      `Establish three equivalent forms: percent, decimal, fraction. Show conversion steps: 45% → move decimal two places left → 0.45 → ⁴⁵⁄₁₀₀ = ⁹⁄₂₀. Practice 10%, 25%, 50%, 75%, and 100% mentally.`,
      `Finding a percent of a number: Method 1 — convert percent to decimal, then multiply. Example: 30% of 80 → 0.30 × 80 = 24. Method 2 — proportion: ³⁰⁄₁₀₀ = ²⁴⁄₈₀ (cross-check). Show both on the board.`,
      `Discount and sale price: "A $60 jacket is 25% off. Amount of discount = 0.25 × 60 = $15. Sale price = 60 − 15 = $45." Walk through a second example: 15% off a $48 video game.`,
      gradeNum >= 7
        ? `Percent of change: "A $40 item goes up to $50. Change = $10. Percent increase = (10 ÷ 40) × 100 = 25%." Worked decrease: price drops from $80 to $64 → (16 ÷ 80) × 100 = 20% decrease.${instrNote}`
        : `Sales tax: "A book costs $14.99, and the tax rate is 6%. Tax = 0.06 × 14.99 ≈ $0.90. Total = $14.99 + $0.90 = $15.89." Practice with two more realistic prices.${instrNote}`,
    ],
  };

  const chosen = steps[type];
  if (chosen) return chosen.map((s, i) => `${i + 1}. ${s}`).join('\n');

  // Generic fallback — must use Worked Example N: header format at line start so
  // extractDirectSection's DI_HEADER_RE can locate them. Do NOT use inline mentions
  // like "Work through a Worked Example 1..." — the regex requires them as line headers.
  return [
    `Worked Example 1:\nIntroduce ${topic} with a concrete, visual example connected to what students already know. Use a physical model, real-world context, or diagram. Think aloud: "First I notice ____. Here is what I do: ____. The reason I do this is ____." Students record the key steps.`,
    `Worked Example 2:\nModel a second example with a slight variation, pausing at each step to invite student input: "What should I do next — and why?" Students record alongside the teacher. Post a reference anchor chart with key steps or strategies students will use during practice.${instrNote}`,
  ].join('\n\n');
}

function generateMathGuidedPractice(ctx: Ctx): string {
  const { topic, gradeNum, t, instrNote } = ctx;
  const type = mathTopic(ctx.tl);

  const gpMap: Partial<Record<MathTopic, string>> = {
    'fractions-compare': `Partner Comparison Challenge (≈${t(0.20)} min)

Each pair receives a set of 8 fraction comparison cards. For each card, partners must:
1. Identify which strategy to use (same denominator, same numerator, benchmark ½, or common denominator).
2. Solve together, writing the correct <, >, or = symbol.
3. Partner A explains their reasoning; Partner B either agrees and adds to it, or respectfully disagrees and explains why.

Sample problems:
• ⁴⁄₉ ☐ ⁷⁄₉  (same denominator → 4 < 7, so ⁴⁄₉ < ⁷⁄₉)
• ²⁄₇ ☐ ²⁄₅  (same numerator → 7 > 5, so ²⁄₇ < ²⁄₅)
• ³⁄₇ ☐ ⁵⁄₈  (benchmark ½: ³⁄₇ < ½; ⁵⁄₈ > ½ → so ³⁄₇ < ⁵⁄₈)
• ²⁄₃ ☐ ³⁄₅  (common denominator 15: ¹⁰⁄₁₅ vs ⁹⁄₁₅ → ²⁄₃ > ³⁄₅)

Whole-class debrief: project one pair's work on the tricky problem (²⁄₃ vs ³⁄₅) and ask: "Did anyone use a different strategy? Which was more efficient?"${instrNote}`,
    'fractions-add-sub': `We-Do Problem Set (≈${t(0.20)} min)

Work through problems 1–4 together as a class, calling on students to supply each step:

1. ²⁄₅ + ¹⁄₅ = ³⁄₅  (like denominators)
2. ¹⁄₄ + ²⁄₃  (LCD = 12 → ³⁄₁₂ + ⁸⁄₁₂ = ¹¹⁄₁₂)
3. ⁵⁄₆ − ¹⁄₄  (LCD = 12 → ¹⁰⁄₁₂ − ³⁄₁₂ = ⁷⁄₁₂)
4. ³⁄₄ + ⁵⁄₈  (LCD = 8 → ⁶⁄₈ + ⁵⁄₈ = ¹¹⁄₈ = 1³⁄₈)

After each problem, ask the class: "Does our answer make sense? Is it close to our estimate?"
Partner extension: "A recipe calls for ²⁄₃ cup flour and ¹⁄₂ cup oats. How much dry ingredient altogether?" (Answer: ⁷⁄₆ = 1¹⁄₆ cups)${instrNote}`,
    'fractions-multiply-divide': `We-Do then Partner-Do (≈${t(0.20)} min)

We-Do (whole class):
1. ¹⁄₂ × ²⁄₃ = ²⁄₆ = ¹⁄₃  (multiply numerators, multiply denominators; simplify)
2. ³⁄₄ × ²⁄₅ = ⁶⁄₂₀ = ³⁄₁₀
3. ¾ ÷ ¹⁄₂ → ³⁄₄ × ²⁄₁ = ⁶⁄₄ = ³⁄₂ = 1½  (use the reciprocal of the divisor)

Partner-Do (students work simultaneously, teacher circulates):
4. ²⁄₃ × ³⁄₈  (Answer: ⁶⁄₂₄ = ¹⁄₄)
5. ⁵⁄₆ ÷ ¹⁄₃  (Answer: ⁵⁄₆ × ³⁄₁ = ¹⁵⁄₆ = ⁵⁄₂ = 2½)

Debrief: "When you multiply two fractions each less than 1, is the product larger or smaller than either factor? Why?"${instrNote}`,
    'fractions-equivalent': `Guided Practice — Equivalent Fractions (≈${t(0.20)} min)
For each problem: name the multiplier, then write the complete equation.

1. ¹⁄₂ = __/6. Denominator: 2 × ___ = 6. Write the equation: ¹⁄₂ = ___/___.  (Answer: ×3. ¹⁄₂ = ³⁄₆)
2. ¹⁄₃ = ²⁄__. Numerator doubled: 1 × 2 = 2. Find the denominator.  (Answer: ×2. ¹⁄₃ = ²⁄₆)
3. ²⁄₅ = __/10. Write the equation, then draw a rectangle model to verify.  (Answer: ×2. ²⁄₅ = ⁴⁄₁₀)
4. ³⁄₄ = __/8  (Answer: ×2. ³⁄₄ = ⁶⁄₈)
5. ²⁄₃ = ⁶/__  (Answer: ×3. ²⁄₃ = ⁶⁄₉)

Debrief: How does multiplying both parts by the same number change the fraction? How does it stay the same?${instrNote}`,
    'geometry-area-perimeter': gradeNum >= 5 ?
      `Partner Activity — Composite Figures (≈${t(0.20)} min)

Each pair receives an L-shaped floor plan on grid paper.

Step 1: Label all known side lengths (some may be missing — calculate them from given information).
Step 2: Divide the shape into rectangles in at least two different ways.
Step 3: Calculate the area of each rectangle, then add for the total area.
Step 4: Calculate the perimeter by adding all outer edges.
Step 5: Compare with another pair — did you get the same answer using different decompositions?

Teacher circulates and asks: "How do you know where to split the shape? What stays the same regardless of how you split it?"${instrNote}` :
      `Guided Problem Set — "We Do" Together (≈${t(0.20)} min)

Problem 1: A classroom is 9 m long and 6 m wide. (a) What is the perimeter? (b) What is the area?
  Answers: P = 2(9) + 2(6) = 30 m; A = 54 m²

Problem 2: A square garden has a perimeter of 36 ft. What is the side length? What is the area?
  Answers: Side = 36 ÷ 4 = 9 ft; A = 81 ft²

Problem 3: Which has the larger area — a 5×8 rectangle or a 6×7 rectangle?
  Answers: A₁ = 40; A₂ = 42. The 6×7 is larger by 2 square units.

Students record answers on mini-whiteboards; hold up on teacher signal for quick whole-class check.${instrNote}`,
    'algebra-equations': `Solving Together — Escalating Difficulty (≈${t(0.20)} min)

Round 1 — One-step (students solve on mini-whiteboards):
• n + 9 = 21  (n = 12)   • y − 14 = 5  (y = 19)   • 8m = 56  (m = 7)   • k ÷ 6 = 9  (k = 54)

Round 2 — Two-step (partners solve, then compare with another pair):
• 2x + 5 = 17  → 2x = 12 → x = 6   ✓ Check: 2(6) + 5 = 17
• 3n − 7 = 14  → 3n = 21 → n = 7   ✓ Check: 3(7) − 7 = 14
• ½p + 4 = 10  → ½p = 6 → p = 12  ✓ Check: ½(12) + 4 = 10

Whole-class error analysis: display a deliberately incorrect solution (2x + 6 = 14 → x = 10). Ask: "What went wrong? How do you know?"${instrNote}`,
    'integers': `Number Line Partner Relay (≈${t(0.20)} min)

Each pair has a laminated number line from −20 to 20. Partner A models the first step by physically moving a token; Partner B records the equation and answer. Alternate roles for each problem.

Problems:
1. Start at −3, move forward 7 → (−3) + 7 = 4
2. Start at 5, move back 9 → 5 + (−9) = −4
3. Start at −6, move back 4 → (−6) + (−4) = −10
4. 8 − (−2) = 8 + 2 = 10  (KCC: subtract, change sign, change sign)
5. −5 − 3 = −5 + (−3) = −8

Class debrief: "When is the sum of two integers positive? Negative? Zero? Write a rule in your own words."${instrNote}`,
    'multiplication-partial-products': gradeNum <= 4 ? `Partial Products — We Do Together (≈${t(0.20)} min)

For each problem: (a) write the decomposition, (b) find both partial products, (c) add them, (d) verify with an estimate.

Problem 1 (whole class model):
  23 × 4: Decompose 23 = 20 + 3. Partial products: 20 × 4 = 80; 3 × 4 = 12. Sum: 80 + 12 = 92.
  Estimate: 20 × 4 = 80 ✓

Problem 2 (whole class — students supply each step):
  56 × 3
  (Answer: 50 × 3 = 150; 6 × 3 = 18; 150 + 18 = 168. Estimate: 60 × 3 = 180 ✓)

Problem 3 (partners — both solve, then compare):
  74 × 5
  (Answer: 70 × 5 = 350; 4 × 5 = 20; 350 + 20 = 370)

Problem 4 (partners):
  38 × 7
  (Answer: 30 × 7 = 210; 8 × 7 = 56; 210 + 56 = 266)

Word problem (partners):
  A school van makes 35 trips, carrying 6 students each trip. How many students total?
  (Answer: 35 × 6 → 30 × 6 = 180; 5 × 6 = 30; 180 + 30 = 210 students)${instrNote}` : `Partial Products — We Do Together (≈${t(0.20)} min)

For each problem, draw the four-section area model, label each partial product, then add them.

Problem 1 (whole class model):
  23 × 14: 20×10=200; 20×4=80; 3×10=30; 3×4=12. Sum: 322.
  Estimate: 20 × 14 = 280 — close ✓

Problem 2 (whole class — students fill each section):
  35 × 26
  (Answer: 30×20=600; 30×6=180; 5×20=100; 5×6=30. Sum: 910)

Problem 3 (partners):
  47 × 32
  (Answer: 40×30=1,200; 40×2=80; 7×30=210; 7×2=14. Sum: 1,504)

Problem 4 (partners):
  68 × 25
  (Answer: 60×20=1,200; 60×5=300; 8×20=160; 8×5=40. Sum: 1,700)${instrNote}`,
    'multiplication': gradeNum <= 4 ? `Multiplication — We Do Together (≈${t(0.20)} min)

Estimate first, then solve. For each problem, show the estimation and all work.

Problem 1 (whole class model):
  4 × 36: Estimate: 4 × 40 = 160. Solve: 4 × 30 = 120; 4 × 6 = 24; 120 + 24 = 144.

Problem 2 (whole class — students supply each step):
  6 × 47
  (Answer: 6 × 40 = 240; 6 × 7 = 42; 240 + 42 = 282. Estimate: 6 × 50 = 300 ✓)

Problem 3 (partners — both solve, then compare):
  8 × 53
  (Answer: 8 × 50 = 400; 8 × 3 = 24; 400 + 24 = 424)

Problem 4 (partners):
  7 × 65
  (Answer: 7 × 60 = 420; 7 × 5 = 35; 420 + 35 = 455)

Word problem (partners):
  A baker makes 78 cookies each day for 5 days. How many cookies total?
  (Answer: 5 × 78 → 5 × 70 = 350; 5 × 8 = 40; 350 + 40 = 390 cookies)${instrNote}` : `Multi-Digit Multiplication — We Do Together (≈${t(0.20)} min)

Estimate before you compute. Show all steps.

Problem 1 (whole class model):
  23 × 14: Estimate: 20 × 14 = 280. Exact: 23 × 4 = 92; 23 × 10 = 230; 92 + 230 = 322.

Problem 2 (whole class — students supply each step):
  46 × 27
  (Answer: 46 × 7 = 322; 46 × 20 = 920; 322 + 920 = 1,242. Estimate: 50 × 27 = 1,350 ✓)

Problem 3 (partners):
  58 × 35
  (Answer: 58 × 5 = 290; 58 × 30 = 1,740; 290 + 1,740 = 2,030)

Problem 4 (partners):
  74 × 46
  (Answer: 74 × 6 = 444; 74 × 40 = 2,960; 444 + 2,960 = 3,404)${instrNote}`,
  };

  return gpMap[type] ??
    `We-Do Practice — ${topic} (≈${t(0.20)} min)

Students attempt each problem below independently (60 seconds), then compare answers with a partner before whole-class discussion.

Problem 1: Apply ${ctx.skillToTeach.replace(/\.$/, '')} — direct example. Show every step and label the strategy.

Problem 2: Apply the same approach with different numbers or a new context. Show every step. After solving, write one sentence explaining why the strategy worked.

Problem 3: Real-world situation that requires ${topic}. Identify what is known, what is needed, and show the complete solution.

Debrief: "What step required the most thought? Is there another approach that gives the same answer?"${instrNote}`;
}

function generateMathIndependentPractice(ctx: Ctx): string {
  const { topic, obj, t, instrNote, gradeNum } = ctx;
  const type = mathTopic(ctx.tl);

  const ipMap: Partial<Record<MathTopic, string>> = {
    'fractions-compare': `Students complete the following problems independently (≈${t(0.20)} min). Show all work and label the strategy used.

Part A — Compare. Write <, >, or = in each box.
1. ³⁄₄ ☐ ⁵⁄₄          (same denominator: ³⁄₄ < ⁵⁄₄)
2. ⁴⁄₉ ☐ ⁴⁄₇          (same numerator: 9 > 7, so ⁴⁄₉ < ⁴⁄₇)
3. ³⁄₅ ☐ ⁷⁄₁₀         (common denominator 10: ⁶⁄₁₀ < ⁷⁄₁₀, so ³⁄₅ < ⁷⁄₁₀)
4. ⁵⁄₆ ☐ ⁷⁄₁₂         (LCD = 12: ¹⁰⁄₁₂ > ⁷⁄₁₂, so ⁵⁄₆ > ⁷⁄₁₂)
5. ²⁄₅ ☐ ³⁄₇          (benchmark: both < ½. Common denominator 35: ¹⁴⁄₃₅ > ¹⁵⁄₃₅? No: ¹⁴⁄₃₅ < ¹⁵⁄₃₅, so ²⁄₅ < ³⁄₇)
6. ⁷⁄₈ ☐ ⁵⁄₆          (common denominator 24: ²¹⁄₂₄ > ²⁰⁄₂₄, so ⁷⁄₈ > ⁵⁄₆)

Part B — Order from least to greatest.
7. ¹⁄₂, ²⁄₅, ³⁄₄, ⁵⁄₈   (Answer: ²⁄₅, ¹⁄₂, ⁵⁄₈, ³⁄₄)

Part C — Word problem.
8. Maria ran ²⁄₃ of a mile. Jacob ran ³⁄₄ of a mile. Who ran farther? By how much?
   (Answer: Jacob. ³⁄₄ − ²⁄₃ = ⁹⁄₁₂ − ⁸⁄₁₂ = ¹⁄₁₂ mile.)${instrNote}`,
    'fractions-add-sub': `Independent Practice — Adding and Subtracting Fractions (≈${t(0.20)} min)

Show all work. Simplify all answers.

Part A — Like denominators:
1. ³⁄₈ + ²⁄₈ = ⁵⁄₈
2. ⁷⁄₁₀ − ³⁄₁₀ = ⁴⁄₁₀ = ²⁄₅

Part B — Unlike denominators:
3. ¹⁄₃ + ¹⁄₆  (LCD = 6: ²⁄₆ + ¹⁄₆ = ³⁄₆ = ¹⁄₂)
4. ³⁄₄ − ¹⁄₃  (LCD = 12: ⁹⁄₁₂ − ⁴⁄₁₂ = ⁵⁄₁₂)
5. ²⁄₅ + ³⁄₄  (LCD = 20: ⁸⁄₂₀ + ¹⁵⁄₂₀ = ²³⁄₂₀ = 1³⁄₂₀)
6. ⁵⁄₆ − ²⁄₉  (LCD = 18: ¹⁵⁄₁₈ − ⁴⁄₁₈ = ¹¹⁄₁₈)

Part C — Word problem:
7. A ribbon is ⁷⁄₈ yard long. Tanya uses ¹⁄₄ yard for a bow. How much ribbon remains?
   (Answer: ⁷⁄₈ − ¹⁄₄ = ⁷⁄₈ − ²⁄₈ = ⁵⁄₈ yard)${instrNote}`,
    'fractions-equivalent': `Independent Practice — Equivalent Fractions (≈${t(0.20)} min)
Name the multiplier and write the complete equation for each problem.

1. ¹⁄₄ = __/8  (Answer: ×2. ²⁄₈)
2. ¹⁄₂ = __/10  (Answer: ×5. ⁵⁄₁₀)
3. ²⁄₃ = ⁶/__  (Answer: ×3. ⁶⁄₉)
4. ³⁄₅ = __/10  (Answer: ×2. ⁶⁄₁₀)
5. ²⁄₅ = __/15  (Answer: ×3. ⁶⁄₁₅)
6. ⁵⁄₆ = __/12  (Answer: ×2. ¹⁰⁄₁₂)
7. Draw a rectangle model showing ¹⁄₃ = ²⁄₆. Label the thirds, then the sixths, and explain what happened to each piece.  (Answer: ²⁄₆. Each third is divided into 2 equal parts.)
8. Are ²⁄₅ and ⁶⁄₁₅ equivalent? Show the multiplier and complete equation.  (Answer: Yes. ×3. ²⁄₅ = ⁶⁄₁₅)
9. A student wrote ¹⁄₃ = ³⁄₆ because 1+2=3 and 3+3=6. What was the error? Find the correct equivalent fraction for ¹⁄₃ with denominator 6.  (Answer: Added instead of multiplied. Correct: ×2. ²⁄₆)${instrNote}`,
    'algebra-equations': `Independent Practice — Solving Equations (≈${t(0.20)} min)

Show all steps. Check each answer by substituting back into the original equation.

1. x + 11 = 28       (x = 17; check: 17 + 11 = 28 ✓)
2. y − 6 = 15        (y = 21; check: 21 − 6 = 15 ✓)
3. 7n = 63           (n = 9; check: 7 × 9 = 63 ✓)
4. m ÷ 4 = 12        (m = 48; check: 48 ÷ 4 = 12 ✓)
5. 3k + 2 = 20       (3k = 18; k = 6; check: 3(6) + 2 = 20 ✓)
6. 5p − 9 = 31       (5p = 40; p = 8; check: 5(8) − 9 = 31 ✓)
7. ¹⁄₂w + 7 = 15    (¹⁄₂w = 8; w = 16; check: ½(16) + 7 = 15 ✓)

Word problem:
8. "A number tripled, minus 5, equals 22. Find the number." (3n − 5 = 22 → n = 9)${instrNote}`,
    'geometry-area-perimeter': `Independent Practice — Area and Perimeter (≈${t(0.20)} min)

Show all work with labels and units.

1. Find the perimeter and area of a rectangle that is 13 cm long and 8 cm wide.
   (P = 2(13) + 2(8) = 42 cm; A = 13 × 8 = 104 cm²)

2. A square patio has an area of 144 ft². What is the length of each side? What is its perimeter?
   (Side = 12 ft; P = 48 ft)

3. A park is 350 m long and 200 m wide. How many meters of fencing are needed to surround it?
   (P = 2(350) + 2(200) = 1,100 m)

4. A triangle has a base of 10 in and a height of 6 in. Find its area.
   (A = ½ × 10 × 6 = 30 in²)

${gradeNum >= 5 ? `5. An L-shaped room has overall dimensions 10 ft × 12 ft, with a 4 ft × 6 ft rectangular section cut from one corner. Find the area of the room.
   (Total rectangle: 120 ft². Cut section: 24 ft². Room area: 120 − 24 = 96 ft²)` : '5. A rectangular pool is 25 m long and 15 m wide. What area of pool cover is needed? (A = 375 m²)'}${instrNote}`,
    'integers': `Independent Practice — Adding and Subtracting Integers (≈${t(0.20)} min)

Part A — Calculate:
1.  (−8) + (−5) = −13
2.  12 + (−7) = 5
3.  (−9) + 4 = −5
4.  6 − (−3) = 9   (KCC: 6 + 3 = 9)
5.  −10 − 4 = −14
6.  −2 − (−8) = 6  (KCC: −2 + 8 = 6)
7.  15 + (−15) = 0
8.  −4 + (−4) + 8 = 0

Part B — Word problem:
9. The temperature at noon was 7°F. By midnight it had dropped 18 degrees. What was the midnight temperature? (7 + (−18) = −11°F)

10. A diver is at −24 feet (24 feet below sea level). She rises 9 feet. Then she descends 5 more feet. What is her final depth? (−24 + 9 − 5 = −24 + 9 + (−5) = −20 feet)${instrNote}`,
    'percentages': `Independent Practice — Percents (≈${t(0.20)} min)

Show your method. Round money answers to the nearest cent.

1. What is 40% of 75?           (0.40 × 75 = 30)
2. What is 15% of $48?          (0.15 × 48 = $7.20)
3. What is 6% of $23.50?        (0.06 × 23.50 = $1.41)
4. A $90 jacket is on sale for 30% off. What is the sale price?  ($90 − 0.30 × $90 = $90 − $27 = $63)
5. There are 800 students at a school; 35% ride the bus. How many students ride the bus? (0.35 × 800 = 280 students)
${gradeNum >= 7 ? `6. A sneaker's price increased from $60 to $75. What is the percent increase? ((75 − 60) ÷ 60 × 100 = 25%)
7. A TV dropped from $480 to $384. What is the percent decrease? ((480 − 384) ÷ 480 × 100 = 20%)` : `6. A student got 38 out of 50 questions correct. What percent did they get right? (38 ÷ 50 × 100 = 76%)`}${instrNote}`,
    'multiplication-partial-products': gradeNum <= 4 ? `Independent Practice — Partial Products (≈${t(0.20)} min)

Show the decomposition and both partial products before writing the final product.

Part A:
1. 31 × 5:  30 × 5 = 150; 1 × 5 = 5; 150 + 5 = 155
2. 62 × 4:  60 × 4 = 240; 2 × 4 = 8; 240 + 8 = 248
3. 43 × 6:  40 × 6 = 240; 3 × 6 = 18; 240 + 18 = 258
4. 57 × 3:  50 × 3 = 150; 7 × 3 = 21; 150 + 21 = 171
5. 86 × 4:  80 × 4 = 320; 6 × 4 = 24; 320 + 24 = 344

Part B — Estimate, then solve:
6. 78 × 6: Estimate: 80 × 6 = 480. Exact: 70 × 6 = 420; 8 × 6 = 48; 420 + 48 = 468.

Part C — Word problem:
7. A school orders 24 boxes of crayons. Each box holds 8 crayons. How many crayons in all?
   (Answer: 24 × 8 → 20 × 8 = 160; 4 × 8 = 32; 160 + 32 = 192 crayons)

Part D — Error analysis:
8. A student solved 65 × 4 as: 6 × 4 = 24; 5 × 4 = 20; 24 + 20 = 44. What was the error?
   (Answer: They multiplied 6 × 4 instead of 60 × 4. The tens digit represents 60, not 6. Correct: 60 × 4 = 240; 5 × 4 = 20; 240 + 20 = 260.)${instrNote}` : `Independent Practice — Partial Products: Multi-Digit (≈${t(0.20)} min)

Draw the four-section area model and record all partial products before writing the final product.

1. 24 × 13: 20×10=200; 20×3=60; 4×10=40; 4×3=12; total = 312
2. 37 × 25: 30×20=600; 30×5=150; 7×20=140; 7×5=35; total = 925
3. 45 × 32: 40×30=1,200; 40×2=80; 5×30=150; 5×2=10; total = 1,440
4. 63 × 41: 60×40=2,400; 60×1=60; 3×40=120; 3×1=3; total = 2,583
5. 76 × 54: 70×50=3,500; 70×4=280; 6×50=300; 6×4=24; total = 4,104

Word problem:
6. A theater has 48 rows with 35 seats each. How many seats total?
   (Answer: 48 × 35 → 40×30=1,200; 40×5=200; 8×30=240; 8×5=40; total = 1,680 seats)

Estimation check:
7. Estimate 67 × 43 by rounding first: 70 × 40 = 2,800. Then calculate using partial products.
   (Answer: 60×40=2,400; 60×3=180; 7×40=280; 7×3=21; total = 2,881. Close to estimate ✓)${instrNote}`,
    'multiplication': gradeNum <= 4 ? `Independent Practice — Multiplication (≈${t(0.20)} min)

Solve each problem. Show your work — write the equation and all steps.

1. 3 × 42 = (3 × 40) + (3 × 2) = 120 + 6 = 126
2. 5 × 61 = (5 × 60) + (5 × 1) = 300 + 5 = 305
3. 6 × 38 = (6 × 30) + (6 × 8) = 180 + 48 = 228
4. 4 × 74 = (4 × 70) + (4 × 4) = 280 + 16 = 296
5. 7 × 25 = (7 × 20) + (7 × 5) = 140 + 35 = 175
6. 9 × 53 = (9 × 50) + (9 × 3) = 450 + 27 = 477

Word problem:
7. A shelf holds 28 books. There are 4 shelves in the library. How many books total?
   (Answer: 4 × 28 = 4 × 20 + 4 × 8 = 80 + 32 = 112 books)

Estimation check:
8. Before solving 8 × 67, write an estimate. Then solve.
   (Answer: Estimate: 8 × 70 = 560. Exact: 8 × 60 = 480; 8 × 7 = 56; 480 + 56 = 536.)${instrNote}` : `Independent Practice — Multi-Digit Multiplication (≈${t(0.20)} min)

Show all steps. Estimate before you compute.

1. 32 × 21: Estimate 30 × 20 = 600. Exact: 32 × 1 = 32; 32 × 20 = 640; total = 672.
2. 47 × 13: Estimate 50 × 13 = 650. Exact: 47 × 3 = 141; 47 × 10 = 470; total = 611.
3. 56 × 24: Estimate 60 × 24 = 1,440. Exact: 56 × 4 = 224; 56 × 20 = 1,120; total = 1,344.
4. 73 × 35: Exact: 73 × 5 = 365; 73 × 30 = 2,190; total = 2,555.
5. 84 × 42: Exact: 84 × 2 = 168; 84 × 40 = 3,360; total = 3,528.

Word problem:
6. A factory produces 65 toy cars each hour. How many are produced in a 38-hour week?
   (Answer: 65 × 38 → 65 × 8 = 520; 65 × 30 = 1,950; 520 + 1,950 = 2,470 toy cars)${instrNote}`,
  };

  return ipMap[type] ??
    `Independent Practice — ${topic} (≈${t(0.20)} min)

Solve each problem independently. Show all steps.

1. Use today's skill and strategy to solve a problem chosen by your teacher. Show every step and label your strategy.  (Answer: Completed by student — teacher verifies strategy and computation)
2. Solve a second problem at the same difficulty level. Show all steps. Check your answer using the inverse operation.  (Answer: Completed by student — teacher verifies)
3. Solve a problem that includes a remainder or an additional step. Write one sentence explaining what the remainder means in context.  (Answer: Completed by student — teacher verifies interpretation)
4. Solve a real-world problem your teacher posts on the board. Write what is known, what is unknown, and your complete solution.  (Answer: Completed by student — teacher verifies setup and solution)
5. Check one of your answers from above by working backwards. Write the checking equation and confirm it is correct.  (Answer: Completed by student — teacher verifies checking process)

Check each answer against the success criteria before moving on.${instrNote}`;
}

function generateMathExitTicket(ctx: Ctx): string {
  const { topic, instrNote, gradeNum } = ctx;
  const type = mathTopic(ctx.tl);

  const etMap: Partial<Record<MathTopic, string[]>> = {
    'fractions-compare': [
      `Compare ³⁄₅ and ⁷⁄₁₀. Write < , >, or = and name the strategy you used. Show your work.`,
      `(Answer: ³⁄₅ = ⁶⁄₁₀ < ⁷⁄₁₀; strategy: convert to common denominator of 10.)`,
      `Bonus: Write one fraction greater than ²⁄₃ and one fraction less than ²⁄₃. Explain how you know.`,
    ],
    'fractions-add-sub': [
      `Solve: ²⁄₃ + ¹⁄₄. Show every step including finding the common denominator.`,
      `(Answer: LCD = 12; ⁸⁄₁₂ + ³⁄₁₂ = ¹¹⁄₁₂.)`,
      `A garden has ⁵⁄₈ acre of vegetables and ¹⁄₄ acre of flowers. How many acres in total?`,
      `(Answer: LCD = 8; ⁵⁄₈ + ²⁄₈ = ⁷⁄₈ acre.)`,
    ],
    'fractions-equivalent': [
      `1. ³⁄₄ = __/12. Name the multiplier, write the complete equation, and draw a fraction bar model showing both fractions shade the same amount.  (Answer: ×3. ³⁄₄ = ⁹⁄₁₂)`,
      `2. In one sentence, explain why multiplying both the numerator and denominator by the same nonzero number does not change the fraction's value.  (Answer: Open response — accept any explanation that references equal subdivision of each piece)`,
    ],
    'algebra-equations': [
      `Solve and verify: 4x − 7 = 21.`,
      `(4x = 28; x = 7. Check: 4(7) − 7 = 28 − 7 = 21 ✓)`,
      `Write your own two-step equation and solve it. Label the unknown and show each step.`,
    ],
    'geometry-area-perimeter': [
      `A rectangle has a perimeter of 44 meters and a width of 8 meters. What is its length? What is its area?`,
      `(P = 2l + 2(8) = 44 → 2l = 28 → l = 14 m; A = 14 × 8 = 112 m².)`,
      `Explain in one sentence why two rectangles can have the same area but different perimeters.`,
    ],
    'integers': [
      `Calculate: (−12) + 7 − (−5). Show each step using the KCC rule where needed.`,
      `(−12 + 7 = −5; −5 − (−5) = −5 + 5 = 0.)`,
      `The temperature at 6 a.m. was −8°F. By noon it rose 15°F. By midnight it dropped 11°F. What was the midnight temperature? Show your work.`,
      `(−8 + 15 = 7; 7 − 11 = −4°F.)`,
    ],
    'percentages': [
      `A $75 coat is discounted 20%. What is the sale price? Show your calculation.`,
      `(Discount = 0.20 × 75 = $15; Sale price = $75 − $15 = $60.)`,
      `Is it better to take 30% off and then 10% off, or 40% off at once? Explain with numbers using a $100 item.`,
      `(Sequential: $100 × 0.70 = $70; $70 × 0.90 = $63. Single: $100 × 0.60 = $60. The single discount is better.)`,
    ],
    'ratios-proportions': [
      `A car travels 150 miles in 3 hours. At this rate, how far will it travel in 5 hours? Set up and solve a proportion.`,
      `(¹⁵⁰⁄₃ = ⁿ⁄₅ → 3n = 750 → n = 250 miles.)`,
      `Explain in your own words: how do you know two ratios form a proportion?`,
    ],
    'multiplication-partial-products': [
      `Multiply 47 × 8 using the partial products strategy. Show the decomposition, both partial products, and the final sum.`,
      `(Answer: Decompose 47 = 40 + 7. Partial products: 40 × 8 = 320; 7 × 8 = 56. Sum: 320 + 56 = 376.)`,
      `In one sentence, explain what "partial products" means and why you must add the two partial products together at the end.`,
    ],
    'multiplication': gradeNum <= 4 ? [
      `Solve 6 × 54. Show your work — write the equation and every step.`,
      `(Answer: 6 × 50 = 300; 6 × 4 = 24; 300 + 24 = 324.)`,
      `A box holds 48 crayons. How many crayons are in 7 boxes? Show your multiplication.`,
      `(Answer: 7 × 48 = 7 × 40 + 7 × 8 = 280 + 56 = 336 crayons.)`,
    ] : [
      `Solve 37 × 24. Estimate first, then calculate. Show all steps.`,
      `(Answer: Estimate: 40 × 24 = 960. Exact: 37 × 4 = 148; 37 × 20 = 740; 148 + 740 = 888.)`,
      `In 1–2 sentences, explain why multiplying a 2-digit number by a 2-digit number requires two partial multiplications (once for each digit of the multiplier).`,
    ],
  };

  const items = etMap[type] ?? [
    `Show what you know: ${ctx.skillToTeach.replace(/\.$/, '')}. Work through one complete example, labeling each step.`,
    `Explain in 1–2 sentences: what is the key step in today's strategy, and what would happen if you skipped it?`,
    `Rate your confidence with today's skill from 1 (still confused) to 5 (I could teach this to someone). Write one specific question you still have.`,
  ];
  return items.join('\n') + (instrNote ? instrNote : '');
}

function generateMathDifferentiation(ctx: Ctx): string {
  const { topic } = ctx;
  const type = mathTopic(ctx.tl);

  const diffMap: Partial<Record<MathTopic, [string[], string[]]>> = {
    'fractions-compare': [
      [
        'Provide fraction tile manipulatives or pre-drawn fraction bars for physical comparison.',
        'Limit to same-denominator and benchmark-fraction strategies; introduce common denominator only when ready.',
        'Offer a strategy reference card listing all four comparison strategies (same denominator, same numerator, benchmark ½, common denominator) with a step-by-step example of each.',
        'Pre-highlight the numerator in one color and denominator in another on worksheets.',
      ],
      [
        'Challenge students to compare three fractions and order them from least to greatest using a different strategy for each pair.',
        'Pose: "Can you write a fraction between ²⁄₃ and ³⁄₄? How many fractions exist between them?"',
        'Extend to fractions greater than 1: compare 1²⁄₃ and 1³⁄₄ using the same strategies.',
        'Create their own "Fraction War" card set with comparisons that require every strategy, with an answer key.',
      ],
    ],
    'algebra-equations': [
      [
        'Provide a two-column "balance scale" graphic organizer to track each operation on both sides.',
        'Begin with one-step equations using only addition and subtraction before introducing multiplication.',
        'Supply a reference card showing each inverse operation pair with a worked example.',
      ],
      [
        'Solve equations with variables on both sides: 3x + 2 = x + 14.',
        'Write and solve equations for real-world scenarios of increasing complexity.',
        'Explore inequalities: 2x + 3 < 15 — solve, graph on a number line, and give three values in the solution set.',
      ],
    ],
    'integers': [
      [
        'Use colored counters (red = negative, yellow = positive) for all addition and subtraction calculations.',
        'Restrict problems to integers between −10 and 10 until the zero-sum pattern is mastered.',
        'Provide a number line strip at the desk for all calculations.',
      ],
      [
        'Explore multiplication and division of integers: what patterns emerge with signs?',
        'Apply integer operations to coordinate graphing — plot points in all four quadrants.',
        'Research real-world integer applications (stock market changes, oceanography) and write a two-paragraph explanation.',
      ],
    ],
  };

  const fallbackSupport = [
    `Provide a step-by-step reference card with a worked example of ${topic} students can use during independent practice.`,
    'Offer partially completed problems (fill-in-the-blank style) to reduce cognitive load while maintaining the thinking.',
    'Allow partner support or peer tutoring during independent practice.',
    'Pre-teach key vocabulary in a small group before the main lesson.',
    'Break multi-step problems into numbered sub-steps with visual cues.',
  ];
  const fallbackEnrich = [
    `Challenge students to apply ${topic} to a multi-step real-world problem not covered in the lesson.`,
    `Ask: "Can you create a problem for a classmate using ${topic}? Write the answer key, too."`,
    'Introduce a related advanced concept that extends today\'s learning one level deeper.',
    'Have students write a brief "Math Journal" entry explaining today\'s strategy as if teaching an absent classmate.',
  ];

  const [support, enrich] = diffMap[type] ?? [fallbackSupport, fallbackEnrich];
  return [
    'SUPPORT STRATEGIES:',
    ...support.slice(0, 4).map(s => `• ${s}`),
    '',
    'EXTENSION / ENRICHMENT STRATEGIES:',
    ...enrich.slice(0, 4).map(s => `• ${s}`),
  ].join('\n');
}

function generateMathExtension(ctx: Ctx): string {
  const { topic, gradeNum } = ctx;
  const type = mathTopic(ctx.tl);

  const extMap: Partial<Record<MathTopic, string>> = {
    'fractions-compare': `Fraction Detective: Students receive a set of six "mystery fractions" where all they're told is which fraction is larger and the numerator of the smaller one. They must use mathematical reasoning — not guess-and-check — to find a valid denominator that makes each comparison true, then explain their reasoning in writing. Example: "¹⁄? > ²⁄₅ — what denominators for 1 make this true?" (Any denominator < 2.5, so 1 or 2: ¹⁄₁ = 1 > ²⁄₅ ✓; ¹⁄₂ > ²⁄₅ ✓; ¹⁄₃ < ²⁄₅ ✗.) Students share findings with the class in the next lesson.`,
    'fractions-add-sub': `Recipe Math Challenge: Students scale a real recipe up to serve 30 people and down to serve 2. They must add and subtract fractional measurements accurately, convert between cups and fluid ounces where noted, and explain in writing why the scaled amounts maintain the recipe's ratios.`,
    'algebra-equations': `Create an "Escape Room" puzzle for a partner: write a series of 5 equations where solving each gives a digit of a combination code (e.g., 3x + 4 = 25 → x = 7, first digit is 7). Trade with a partner and race to solve the code. Include an answer key with full work shown.`,
    'integers': gradeNum >= 7
      ? `Stock Market Simulation: Students receive a fictional stock with a starting price and a table of daily integer gains and losses over two weeks. They calculate the running balance, find the mean daily change, determine the highest and lowest portfolio values, and write a one-paragraph "investor report" summarizing the stock's performance.`
      : `Temperature Challenge: Students research the record high and record low temperatures for five different cities around the world, calculate the range (difference) between the extremes, find the mean of the five record lows, and create a visual display comparing the cities using a number line.`,
    'geometry-area-perimeter': `Design a Dream Space: Students sketch a floor plan for a bedroom, classroom, or park using a scale of 1 cm = 1 m. They must label all dimensions, calculate the total floor area and perimeter, identify three composite shapes within the plan and compute their individual areas, and find the total cost of carpeting at a given price per square meter. Present to a partner explaining all calculations.`,
    'percentages': `Consumer Math Project: Students research three real products online (or use teacher-provided price lists), calculate the final cost after various discounts and taxes, compare which combination of deals gives the lowest total, and write a one-page "shopper's guide" with clear calculations justifying their recommendation.`,
  };

  return extMap[type] ??
    (gradeNum >= 9
      ? `Students who demonstrate mastery write a rigorous extension problem about ${topic} with a full solution key, then present it to the class as a 3-minute "mini-lesson." The problem must include at least one real-world context and require two or more distinct steps.`
      : `Students create a "Teaching Poster" for ${topic} that includes: the key vocabulary with student-friendly definitions, a worked example with every step labeled, a "common mistake" section showing what goes wrong and why, and a practice problem with answer key. Display finished posters around the classroom.`);
}

function generateMathHomework(ctx: Ctx): string {
  const { topic, gradeNum } = ctx;
  const type = mathTopic(ctx.tl);

  const hwMap: Partial<Record<MathTopic, string>> = {
    'fractions-compare': `Complete problems 1–8 comparing fractions (mixed strategy set). Show all work and label the strategy used for each comparison. Also write 2–3 sentences answering: "When would you choose the benchmark fraction strategy instead of finding a common denominator?" Be ready to share your reasoning tomorrow.`,
    'fractions-add-sub': `Solve the 8 assigned addition and subtraction of fractions problems. Simplify all answers. Then find one recipe at home that uses fractional measurements and write out what the total amount of each ingredient would be if you doubled the recipe. Bring your work to class.`,
    'fractions-equivalent': `Equivalent Fractions Homework
For each problem: name the multiplier, then write the complete equation.

1. ¹⁄₂ = __/8  (Answer: ×4. ⁴⁄₈)
2. ¹⁄₃ = __/9  (Answer: ×3. ³⁄₉)
3. ²⁄₅ = __/10  (Answer: ×2. ⁴⁄₁₀)
4. ³⁄₄ = __/12  (Answer: ×3. ⁹⁄₁₂)
5. ²⁄₃ = __/12  (Answer: ×4. ⁸⁄₁₂)
6. ⁵⁄₆ = __/18  (Answer: ×3. ¹⁵⁄₁₈)
7. Draw a fraction bar showing ¹⁄₄ = ²⁄₈. Explain in one sentence what happened to each fourth.  (Answer: ²⁄₈. Each fourth is divided into 2 equal parts.)
8. A recipe calls for ²⁄₃ cup of sugar. Maria uses a ¹⁄₁₂-cup scoop. How many scoops does she need? Write the equivalent fraction.  (Answer: ×4. ²⁄₃ = ⁸⁄₁₂. Maria needs 8 scoops.)`,
    'algebra-equations': `Solve equations 1–10 from the handout. Show all steps and verify each answer. Then write and solve an equation based on something in your own life (e.g., saving money, tracking scores, splitting a bill). Explain what the variable represents.`,
    'geometry-area-perimeter': `Complete the perimeter and area problem set (8 problems, both rectangles and composite figures). For extra challenge: measure a room in your home, calculate the area, and estimate how many square feet of flooring it would require. Show all work.`,
    'integers': `Complete the 10-problem integer practice set. Show all steps including the KCC rule where applicable. Then create a word problem using integers based on a real-world scenario (temperature, elevation, money) — include the solution.`,
    'percentages': `Complete the 8-problem percent practice set. Show all work. Choose one receipt or price tag from home and calculate: (a) the amount saved if there were a 15% discount, and (b) the total cost with a ${gradeNum >= 7 ? '7%' : '5%'} sales tax. Bring the receipt and your calculations to class.`,
    'multiplication-partial-products': gradeNum <= 4
      ? `Multiply using the partial products strategy. Write the decomposition and both partial products for each problem.\n\n1. 21 × 7: (20 × 7) + (1 × 7) = 140 + 7 = 147\n2. 34 × 5: (30 × 5) + (4 × 5) = 150 + 20 = 170\n3. 48 × 3: (40 × 3) + (8 × 3) = 120 + 24 = 144\n4. 53 × 6: (50 × 6) + (3 × 6) = 300 + 18 = 318\n5. 72 × 8: (70 × 8) + (2 × 8) = 560 + 16 = 576\n6. 65 × 4: (60 × 4) + (5 × 4) = 240 + 20 = 260\n\nWord problem: A parking lot has 37 rows with 6 cars in each row. How many cars fit?\n(Answer: 37 × 6 → 30 × 6 = 180; 7 × 6 = 42; 180 + 42 = 222 cars)`
      : `Use partial products to multiply. Draw the area model and record all four partial products.\n\n1. 24 × 13: 20×10=200; 20×3=60; 4×10=40; 4×3=12; total = 312\n2. 35 × 24: 30×20=600; 30×4=120; 5×20=100; 5×4=20; total = 840\n3. 46 × 32: 40×30=1,200; 40×2=80; 6×30=180; 6×2=12; total = 1,472\n4. 57 × 23: 50×20=1,000; 50×3=150; 7×20=140; 7×3=21; total = 1,311\n5. 68 × 41: 60×40=2,400; 60×1=60; 8×40=320; 8×1=8; total = 2,788\n\nWord problem: A garage has 56 floors with 34 spaces each. How many spaces total?\n(Answer: 56 × 34 → 50×30=1,500; 50×4=200; 6×30=180; 6×4=24; total = 1,904 spaces)`,
    'multiplication': gradeNum <= 4
      ? `Solve each multiplication problem. Show your work.\n\n1. 3 × 51: (3 × 50) + (3 × 1) = 150 + 3 = 153\n2. 7 × 24: (7 × 20) + (7 × 4) = 140 + 28 = 168\n3. 8 × 46: (8 × 40) + (8 × 6) = 320 + 48 = 368\n4. 6 × 73: (6 × 70) + (6 × 3) = 420 + 18 = 438\n5. 9 × 57: (9 × 50) + (9 × 7) = 450 + 63 = 513\n\nWord problem: A school has 32 classrooms. Each classroom has 9 rows of 4 desks. How many desks total?\n(Answer: Each room has 9 × 4 = 36 desks. 32 × 36 → 30×36=1,080; 2×36=72; total = 1,152 desks.)`
      : `Solve each problem. Show all steps and estimate to check.\n\n1. 28 × 15: Estimate 30×15=450. Exact: 28×5=140; 28×10=280; total = 420.\n2. 43 × 26: 43×6=258; 43×20=860; total = 1,118.\n3. 67 × 34: 67×4=268; 67×30=2,010; total = 2,278.\n4. 82 × 47: 82×7=574; 82×40=3,280; total = 3,854.\n5. 95 × 63: 95×3=285; 95×60=5,700; total = 5,985.\n\nWord problem: A stadium has 94 sections with 52 seats each. How many seats total?\n(Answer: 94×2=188; 94×50=4,700; 188+4,700 = 4,888 seats.)`,
  };

  return hwMap[type] ??
    `Practice: ${ctx.skillToTeach.replace(/\.$/, '')}.\n\nSolve 6 problems applying today's skill. Show all steps for each. For at least one problem, write 1–2 sentences explaining the strategy you used and why it works. Bring your work to class — be ready to explain your reasoning on one problem.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ELA GENERATORS
// ═══════════════════════════════════════════════════════════════════════════════

function generateELAVocabulary(ctx: Ctx): string {
  const type = elaTopic(ctx.tl);
  const { topic } = ctx;

  const vocabSets: Partial<Record<ELATopic, string[]>> = {
    'main-idea': [
      'Main idea — The central point or most important message of a text; what the whole passage is mostly about.',
      'Supporting detail — A specific fact, example, reason, or explanation that tells more about the main idea.',
      'Topic sentence — The sentence (often first) that states the main idea of a paragraph.',
      'Summarize — To briefly restate the most important information using your own words, leaving out minor details.',
      'Relevant — Closely connected to or important for the topic being discussed.',
      'Text evidence — Specific words or sentences from the passage used to support a claim or answer.',
    ],
    'authors-purpose': [
      "Author's purpose — The reason an author writes a text: to persuade, to inform, or to entertain (PIE).",
      'Persuade — To convince the reader to agree with a position, take an action, or change a belief.',
      'Inform — To provide factual information or explain a topic clearly.',
      'Entertain — To engage the reader through story, humor, or creative writing.',
      'Bias — A preference toward one side; may influence how an author presents information.',
      'Audience — The specific readers an author has in mind when writing.',
      'Tone — The author\'s attitude toward the subject, shown through word choice and style.',
    ],
    'inference': [
      'Inference — A conclusion the reader draws by combining text evidence with prior knowledge.',
      'Text evidence — Specific words, phrases, or sentences from the passage that support a conclusion.',
      'Implied — Something suggested but not directly stated in the text.',
      'Conclusion — A logical judgment based on evidence; the result of inferential thinking.',
      'Prior knowledge — What you already know from experience or learning that helps you understand a text.',
      'Context clue — Words or phrases near an unfamiliar word that help reveal its meaning.',
    ],
    'narrative': [
      'Narrative — A story; a text that describes a sequence of events involving characters.',
      'Plot — The sequence of events in a story: exposition, rising action, climax, falling action, resolution.',
      'Protagonist — The main character; the one the story centers on.',
      'Antagonist — The character or force in conflict with the protagonist.',
      'Conflict — The central struggle or problem that drives the story forward.',
      'Setting — The time and place in which the story occurs.',
      'Theme — The central message or life lesson the story conveys.',
      'Point of view — The perspective from which a story is told (first, second, or third person).',
    ],
    'opinion-argument': [
      'Claim — The main argument or position the writer takes on an issue.',
      'Evidence — Facts, statistics, examples, or expert opinions used to support the claim.',
      'Reasoning — An explanation connecting the evidence to the claim.',
      'Counterclaim — An opposing argument the writer must acknowledge and respond to.',
      'Rebuttal — The writer\'s response to the counterclaim, showing why it does not weaken the claim.',
      'Credible source — A reliable, trustworthy source of information (expert, peer-reviewed, established organization).',
      'Transition — A word or phrase connecting ideas (e.g., furthermore, however, in contrast).',
    ],
    'figurative-language': [
      'Figurative language — Words or phrases that go beyond their literal meaning to create images, emphasis, or emotion.',
      'Simile — A comparison using "like" or "as." Example: Her laughter was like music.',
      'Metaphor — A direct comparison without "like" or "as." Example: Time is a thief.',
      'Personification — Giving human qualities to a non-human thing. Example: The wind whispered secrets.',
      'Hyperbole — Extreme exaggeration for effect. Example: I\'ve told you a million times!',
      'Idiom — A phrase whose meaning differs from the literal meaning of its words. Example: "Break a leg" means "good luck."',
      'Alliteration — Repetition of the same beginning consonant sound. Example: Peter Piper picked a peck of peppers.',
    ],
    'grammar-parts-of-speech': [
      'Noun — A word that names a person, place, thing, or idea. (Examples: teacher, city, courage)',
      'Pronoun — A word that replaces a noun. (Examples: he, she, they, it)',
      'Verb — A word that expresses action or state of being. (Examples: run, think, is)',
      'Adjective — A word that describes or modifies a noun. (Examples: tall, curious, ancient)',
      'Adverb — A word that modifies a verb, adjective, or other adverb, often ending in -ly. (Examples: quickly, very, carefully)',
      'Preposition — A word showing the relationship between a noun and another word. (Examples: in, on, under, between)',
      'Conjunction — A word that connects words, phrases, or clauses. (Examples: and, but, because, although)',
    ],
    'poetry': [
      'Stanza — A group of lines in a poem; like a paragraph in prose.',
      'Rhyme scheme — The pattern of rhymes at the end of each line in a poem (marked A, B, C, etc.).',
      'Meter — The rhythmic pattern of stressed and unstressed syllables in a line of poetry.',
      'Imagery — Descriptive language that appeals to the senses — sight, sound, taste, touch, smell.',
      'Alliteration — Repetition of initial consonant sounds in close proximity.',
      'Repetition — Intentional reuse of words, phrases, or lines to create emphasis or rhythm.',
      'Free verse — Poetry without a fixed rhyme scheme or meter.',
      'Tone — The speaker\'s attitude toward the poem\'s subject, revealed through word choice.',
    ],
    'vocabulary-context': [
      'Context clue — Information in the surrounding words or sentences that helps reveal an unfamiliar word\'s meaning.',
      'Definition clue — A context clue where the meaning is directly stated. Signal words: is, means, refers to.',
      'Synonym clue — A context clue using a word with a similar meaning nearby.',
      'Antonym clue — A context clue using a word with an opposite meaning. Signal words: however, unlike, but.',
      'Example clue — A context clue that gives examples illustrating the word\'s meaning. Signal words: such as, for example, including.',
      'Prefix — A word part added to the beginning of a root word to change its meaning. (Examples: un-, re-, pre-, mis-)',
      'Suffix — A word part added to the end of a root word. (Examples: -ful, -less, -tion, -able)',
    ],
    'grammar-sentences': [
      'Sentence — A group of words that contains a subject and predicate and expresses a complete thought.',
      'Subject — The noun or pronoun that the sentence is about; who or what the sentence is discussing.',
      'Predicate — The part of the sentence containing the verb; tells what the subject does or is.',
      'Fragment — An incomplete sentence missing a subject, predicate, or complete thought.',
      'Run-on sentence — Two or more independent clauses joined without correct punctuation or a conjunction.',
      'Independent clause — A group of words with a subject and verb that expresses a complete idea.',
      'Dependent clause — A group of words with a subject and verb that cannot stand alone as a sentence.',
      'Conjunction — A connecting word (and, but, because, although) used to join clauses.',
    ],
  };

  const terms = vocabSets[type] ?? [
    `${topic} — The central focus of today's lesson; students will be able to define and apply this concept by the end of class.`,
    'Text evidence — Specific words or sentences from a reading used to support an answer or claim.',
    'Analysis — Careful examination of a text, idea, or structure to understand it more deeply.',
    'Craft and structure — The author\'s choices about word selection, organization, and point of view.',
    'Argument — A claim supported by reasons and evidence intended to convince an audience.',
  ];

  return terms.join('\n');
}

function generateELAWarmUp(ctx: Ctx): string {
  const { topic, obj, instrNote } = ctx;
  const type = elaTopic(ctx.tl);

  const warmUps: Partial<Record<ELATopic, string>> = {
    'main-idea': `Project a short, unlabeled paragraph (5–7 sentences) on the board — choose a high-interest topic students know (e.g., wolves in Yellowstone, the invention of chocolate, extreme weather). Ask students: "In 10 words or fewer, what is this paragraph mostly about?" Give 60 seconds to write, then share with a partner. Take 3–4 responses. Discuss: "Were your answers exactly the same? Why do some words matter more than others?" Bridge: "Today we'll learn a strategy for finding the main idea in any text — even when the author doesn't state it directly."${instrNote}`,
    'authors-purpose': `Display three short excerpts — one ad for sneakers (persuade), one encyclopedia entry about dolphins (inform), and one funny short story opener (entertain). Ask: "These were written by three different authors for very different reasons. What clue words, styles, or structures helped you know the purpose of each?" Partners discuss (90 seconds), then share out. Build the PIE anchor chart together. Say: "Today we'll look closely at how authors signal their purpose — and how knowing the purpose makes you a stronger reader."${instrNote}`,
    'inference': `Project this sentence: "Maya slammed her locker, shoved her backpack on, and walked past her best friend without a word." Ask: "What do you know about Maya right now? What happened? How does she feel? Did the author tell you any of this directly?" Partners discuss, then share inferences and the text evidence behind them. Establish: "An inference = text clue + what I know from experience. Today you'll practice making and defending inferences using specific evidence."${instrNote}`,
    'narrative': `Read aloud the first paragraph of a short story without revealing the title. Stop at a tension point and ask: "What is the conflict? Who is the protagonist? What do you predict happens next?" Students quick-write their predictions (60 seconds). Share 3–4 and discuss: "What text clues led to your prediction?" Connect: "A strong narrative pulls readers in immediately. Today we'll study the elements that make stories work — and you'll use them in your own writing."${instrNote}`,
    'opinion-argument': `Show students a short, real opinion piece headline: "Schools Should Start Later." Ask: "Raise your hand if you agree. Now: can you give me two specific reasons? And can anyone argue the other side?" Run a quick 3-minute structured debate: 2 students argue for, 2 argue against. Debrief: "Which argument was more convincing — and why? Was it the emotion, the facts, the logic?" Bridge: "Today you'll learn what makes an argument truly persuasive in writing."${instrNote}`,
    'figurative-language': `Read aloud 3 sentences: 1) "She was nervous." 2) "Her heart hammered like a drum in a thunderstorm." 3) "Butterflies turned cartwheels in her stomach." Ask: "Which is most vivid? Which creates the strongest picture in your mind? Why?" Students rank and explain to a partner. Pull out the figurative language in #2 and #3. Ask: "What makes these phrases powerful — and what do they have in common?" Bridge: "Today we're going to learn the names and rules for different types of figurative language and practice using them in our own writing."${instrNote}`,
    'poetry': `Read aloud a short, powerful poem (≈8 lines) without showing the text. Ask students to close their eyes and note: "What images appear? What emotions do you feel? What words stand out?" After the reading, show the text and have students annotate what they noticed. Share: "What does this poem do that prose doesn't? What makes it feel different?" Connect: "Today we'll explore the tools poets use to create those effects — and you'll try them yourself."${instrNote}`,
    'vocabulary-context': `Project a sentence with an unfamiliar word highlighted: "The scientist's findings were so abstruse that even other experts struggled to understand them." Cover the word and ask: "Based on the surrounding words, what does abstruse probably mean?" Think-pair-share (60 seconds). Reveal the definition (extremely complex or obscure). Ask: "Which words helped you figure it out?" Name the type of clue. Bridge: "Today you'll practice using four different types of context clues to figure out unfamiliar vocabulary — a skill you'll use every time you read."${instrNote}`,
  };

  return warmUps[type] ??
    `Display a short high-interest text (3–5 sentences) related to ${topic}. Students read independently and write one observation and one question in their journals. Partners share (60 seconds). Cold-call 2–3 responses and use them to set up today's learning goal: students will ${obj}. Ask: "What do you already know about ${topic} that could help you as a reader today?"${instrNote}`;
}

function generateELADirectInstruction(ctx: Ctx): string {
  const { topic, instrNote } = ctx;
  const type = elaTopic(ctx.tl);

  const steps: Partial<Record<ELATopic, string[]>> = {
    'main-idea': [
      `Explain the two-part rule: "The main idea is (1) what or who the text is mostly about, AND (2) the most important thing the author says about it." It's not just the topic — it includes the author's message.`,
      `Model with a sample paragraph. Think aloud: "First I ask — what is this paragraph about? (Topic: monarch butterflies.) Then I ask — what is the most important thing being said about them? (They migrate thousands of miles each year.)" Combine: "The main idea is that monarch butterflies make an extraordinary annual migration."`,
      `Show the "zoom out" strategy: if every sentence were a photo, the main idea is what caption you'd put under the whole album.`,
      `Demonstrate finding implicit main ideas — when no single sentence states it, use: "Read all the details. What one idea do they ALL point to?" Model with a second paragraph. Post both strategies on the anchor chart.${instrNote}`,
    ],
    'inference': [
      `Define inference using the equation: Evidence + Prior Knowledge = Inference. Post it on the board and walk through each term with a concrete classroom example.`,
      `Read a short passage aloud. Stop at a strong inference point: "The author doesn't say this directly — but look at the evidence. What can we conclude?" Think aloud: state the evidence, connect it to background knowledge, form the inference, and then write it as a sentence.`,
      `Show the "Strong Inference Checklist": ✓ I can point to exact words in the text. ✓ My inference explains something the author implies but doesn't state. ✓ A different reader looking at the same text could form the same inference from the same evidence.`,
      `Model one more inference together; then invite students to suggest the evidence before you state the inference, to build the habit of returning to the text first.${instrNote}`,
    ],
    'opinion-argument': [
      `Present the three-part argument structure: Claim (what you believe) → Evidence (proof from facts/examples/experts) → Reasoning (explanation connecting evidence to claim). Draw an "argument pyramid" on the board.`,
      `Model writing a strong claim statement for a class debate prompt. Show the difference: weak claim = "I think homework is bad." Strong claim = "Schools should limit homework to 30 minutes per night to improve student well-being without sacrificing academic achievement."`,
      `Demonstrate selecting and citing evidence: "I choose this statistic because… The example I used shows…" Model reasoning by asking "so what?" after each piece of evidence.`,
      `Introduce and model the counterclaim paragraph: "A strong arguer acknowledges the other side. Here's how: 'Some might argue that… however, this overlooks the fact that…'" Write one together.${instrNote}`,
    ],
    'figurative-language': [
      `Create an anchor chart with five columns: Simile | Metaphor | Personification | Hyperbole | Idiom. Provide definition, example, and effect for each.`,
      `Model identifying figurative language in a published text excerpt. Think aloud for each example: "This is a metaphor because… The effect it creates is… If the author had written it literally instead, it would say…"`,
      `Show how to analyze figurative language: not just name it, but explain what it means literally, what it means figuratively, and why the author chose it.`,
      `Model writing one example of each type connected to a shared class topic. Think aloud as you draft: "I want to show how nervous the character is. A simile using sound could be… A metaphor could be…"${instrNote}`,
    ],
    'narrative': [
      `Map the five elements of plot on a story arc: Exposition (introduce characters, setting, problem) → Rising Action (events that build tension) → Climax (the turning point, highest tension) → Falling Action (aftermath) → Resolution (how things end).`,
      `Apply the arc to a familiar mentor text or fairy tale. Ask students to supply each element while you annotate the arc on the board. Discuss: "Which is most important — and why?"`,
      `Introduce the concept of internal vs. external conflict. Show examples of each from the mentor text. Ask: "How does conflict create tension? What would the story be without it?"`,
      `Model planning a short narrative: begin with the conflict, work backward to the exposition, and work forward to the resolution. "Writers plan the ending before they draft the beginning."${instrNote}`,
    ],
    'grammar-sentences': [
      `Display four sentence types — simple, compound, complex, compound-complex — with clear examples. "A simple sentence has one independent clause. A compound sentence joins two independent clauses with a coordinating conjunction (FANBOYS) or semicolon. A complex sentence contains one independent and at least one dependent clause."`,
      `Identify and fix fragments: show 5 examples of fragments and model the process — identify what's missing (subject, verb, or complete thought), then rewrite as a complete sentence.`,
      `Identify and fix run-on sentences: show 3 run-ons and demonstrate four correction strategies: (1) add a period, (2) add a semicolon, (3) add a coordinating conjunction, (4) add a subordinating conjunction.`,
      `Write two student-level sentences on the board. Model combining them into a compound sentence, then a complex sentence. Ask: "How does the meaning shift? Which version is more sophisticated?"${instrNote}`,
    ],
    'poetry': [
      `Introduce three structural elements: stanza, line break, and rhyme scheme. Show how to mark rhyme scheme with letters (ABAB, AABB, etc.) using a simple poem on the board. Students annotate their own copy.`,
      `Introduce imagery: "Imagery is language that paints a picture using the five senses." Read one stanza aloud; students close eyes. Ask: "What do you see, hear, smell, taste, feel?" Identify which sensory details created each image.`,
      `Demonstrate how line breaks create meaning and emphasis: take a prose sentence and break it into lines three different ways. Discuss how each version changes tone, pacing, and emphasis.`,
      `Model writing a short poem on a class topic, thinking aloud: "I want to create a visual image here… The line break after this word will create a pause that emphasizes the surprise… I'm going to use personification in this line because…"${instrNote}`,
    ],
    'vocabulary-context': [
      `Introduce the four context clue types with signal words: Definition clue (signal: is, means, refers to), Synonym clue (signal: also called, or, in other words), Antonym clue (signal: however, unlike, but), Example clue (signal: such as, including, for example).`,
      `Model Type 1 — Definition clue: "The scientist studied ornithology, the branch of zoology dealing with birds." Think aloud: "I see the word 'the branch of zoology dealing with birds' — that's a direct definition right in the sentence."`,
      `Model Type 3 — Antonym clue: "Unlike her gregarious brother, Amara was quiet and preferred time alone." Think aloud: "Unlike and quiet/alone suggest gregarious is the opposite of quiet — so it means outgoing or sociable."`,
      `Model using prefix/suffix knowledge: "Un- means 'not'; -ful means 'full of'; the root 'believe' means to accept as true. So unbelievable means 'not able to be believed.'" Practice with two more words.${instrNote}`,
    ],
  };

  const chosen = steps[type];
  if (chosen) return chosen.map((s, i) => `${i + 1}. ${s}`).join('\n');

  return [
    `1. Introduce the concept of ${topic} with a clear, student-friendly definition and one concrete example drawn from a text students know.`,
    `2. Model the skill or strategy using a short text, thinking aloud through every decision: "I notice ____. This tells me ____. So I ____."`,
    `3. Provide a second, slightly harder example and invite student input before revealing the answer.`,
    `4. Create an anchor chart together that students can reference throughout the lesson and in future work.${instrNote}`,
  ].join('\n');
}

function generateELAGuidedPractice(ctx: Ctx): string {
  const { topic, t, instrNote } = ctx;
  const type = elaTopic(ctx.tl);

  const gpMap: Partial<Record<ELATopic, string>> = {
    'main-idea': `We-Do Reading (≈${t(0.20)} min)

Distribute or project a shared text — informational or literary depending on the unit. Read aloud together. After each paragraph, pause and ask:
• "What is this paragraph mostly about?"
• "What is the most important thing being said?"
• "Does this sentence state the main idea, or is it a supporting detail?"

Students annotate their own copy using a simple code: MI = main idea, SD = supporting detail. After reading, partners compare annotations and discuss disagreements. Whole-class debrief: "What is the main idea of the entire passage? Can you write it in one sentence — in your own words, not the author's?"${instrNote}`,
    'inference': `Text Evidence Scavenger Hunt (≈${t(0.20)} min)

Students receive a short passage and 5 inference questions. For each question:
1. Write the inference (what you conclude).
2. Quote the exact text evidence that supports it.
3. Write one sentence explaining how the evidence leads to the inference.

Partners compare answers. For any disagreement: "Who has stronger text evidence? Can we both be right?" Share out the two most debated inferences with the whole class and discuss what makes one more defensible.${instrNote}`,
    'opinion-argument': `Argument Analysis — "Strong or Weak?" (≈${t(0.20)} min)

Distribute a short opinion paragraph. Partners work together to:
1. Identify and underline the claim.
2. Circle each piece of evidence.
3. Put brackets around the reasoning.
4. Rate the argument's strength (1–4) and write 1–2 sentences justifying the rating.

Share ratings and reasoning with the class. Then: "What would make this argument stronger? What counterargument should it address?" Students revise the weakest sentence of the paragraph together.${instrNote}`,
    'figurative-language': `Figure Hunt and Analysis (≈${t(0.20)} min)

Partners receive a poem or prose excerpt rich in figurative language. They:
1. Highlight every example of figurative language.
2. Label the type in the margin (S = simile, M = metaphor, P = personification, etc.).
3. For their two strongest examples, write: "This _____ means (literal meaning). The author uses it to _____ (purpose/effect)."

Share one analysis each. Class votes: "Which example of figurative language is most effective, and why?"

Writing connection: students draft two original figurative language sentences about the shared class topic, then share with a partner for feedback.${instrNote}`,
    'narrative': `Story Mapping + Discussion (≈${t(0.20)} min)

Pairs complete a plot arc graphic organizer for the mentor text (or a short story excerpt). They must:
1. Identify the protagonist, antagonist, and main conflict.
2. List 3 rising action events in order.
3. Mark the climax and explain what makes it the turning point.
4. Note how the conflict is resolved.

Then each pair discusses: "What would change about the story if the setting were different? If the conflict were internal instead of external?"

Whole-class share: focus on the climax — several groups explain their choice and defend it with text evidence.${instrNote}`,
  };

  return gpMap[type] ??
    `Shared Practice — ${topic} (≈${t(0.20)} min)

Step 1: Teacher and students read a shared text together. Teacher models the strategy one more time, narrating every decision.

Step 2: Partners apply the strategy to the next section of text, recording their thinking in writing.

Step 3: Pairs share their analysis with another pair. Where they disagree, they return to the text to resolve the difference.

Step 4: Whole-class debrief. Teacher addresses the most common misconceptions and reinforces the strategy by asking: "What text evidence supports that? Where in the passage do you see it?"${instrNote}`;
}

function generateELAIndependentPractice(ctx: Ctx): string {
  const { topic, obj, t, instrNote } = ctx;
  const type = elaTopic(ctx.tl);

  const ipMap: Partial<Record<ELATopic, string>> = {
    'main-idea': `Independent Reading and Analysis (≈${t(0.20)} min)

Students receive a fresh informational text (or the next section of the current text) they have not yet seen.

Task:
1. Read the passage independently.
2. For each paragraph: identify the main idea (MI) and underline one key supporting detail (SD).
3. Write the overall main idea of the entire passage in one complete sentence — in your own words.
4. Choose the three most important supporting details and explain in a sentence each why they matter.
5. Answer: "What would be lost from this passage if paragraph 2 were removed?" (Adjust paragraph number to the text.)

Success criterion: Main idea statement is neither too broad (just the topic) nor too narrow (just one detail).${instrNote}`,
    'opinion-argument': `Independent Writing — Argument Paragraph or Essay (≈${t(0.20)} min)

Students choose from 2–3 teacher-provided prompts connected to today's topic and write independently.

Requirements:
• One strong, specific claim sentence.
• At least two distinct pieces of evidence (facts, examples, or data — not personal feelings).
• Reasoning that connects each piece of evidence to the claim.
• One counterclaim acknowledged and rebutted.
• A concluding sentence that restates the claim in fresh language.

Students self-assess using the class anchor chart before submitting.${instrNote}`,
    'narrative': `Independent Narrative Writing (≈${t(0.20)} min)

Students write the opening of a short story (minimum 1 full page) using the story-arc planning from guided practice.

Requirements:
• Establish the protagonist, setting, and central conflict in the exposition.
• Include at least one example of figurative language.
• Use at least two different sentence types (simple, compound, or complex).
• Show character emotion through action and dialogue — not just "she was scared."
• End at a moment of tension that leaves the reader wanting to continue.

Peer share: students read their opening sentence aloud to their partner and ask: "Does it make you want to keep reading? Why or why not?"${instrNote}`,
    'figurative-language': `Writing with Figurative Language (≈${t(0.20)} min)

Students write a descriptive paragraph (8–10 sentences) on a topic of their choice or a class-assigned topic, intentionally incorporating:
• At least 1 simile
• At least 1 metaphor
• At least 1 example of personification
• At least 1 other device (hyperbole, alliteration, or idiom)

Each device must be underlined and labeled in the margin.

After writing: students trade paragraphs with a partner. The partner reads it and writes: "The most vivid image I got was ____. The figurative language that worked best was ____ because ____."${instrNote}`,
    'vocabulary-context': `Context Clue Practice (≈${t(0.20)} min)

Students complete a 10-sentence practice set. For each sentence:
1. Circle the unfamiliar word.
2. Underline the context clues.
3. Write the type of clue used.
4. Write the word's probable meaning.
5. Verify by substituting their meaning back into the sentence — does it make sense?

Extension: students write their own "mystery sentence" using a challenging word with a synonym clue embedded, then swap with a partner to solve.${instrNote}`,
  };

  return ipMap[type] ??
    `Students apply today's strategy to an independent reading or writing task connected to ${topic}. (≈${t(0.20)} min)

Success criteria are visible; students reference the anchor chart as needed. Teacher circulates and confers individually, collecting formative data. Students who finish early tackle the extension task posted in the room.

Minimum task length: one full written response (paragraph or more) with at least two pieces of text evidence.${instrNote}`;
}

function generateELAExitTicket(ctx: Ctx): string {
  const { topic } = ctx;
  const type = elaTopic(ctx.tl);

  const etMap: Partial<Record<ELATopic, string[]>> = {
    'main-idea': [
      `Read this short paragraph: [teacher inserts a 4–5 sentence paragraph before printing]. In one sentence, state the main idea. Then identify one supporting detail and explain how it supports the main idea.`,
      `What is the difference between a topic and a main idea? Give an example of each from today's reading.`,
    ],
    'inference': [
      `Read this sentence: "After the announcement, the room went completely silent and nobody met the teacher's eyes." Write one inference. Quote the exact words that support it. Explain in one sentence how the evidence leads to the inference.`,
      `What is the biggest mistake a reader can make when making an inference — and how do you avoid it?`,
    ],
    'opinion-argument': [
      `Write a claim statement for this prompt: "Should students be allowed to use cell phones in class?" Make it specific and arguable — not just "I think phones are good/bad."`,
      `Choose one piece of evidence from today's practice and write the reasoning that connects it to the claim. (Use the sentence starter: "This shows that… because…")`,
    ],
    'figurative-language': [
      `Write one original simile and one original metaphor about the same subject. Explain the effect you were trying to create with each.`,
      `Identify the figurative language in this sentence: "The old oak tree groaned under the weight of winter, its branches outstretched like the arms of someone asking for help." Name the type and explain what it means literally vs. figuratively.`,
    ],
    'narrative': [
      `Write the first 2–3 sentences of a new story. Your opening must establish the protagonist, hint at the setting, and create immediate tension or mystery.`,
      `Name the five plot elements and explain in one sentence why the climax is considered the most important.`,
    ],
  };

  const items = etMap[type] ?? [
    `Apply today's key strategy for ${topic} to this prompt: [teacher adds a brief text or question before printing].`,
    `In 2–3 sentences, explain today's most important idea about ${topic} in your own words — as if teaching a student who was absent.`,
    `Rate your confidence with ${topic} (1–5) and write one specific question you still have.`,
  ];
  return items.join('\n');
}

function generateELAHomework(ctx: Ctx): string {
  const type = elaTopic(ctx.tl);
  const { topic } = ctx;

  const hwMap: Partial<Record<ELATopic, string>> = {
    'main-idea': `Read independently for 20–25 minutes. Choose one passage or chapter. Write 2–3 sentences stating the main idea and list three supporting details. Be ready to share tomorrow.`,
    'authors-purpose': `Find one piece of writing at home — a news article, advertisement, recipe, or story — and identify the author's purpose. Write a paragraph explaining which clues revealed the purpose and how the author achieved it.`,
    'inference': `Read one chapter or article from your independent reading book. Write down three inferences you made, quoting the exact text evidence for each and explaining your reasoning.`,
    'narrative': `Write the next scene in the story you started today (minimum one page). Include at least one piece of dialogue, one example of figurative language, and show character reaction through action — not just telling.`,
    'opinion-argument': `Write a full argument paragraph (8–10 sentences) on a topic of your choice: a school rule you'd change, a community problem, or an issue from current events. Include: a strong claim, two pieces of evidence with reasoning, and a concluding sentence.`,
    'figurative-language': `Find three examples of figurative language in a book, song, movie, or advertisement. Write down each example, identify its type, and explain what the author/creator was trying to communicate. Share the best one tomorrow.`,
    'vocabulary-context': `Read your independent book for 20 minutes. Find three words you don't know. For each: write the sentence, underline the context clue, write the probable meaning, and look it up to check accuracy.`,
    'poetry': `Choose a poem from the provided list or find one you love. Copy it out by hand, annotate it (circle figurative language, label devices, note images), and write 3–5 sentences describing how the poem made you feel and why.`,
  };

  return hwMap[type] ??
    `Read independently for 20–25 minutes from your chosen book. In your reading journal, write 2–3 sentences connecting today's lesson on ${topic} to something in your reading — a character's choice, an author's technique, or a theme. Be ready to share your connection tomorrow.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCIENCE GENERATORS
// ═══════════════════════════════════════════════════════════════════════════════

function generateScienceVocabulary(ctx: Ctx): string {
  const type = sciTopic(ctx.tl);
  const { topic } = ctx;

  const vocabSets: Partial<Record<ScienceTopic, string[]>> = {
    'cells': [
      'Cell — The basic structural and functional unit of all living organisms.',
      'Cell membrane — A thin, flexible layer that surrounds all cells, controlling what enters and exits.',
      'Cell wall — A rigid outer layer found in plant cells (not animal cells) that provides structure and support.',
      'Nucleus — The control center of the cell; contains DNA and directs cell activities.',
      'Cytoplasm — The gel-like fluid inside the cell that holds organelles in place.',
      'Mitochondria — Organelles that convert glucose and oxygen into energy (ATP); the "powerhouse" of the cell.',
      'Chloroplast — Organelle in plant cells that uses sunlight, water, and carbon dioxide to make food via photosynthesis.',
      'Vacuole — A storage organelle; large in plant cells (stores water) and small in animal cells.',
    ],
    'ecosystems': [
      'Ecosystem — A community of living organisms interacting with each other and their non-living environment.',
      'Biotic factor — Living components of an ecosystem (plants, animals, fungi, bacteria).',
      'Abiotic factor — Non-living components (sunlight, water, temperature, soil, air).',
      'Population — All individuals of the same species living in the same area at the same time.',
      'Community — All populations of different species living and interacting in the same area.',
      'Niche — The specific role or function an organism plays in its ecosystem.',
      'Carrying capacity — The maximum population size an ecosystem can support given available resources.',
      'Biodiversity — The variety of different species living in an ecosystem.',
    ],
    'adaptations': [
      'Adaptation — An inherited trait that helps an organism survive and reproduce in its environment.',
      'Structural adaptation — A physical feature that helps an organism survive. Examples: thick fur, sharp claws, camouflage coloring.',
      'Behavioral adaptation — A way of acting that helps an organism survive. Examples: migration, hibernation, building nests.',
      'Camouflage — An adaptation where an organism blends into its surroundings to avoid predators.',
      'Mimicry — An adaptation where a harmless organism resembles a dangerous one for protection.',
      'Natural selection — The process by which organisms with favorable adaptations survive and reproduce more successfully.',
      'Environment — The surroundings in which an organism lives, including all living and non-living things.',
    ],
    'forces-motion': [
      'Force — A push or pull that can change an object\'s motion, speed, or direction; measured in Newtons (N).',
      'Gravity — A force that attracts all objects with mass toward each other; pulls objects downward on Earth.',
      'Friction — A force that opposes motion between two surfaces in contact; can slow objects down.',
      'Net force — The overall force on an object when all individual forces are combined.',
      'Balanced forces — Equal forces acting in opposite directions; result in no change in motion.',
      'Unbalanced forces — Forces that are not equal; result in a change in motion (acceleration).',
      'Newton\'s First Law — An object at rest stays at rest, and an object in motion stays in motion unless acted on by an unbalanced force.',
      'Acceleration — A change in speed or direction of motion caused by an unbalanced force.',
    ],
    'matter-states': [
      'Matter — Anything that has mass and takes up space.',
      'Solid — Matter with a definite shape and volume; particles are packed closely and vibrate in place.',
      'Liquid — Matter with a definite volume but no definite shape; particles slide past each other.',
      'Gas — Matter with no definite shape or volume; particles move rapidly and spread out to fill any container.',
      'Physical change — A change in matter\'s form or appearance without changing its chemical composition.',
      'Chemical change — A change that forms a new substance with different properties (e.g., burning, rusting, cooking).',
      'Melting point — The temperature at which a solid changes to a liquid.',
      'Boiling point — The temperature at which a liquid changes to a gas.',
    ],
    'water-cycle': [
      'Water cycle — The continuous movement of water through Earth\'s systems: evaporation → condensation → precipitation → runoff/collection.',
      'Evaporation — The process by which liquid water is heated and changes into water vapor (gas).',
      'Condensation — The process by which water vapor cools and changes back into liquid water, forming clouds and fog.',
      'Precipitation — Water that falls from clouds to Earth\'s surface as rain, snow, sleet, or hail.',
      'Runoff — Water that flows over land into rivers, streams, and eventually oceans.',
      'Transpiration — Water vapor released by plants through their leaves; contributes to evaporation.',
      'Groundwater — Water stored beneath Earth\'s surface in aquifers; replenished by precipitation seeping into soil.',
    ],
    'solar-system': [
      'Solar system — The Sun and all the objects (planets, moons, asteroids, comets) that orbit it.',
      'Orbit — The curved path one object takes around another due to gravity.',
      'Revolution — One complete trip of a planet around the Sun; Earth\'s revolution takes approximately 365.25 days (1 year).',
      'Rotation — The spinning of a planet on its axis; Earth\'s rotation takes approximately 24 hours (1 day).',
      'Gravity — The force that keeps planets in orbit around the Sun and moons in orbit around planets.',
      'Terrestrial planets — Rocky inner planets (Mercury, Venus, Earth, Mars).',
      'Gas giants — Large outer planets composed mainly of gas (Jupiter, Saturn, Uranus, Neptune).',
      'Astronomical Unit (AU) — The average distance from Earth to the Sun (≈150 million km); used to measure distances in the solar system.',
    ],
    'energy': [
      'Energy — The ability to do work or cause change; measured in Joules (J).',
      'Kinetic energy — The energy an object has due to its motion. Example: a rolling ball, flowing water.',
      'Potential energy — Stored energy based on an object\'s position or condition. Example: a stretched spring, water behind a dam.',
      'Gravitational potential energy — Potential energy stored due to an object\'s height above the ground.',
      'Energy transfer — The movement of energy from one object or system to another.',
      'Energy transformation — The conversion of energy from one form to another. Example: chemical energy → thermal energy (burning wood).',
      'Conservation of energy — Energy is never created or destroyed; it only changes form.',
      'Thermal energy — The total kinetic energy of all the particles in a substance; related to temperature.',
    ],
    'plants': [
      'Photosynthesis — The process plants use to convert sunlight, water, and carbon dioxide into glucose and oxygen. Equation: 6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂.',
      'Chlorophyll — The green pigment in plant cells (chloroplasts) that absorbs sunlight for photosynthesis.',
      'Transpiration — The loss of water vapor from plant leaves through tiny pores called stomata.',
      'Stomata — Tiny pores on leaf surfaces that open and close to allow gas exchange (CO₂ in, O₂ out).',
      'Roots — Plant structures that absorb water and minerals from the soil and anchor the plant.',
      'Phototropism — A plant\'s growth response toward light.',
      'Pollination — The transfer of pollen from a stamen to a pistil, enabling seed production.',
    ],
    'scientific-method': [
      'Observation — Using the five senses (or scientific instruments) to gather information about the world.',
      'Hypothesis — A testable, falsifiable prediction about the outcome of an investigation, often written as an if–then statement.',
      'Independent variable — The one factor deliberately changed in an experiment to test its effect.',
      'Dependent variable — The factor measured or observed in an experiment; what changes because of the independent variable.',
      'Controlled variable — All factors kept the same throughout an experiment to ensure a fair test.',
      'Data — Measurements, observations, or information collected during an investigation.',
      'Conclusion — A summary of what the data shows and whether it supports or refutes the hypothesis.',
      'Inference — An explanation or interpretation of data based on observations and prior knowledge.',
    ],
    'food-chains': [
      'Producer — An organism that makes its own food using sunlight (photosynthesis). Examples: plants, algae.',
      'Consumer — An organism that eats other organisms for energy. Examples: herbivores, carnivores, omnivores.',
      'Herbivore — A consumer that eats only plants.',
      'Carnivore — A consumer that eats only animals.',
      'Omnivore — A consumer that eats both plants and animals.',
      'Decomposer — An organism that breaks down dead matter and returns nutrients to the soil. Examples: fungi, bacteria, worms.',
      'Food chain — A model showing the transfer of energy from one organism to the next through feeding relationships.',
      'Food web — A model showing the complex, overlapping feeding relationships in an ecosystem.',
    ],
    'weather-climate': [
      'Weather — The atmospheric conditions at a specific place at a specific time (temperature, precipitation, wind speed).',
      'Climate — The average weather patterns of a region over many years (typically 30+).',
      'Atmosphere — The layers of gases surrounding Earth, held in place by gravity.',
      'Temperature — A measure of how hot or cold the air is; measured with a thermometer in °C or °F.',
      'Humidity — The amount of water vapor in the air; high humidity makes air feel muggy.',
      'Precipitation — Any form of water falling from clouds: rain, snow, sleet, or hail.',
      'Air pressure — The weight of the atmosphere pushing down on Earth\'s surface; measured with a barometer.',
      'Front — The boundary between two air masses of different temperatures and densities; often associated with weather changes.',
    ],
  };

  const terms = vocabSets[type] ?? [
    `${topic} — The central concept being studied; students will construct a definition by the end of this lesson.`,
    'Hypothesis — A testable prediction about the outcome of an investigation.',
    'Observation — Information gathered using the five senses or scientific instruments.',
    'Inference — A conclusion drawn from observations and prior knowledge.',
    'Variable — A factor that can change or be changed in a scientific investigation.',
    'Evidence — Data or observations used to support or refute a scientific explanation.',
  ];
  return terms.join('\n');
}

function generateScienceWarmUp(ctx: Ctx): string {
  const { topic, instrNote } = ctx;
  const type = sciTopic(ctx.tl);

  const warmUps: Partial<Record<ScienceTopic, string>> = {
    'forces-motion': `Show a slow-motion video clip (15–20 seconds) of a skateboarder rolling down a ramp. Ask: "What forces are acting on the skateboarder? What would happen if we removed each one?" Give students 90 seconds to write answers, then pair-share. Surface the words push, pull, gravity, and friction. Say: "Today we're going to make these forces precise — define them, measure them, and test what happens when they're balanced or unbalanced."${instrNote}`,
    'matter-states': `Set out three mystery cups labeled A, B, and C — one contains ice chips, one water, one air (sealed balloon). Ask: "Using only your senses (no tasting), how would you describe what's inside each cup? What do they have in common? What's different?" Students record observations. Bridge: "All three containers hold matter — but matter can exist in three very different states. Today we'll explore why."${instrNote}`,
    'ecosystems': `Project two images side by side: a dense rainforest teeming with animals and a concrete parking lot. Ask: "Which one is an ecosystem? Both? Neither?" Allow debate (90 seconds). Ask: "What's in each? What does each have that the other lacks?" Define ecosystem: "A community of living things and the non-living environment they interact with — and yes, even a city is an ecosystem." Bridge today's investigation.${instrNote}`,
    'food-chains': `Project a simple picture: a caterpillar on a leaf, a bird nearby, and a hawk in the sky. Ask: "Describe what might happen in the next few minutes. Who eats whom? Where does the caterpillar's energy come from originally?" Students sketch a food chain from sun to hawk. Debrief: "Energy doesn't get created — it gets transferred. Today we'll follow that energy through ecosystems."${instrNote}`,
    'plants': `Show two identical seedlings — one placed in full sunlight, one kept in a dark closet for one week. Ask: "What differences do you observe? What caused them? What does the plant in the dark lack?" Students discuss in pairs. Pull out: light, water, and carbon dioxide as ingredients. Say: "Today we're investigating the process plants use to turn these ingredients into food — and why that matters for nearly every living thing on Earth."${instrNote}`,
    'cells': `Ask: "What is the smallest living thing?" Take predictions. Show a microscope image of an onion cell next to a cheek cell. Ask: "What do these have in common? What's different? If these are the 'building blocks' of living things, what kind of work must they do?" Students quick-write (60 seconds). Bridge: "Today we'll learn the parts of plant and animal cells and what each part does — like learning the jobs in a well-run factory."${instrNote}`,
    'water-cycle': `Pour a small amount of water into a zip-lock bag, seal it, and tape it to a sunny window. Ask students to make a prediction: "What will happen to the water by the end of class? Where will it go?" Students sketch their prediction. Bridge: "We're going to watch part of the water cycle happen right in front of us — and learn how this same process shapes our weather and freshwater supply."${instrNote}`,
    'solar-system': `Turn off the lights and project a 1-minute time-lapse video of the night sky showing star movement. Ask: "Why do the stars appear to move? Are they actually moving — or is something else happening?" After discussion, ask: "How do we know how far away planets are? Or that Earth orbits the Sun rather than the other way around?" Bridge: "Today we'll build a scale model of the solar system and explore why size, distance, and gravity shape everything."${instrNote}`,
    'scientific-method': `Show a surprising phenomenon: "I put a fresh egg in plain water — it sinks. I put the same egg in a cup of salt water — it floats. Why?" Take predictions from the class. Ask: "How could we prove which explanation is correct?" Surface the idea of testing one variable at a time. Bridge: "Scientists follow a systematic process so their tests are fair and their conclusions are trustworthy. Today we'll learn and practice that process."${instrNote}`,
  };

  return warmUps[type] ??
    `Conduct a short teacher demonstration or show a striking image/video connected to ${topic}. Ask students to record at least three observations and one question in their science journals. Share out. Record key ideas on the board and use them to bridge to today's investigation: "Today we're going to find out why/how ____."${instrNote}`;
}

function generateScienceDirectInstruction(ctx: Ctx): string {
  const { topic, instrNote } = ctx;
  const type = sciTopic(ctx.tl);

  const steps: Partial<Record<ScienceTopic, string[]>> = {
    'cells': [
      `Introduce the cell theory: (1) All living things are made of cells. (2) The cell is the basic unit of life. (3) All cells come from existing cells. Ask: "Why is this called a theory — and why do scientists treat it as fact?" Distinguish scientific theory from everyday usage.`,
      `Display labeled diagrams of a plant cell and an animal cell side by side. Introduce each organelle using the "factory analogy": nucleus = CEO's office, mitochondria = power plant, cell membrane = security gate, ribosomes = assembly line, etc. Students label their own diagrams as you introduce each part.`,
      `Compare plant and animal cells: "What does a plant cell have that an animal cell doesn't?" (Cell wall, chloroplasts, large central vacuole.) Ask: "Why might a plant need a rigid wall but an animal does not?"`,
      `Brief video clip (2–3 min) of a real cell under an electron microscope. Students record two new observations in their science journals.${instrNote}`,
    ],
    'forces-motion': [
      `Define force: "A push or pull that can change an object's motion, speed, or direction." Introduce the unit Newton (N). Demonstrate with a spring scale: pull a textbook across the desk and read the force.`,
      `Introduce Newton's First Law (Inertia): "An object at rest stays at rest, and an object in motion stays in motion with the same speed and direction unless acted on by an unbalanced force." Demonstrate with a tablecloth pull: dishes stay (inertia). Discuss: seatbelts in cars — why do we need them?`,
      `Explain balanced vs. unbalanced forces with a tug-of-war model. When forces are equal and opposite → balanced → no motion change. When one side wins → unbalanced → acceleration. Use a labeled force diagram (free-body diagram) for a book resting on a table.`,
      `Introduce friction and gravity specifically: Define each with real-world examples and discuss how engineers account for them in design (tires, parachutes, spacecraft re-entry).${instrNote}`,
    ],
    'ecosystems': [
      `Build the ecosystem concept from the ground up: individual → population → community → ecosystem → biosphere. Use a local example (schoolyard, local park, nearby river) at each level.`,
      `Distinguish biotic and abiotic factors using a two-column anchor chart. Students contribute examples from the warm-up images. Discuss: "If we removed all abiotic factors, what happens to biotic ones? Why?"`,
      `Introduce carrying capacity with a graph showing population growth, overshoot, and crash in a deer population. Ask: "What factors limited the population? What would happen if we removed predators?"`,
      `Introduce biodiversity as an ecosystem "health indicator." Show a high-biodiversity vs. low-biodiversity sample. Ask: "Which ecosystem is more resilient to disease or environmental change — and why?"${instrNote}`,
    ],
    'scientific-method': [
      `Walk through the 6 steps: Observe → Question → Hypothesis → Experiment → Analyze Data → Conclusion. Emphasize it's a cycle, not a checklist — scientists repeat steps constantly.`,
      `Model writing a hypothesis using the if–then format: "If we increase the amount of salt in water, then the boiling point will increase, because dissolved substances raise the boiling point of a liquid." Identify the independent variable, dependent variable, and the scientific reasoning.`,
      `Explain controlled variables: "If we're testing salt concentration, every other factor — amount of water, pot size, heat source, starting temperature — must stay the same. Why?" Run a class discussion to surface why uncontrolled variables make results meaningless.`,
      `Preview the class investigation: students will design a fair test for [topic-specific question]. Show the materials and ask: "What should you control? What will you measure? How will you record data?" Students plan in their science journals before touching materials.${instrNote}`,
    ],
    'water-cycle': [
      `Display a large labeled water cycle diagram. Introduce the four main processes: evaporation, condensation, precipitation, and collection/runoff. Ask students to connect each process to a weather event or observation they've experienced.`,
      `Explain evaporation: "Water molecules near the surface absorb enough energy (from the sun) to escape as water vapor." Demonstrate: blow a small amount of water on your hand vs. leave some still. Which evaporates faster — why? (Surface area and air movement.)`,
      `Explain condensation: "As warm, humid air rises, it cools. Cool air holds less moisture — so the water vapor condenses into tiny droplets, forming clouds." Show the sealed water bag from the warm-up if visible changes have occurred.`,
      `Discuss precipitation and freshwater storage (glaciers, groundwater, rivers). Ask: "Where does the water you drink come from originally? Trace it back through the cycle."${instrNote}`,
    ],
    'food-chains': [
      `Introduce energy flow terminology: producer → primary consumer → secondary consumer → tertiary consumer → decomposer. Build the class food chain on the board with real species from a temperate forest: oak tree → caterpillar → robin → red-tailed hawk → bacteria. Label each role as you add it. Turn & Talk: "Where does the caterpillar's energy originally come from? Trace it all the way back." Cold-call two pairs to share — confirm every answer leads back to the sun.`,
      `Introduce the 10% rule: "At each step in a food chain, only about 10% of the energy is passed on — the rest is lost as heat or used for the organism's own life processes." Show the pyramid for today's chain: 10,000 J (oak tree) → 1,000 J (caterpillar) → 100 J (robin) → 10 J (hawk). Before revealing each number, ask students to predict: "If the caterpillar gets 1,000 J, how much does the robin get? Write your prediction — then check it." This makes the 10% rule a discovery rather than a fact to copy.`,
      `Extend to food webs: show how a mouse also eats oak tree seeds, and a fox eats both mice and robins — add these to the board web. Ask: "What happens if the caterpillar population crashes due to a disease? Trace the effect: which species are hurt? Which might actually benefit — and why?" Students Think-Pair-Share before the class debrief.`,
      `Introduce decomposers: "What happens to the hawk when it dies? And the caterpillar? Bacteria break down dead matter and return nutrients to the soil — which the oak tree uses to grow more leaves. Point to the oak tree: what just came full circle?" Students draw the complete cycle (including the decomposer loop) in their science journals.${instrNote}`,
    ],
  };

  const chosen = steps[type];
  if (chosen) return chosen.map((s, i) => `${i + 1}. ${s}`).join('\n');

  return [
    `1. Introduce ${topic} with a striking real-world phenomenon, demonstration, or image to anchor the new concept. Students record initial observations in science journals.`,
    `2. Present key scientific vocabulary with diagrams, physical models, or real specimens where available. Connect terms to student observations.`,
    `3. Explain the investigation students will conduct: the question, the procedure, safety notes, and what to record as data.`,
    `4. Connect today's hands-on work to the broader scientific concept: "We're doing this because it helps us understand ____."${instrNote}`,
  ].join('\n');
}

function generateScienceGuidedPractice(ctx: Ctx): string {
  const { topic, t, instrNote } = ctx;
  const type = sciTopic(ctx.tl);

  const gpMap: Partial<Record<ScienceTopic, string>> = {
    'cells': `Microscope Lab — Observing Real Cells (≈${t(0.20)} min)

⚠️ Safety: Handle microscope slides carefully. Wash hands after handling biological samples.

Part 1: Teacher prepares pre-made wet mount slides of onion skin cells and cheek cells (or uses prepared slides). Students observe under low then high power.

Data Table:
| Observation | Onion Cell | Cheek Cell |
|---|---|---|
| Shape | | |
| Cell wall visible? (Y/N) | | |
| Nucleus visible? (Y/N) | | |
| Chloroplasts visible? (Y/N) | | |
| Size (estimate) | | |

Part 2: Students sketch each cell type, label at least 3 structures, and write one comparison sentence.

Checkpoint: "Point to the cell membrane on your diagram. How does it differ from the cell wall?"${instrNote}`,
    'forces-motion': `Forces Lab — Measuring and Comparing (≈${t(0.20)} min)

⚠️ Safety: Move carefully when pulling objects across surfaces to avoid slipping.

Materials: spring scale, textbook, ruler, three different surface types (smooth desk, carpet swatch, sandpaper)

Procedure:
1. Attach spring scale to textbook. Pull steadily across Surface 1. Record force in Newtons.
2. Repeat on Surface 2 and Surface 3.
3. Record all data in the table below.

Data Table:
| Surface | Force to Move Book (N) | Ranking (1 = easiest) |
|---|---|---|
| Smooth desk | | |
| Carpet | | |
| Sandpaper | | |

Analysis questions (discuss with partner, write individually):
• Which surface has the greatest friction? How do you know?
• Predict: if we added a second book, what would happen to the force needed?
• Real-world application: where is HIGH friction useful? Where is LOW friction useful?${instrNote}`,
    'water-cycle': `Terrarium Water Cycle Model (≈${t(0.20)} min)

⚠️ Safety: Handle plastic bags carefully; avoid putting bags near faces.

Materials: large zip-lock bag, a small amount of water, blue food coloring, tape, sunny window (or lamp)

Procedure:
1. Add ¼ cup water and 2 drops blue food coloring to the bag. Seal tightly. Label: Date, Time, Name.
2. Tape the bag to the window (or place under the lamp).
3. Observe immediately and record. Observe again after 15 minutes.

Data Table:
| Time | Observations (describe droplets, fog, water location) |
|---|---|
| 0 minutes | |
| 15 minutes | |

Discussion: "What process occurred on the inside of the bag? What would happen if we put the bag in a cold room? What does the bag represent in nature?"${instrNote}`,
    'food-chains': `Food Web Simulation (≈${t(0.20)} min)

Students receive role cards naming the organisms from today's class food chain — Oak Tree (Producer), Caterpillar (Primary Consumer), Mouse (Primary Consumer), Robin (Secondary Consumer), Fox (Secondary Consumer), Red-Tailed Hawk (Tertiary Consumer), Bacteria (Decomposer), and Sun — plus a ball of yarn representing energy flow.

Round 1 — Linear chain: "Sun" holds the yarn and passes one end to Oak Tree, who passes to Caterpillar, who passes to Robin, who passes to Red-Tailed Hawk, who passes to Bacteria — and Bacteria return the last length to the center of the circle (the "soil"). Ask: "Where did the energy start? Where does it end up? Why does it return to the soil?"

Round 2 — Food web: Add Mouse and Fox connections. Mouse takes a new piece of yarn from Oak Tree; Fox takes connections from Mouse and Robin. Each student holds all their yarn strands. Teacher calls: "A disease wipes out all the Caterpillars!" — Caterpillar drops every strand. Ask students: "Feel your yarn. Did it go slack? Tighten? What does that tell you about your organism?" Mouse and Robin are directly affected; Fox is indirectly affected; Oak Tree may benefit. Students explain the cascade before recording.

Round 3 — Disruption of your choice: teacher calls "Red-Tailed Hawk population crashes due to pesticides." Students predict the cascade before dropping strands — then confirm by feeling what tightens and what goes slack.

Analysis (written in science journal): "Which organisms would be most hurt if oak trees disappeared? Which might temporarily benefit — and why? What would eventually happen to that organism too?"${instrNote}`,
    'scientific-method': `Design a Fair Test (≈${t(0.20)} min)

Groups of 3–4 receive this problem: "Does the height of a ramp affect how far a toy car travels?"

Groups must write:
1. A testable hypothesis (if–then format).
2. A list of at least 4 controlled variables.
3. The independent variable (what they'll change) and the dependent variable (what they'll measure).
4. A step-by-step procedure (minimum 5 steps) for a fair test.
5. A blank data table with labeled columns.

Groups share their plans with another group for peer feedback: "Is this a fair test? Is anything uncontrolled?"

Teacher circulates and asks: "How will you make sure your results are reliable — and what would you do to improve accuracy?" [If time allows, groups may run trial 1 of their investigation.]${instrNote}`,
  };

  return gpMap[type] ??
    `Guided Investigation — ${topic} (≈${t(0.20)} min)

⚠️ Review relevant safety procedures before students begin.

Groups of 3–4 conduct a structured, teacher-guided hands-on investigation. Teacher circulates to:
• Ask guiding questions: "What do you observe? What does this tell you? What would happen if we changed ____?"
• Ensure all students record data in science journals.
• Pause at one mid-lab checkpoint to address common misconceptions before students continue.

Groups record data in a structured table (columns: Trial #, Variable, Observation/Measurement, Notes).

Debrief: each group shares one surprising observation. Teacher connects it to the concept introduced in direct instruction.${instrNote}`;
}

function generateScienceIndependentPractice(ctx: Ctx): string {
  const { topic, obj, t, instrNote } = ctx;
  const type = sciTopic(ctx.tl);

  const ipMap: Partial<Record<ScienceTopic, string>> = {
    'cells': `Independent Lab Write-Up and Diagram (≈${t(0.20)} min)

Students complete the following in their science journals:

1. Draw and label a plant cell and an animal cell from memory (minimum 5 structures each).
2. Complete a comparison table: list 3 similarities and 3 differences between plant and animal cells.
3. Answer: "A student looks at a cell under a microscope and sees a large, green organelle. What type of cell is this most likely? How do you know?"
4. Explain in 2–3 sentences why the mitochondria is called the "powerhouse" of the cell. Include what it takes in and what it produces.
5. Challenge: "If a cell's mitochondria stopped working, what would happen — and why?"${instrNote}`,
    'food-chains': `Independent Analysis — Today's Forest Food Web (≈${t(0.20)} min)

Students receive a printed diagram of the food web built during class: Oak Tree → Caterpillar → Robin → Red-Tailed Hawk (with Mouse and Fox branching off) → Bacteria. This is the same web from Direct Instruction and the yarn simulation — now students analyze it independently.

Tasks:
1. Label every organism in the diagram with its feeding role: Producer, Primary Consumer, Secondary Consumer, Tertiary Consumer, or Decomposer.
2. Trace 3 different complete food chains from Oak Tree to a top predator. Write each chain using arrows (→).
3. Energy math: "The oak tree in our forest stores 10,000 J of energy. Using the 10% rule, calculate how much energy reaches the Robin. Show each step." (Expected: 10,000 → 1,000 J at caterpillar → 100 J at robin.)
4. Cascade prediction: "A disease wipes out 80% of the Robin population. Predict what happens to: (a) the Caterpillar population, (b) the Oak Tree, and (c) the Fox population. Explain the chain of reasoning for each."
5. Challenge: "We added Mouse and Fox to our food web today. How did adding those two organisms make the forest ecosystem more stable — not less? Use the 10% rule and cascade prediction ideas in your answer."${instrNote}`,
    'scientific-method': `Independent Investigation Design (≈${t(0.20)} min)

Students design their own investigation on this question: "Which brand of paper towel absorbs the most water?"

Written plan must include:
1. Background: what do you already know that's relevant?
2. Hypothesis (if–then format with scientific reasoning).
3. Materials list.
4. Step-by-step procedure (minimum 6 steps).
5. Identified variables: independent, dependent, and at least 3 controlled.
6. Blank data table.
7. Explanation of how you'll ensure your test is fair and results are reliable.

After writing: students exchange plans with a partner who evaluates: ✓ Is the hypothesis testable? ✓ Are variables correctly identified? ✓ Is the procedure detailed enough to replicate?${instrNote}`,
  };

  return ipMap[type] ??
    `Students complete an independent analysis task connected to today's investigation of ${topic}. (≈${t(0.20)} min)

Tasks:
1. Complete data recording in science journals (if investigation data is still being collected).
2. Respond to 3–5 analysis questions that require using today's data and vocabulary.
3. Write a 2–3 sentence "I used to think ____; now I know ____ because ____" reflection.
4. Challenge: design one follow-up question they'd want to investigate next and explain how they'd test it.

Teacher circulates and uses 1-on-1 conferences to gather formative data on ${obj}.${instrNote}`;
}

function generateScienceExitTicket(ctx: Ctx): string {
  const { topic } = ctx;
  const type = sciTopic(ctx.tl);

  const etMap: Partial<Record<ScienceTopic, string[]>> = {
    'cells': [
      `Name two organelles found in plant cells but NOT in animal cells. For each, explain its function.`,
      `A student says, "Cells are just bags of liquid." Write 2–3 sentences using today's vocabulary to explain why this is incorrect.`,
    ],
    'forces-motion': [
      `A book is sitting still on a desk. Two forces are acting on it: gravity pulls it down with 20 N, and the desk pushes up with 20 N. Are the forces balanced or unbalanced? What does this mean for the book's motion?`,
      `Give one real-world example each of: (a) friction being helpful, (b) friction being a problem engineers try to reduce.`,
    ],
    'food-chains': [
      `Using today's forest food web (Oak Tree → Caterpillar → Robin → Red-Tailed Hawk, with Mouse and Fox), apply the 10% rule: if the oak tree stores 50,000 J, calculate how much energy reaches the Red-Tailed Hawk. Show each step of your math.`,
      `Predict and explain: "A logger clears 60% of the oak trees in this forest. Trace the cascade — what happens to the Caterpillar, Robin, Fox, and Hawk populations over the next two years? Which organism is hit first? Which is hit last — and why does the effect take longer to reach the top?" Write 3–4 sentences using at least two vocabulary words from today.`,
    ],
    'scientific-method': [
      `Write a hypothesis for this question: "Does the color of light affect plant growth?" Use the if–then format. Identify the independent and dependent variables.`,
      `What makes a test "fair"? List three controlled variables you would use in a plant growth experiment.`,
    ],
    'water-cycle': [
      `Describe what you observed in the bag experiment using the words evaporation and condensation correctly in each sentence.`,
      `Explain the water cycle in 3–4 sentences, including what energy source drives evaporation and what causes condensation.`,
    ],
  };

  const items = etMap[type] ?? [
    `Explain one key concept from today's lesson on ${topic} in 2–3 sentences. Use at least two vocabulary words from today.`,
    `Describe something you observed today that you cannot yet fully explain. Write a question you'd want to investigate next.`,
    `Complete: "I used to think ____; now I know ____ because ____."`,
  ];
  return items.join('\n');
}

function generateScienceHomework(ctx: Ctx): string {
  const { topic } = ctx;
  const type = sciTopic(ctx.tl);

  const hwMap: Partial<Record<ScienceTopic, string>> = {
    'cells': `Draw and label a cell from memory (plant OR animal — your choice). Include at least 6 organelles with labels and a one-sentence function description for each. At the bottom, write 2–3 sentences explaining why cells are called the "basic unit of life."`,
    'forces-motion': `Observe one example of friction and one example of gravity in your daily life. In your science journal, write: (a) what you observed, (b) what forces were acting, (c) whether the forces were balanced or unbalanced, and (d) what evidence you used to decide. Draw a simple force diagram for each.`,
    'food-chains': `Tonight, extend today's forest food web. We studied: Oak Tree → Caterpillar → Robin → Red-Tailed Hawk (with Mouse and Fox). Research and add two more real organisms that belong in a temperate forest ecosystem — one that feeds ON something already in the web, and one that IS fed ON by something already in the web. Draw the updated web, label every organism's feeding role, and write a paragraph answering: "How did your two additions change the stability of the ecosystem? Would a disease that wiped out the Robin now cause more damage or less damage to the web — explain using the 10% rule."`,
    'scientific-method': `Design a simple experiment at home testing one variable (e.g., does sugar dissolve faster in hot or cold water?). Write a hypothesis, list materials, describe your procedure (5 steps), and conduct the test. Record results and write one sentence conclusion.`,
    'water-cycle': `Science journal reflection: trace the water molecule in your last glass of water through the complete water cycle before it reached you. Include all four stages (evaporation, condensation, precipitation, collection) and estimate how many years the cycle may have taken.`,
    'plants': `Find a plant at home, outside, or in a photo. Identify and photograph or sketch: the leaves (where photosynthesis occurs), the stem (transport), and the roots (absorption). Write 2–3 sentences explaining the role of each structure in the plant's survival.`,
  };

  return hwMap[type] ??
    `Science journal reflection on ${topic}: "What did I observe today? What does it mean? What new questions do I have?" (Minimum 4 complete sentences.) Also: identify one real-world example of today's concept that you can find at home, on the news, or in nature. Bring your written reflection to the next class.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SOCIAL STUDIES GENERATORS
// ═══════════════════════════════════════════════════════════════════════════════

function generateSSVocabulary(ctx: Ctx): string {
  const type = ssTopic(ctx.tl);
  const { topic } = ctx;

  const vocabSets: Partial<Record<SSTopic, string[]>> = {
    'american-revolution': [
      'Colony — A territory settled and controlled by a distant country or government.',
      'Taxation without representation — The colonists\' protest that the British Parliament taxed them without giving them any voice (seats) in Parliament.',
      'Continental Congress — A governing body formed by colonial delegates beginning in 1774 to coordinate resistance to British rule.',
      'Declaration of Independence — The document, adopted July 4, 1776, declaring that the 13 colonies were free and independent states.',
      'Loyalist — A colonist who remained loyal to the British Crown during the American Revolution.',
      'Patriot — A colonist who supported independence from Britain.',
      'Unalienable rights — Rights that cannot be taken away or given up; the Declaration lists life, liberty, and the pursuit of happiness.',
    ],
    'civil-war': [
      'Sectionalism — An excessive loyalty to the interests of a region or section rather than the whole country.',
      'Abolition — The movement to end slavery in the United States.',
      'Confederacy — The Confederate States of America; the 11 Southern states that seceded from the Union.',
      'Union — The Northern states that remained in the United States and fought to preserve the nation.',
      'Emancipation — The act of setting free; the Emancipation Proclamation (1863) declared enslaved people in rebel states free.',
      'Secession — The withdrawal of states from the Union; 11 states seceded beginning in 1860–61.',
      'Reconstruction — The period after the Civil War (1865–1877) when the government worked to rebuild the South and integrate formerly enslaved people as citizens.',
    ],
    'us-government': [
      'Constitution — The supreme law of the United States; establishes the framework of government.',
      'Separation of powers — The division of government authority into three branches to prevent any one branch from becoming too powerful.',
      'Legislative branch — The branch of government that makes laws; composed of Congress (Senate + House of Representatives).',
      'Executive branch — The branch that enforces laws; led by the President.',
      'Judicial branch — The branch that interprets laws and determines if they are constitutional; led by the Supreme Court.',
      'Checks and balances — A system in which each branch of government has the power to limit or review the actions of the other two branches.',
      'Amendment — A formal change or addition to the Constitution; 27 amendments have been ratified.',
      'Bill of Rights — The first 10 amendments to the Constitution, protecting fundamental rights of citizens.',
    ],
    'civil-rights': [
      'Civil rights — The rights of all citizens to political and social equality under the law.',
      'Segregation — The enforced separation of people based on race, especially in schools, public facilities, and neighborhoods.',
      'Jim Crow laws — State and local laws that enforced racial segregation in the South from the 1870s until 1965.',
      'Boycott — A refusal to buy, use, or participate in something as a form of protest.',
      'Nonviolent protest — A method of civil disobedience (marches, sit-ins, boycotts) that does not use physical force.',
      'Civil Rights Act of 1964 — Federal legislation that outlawed discrimination based on race, color, religion, sex, or national origin.',
      'Voting Rights Act of 1965 — Legislation that prohibited discriminatory voting practices that had been used to disenfranchise Black Americans.',
    ],
    'ancient-civilizations': [
      'Civilization — A complex society with cities, a government, writing, organized religion, and economic activity.',
      'City-state — An independent self-governing city and its surrounding territory; common in ancient Greece and Mesopotamia.',
      'Democracy — A system of government in which citizens have a say; originated in ancient Athens.',
      'Hierarchy — A system in which people or groups are ranked one above the other according to status or authority.',
      'Artifact — An object made or used by people in the past that reveals information about their culture.',
      'Primary source — A document, object, or other evidence created at the time being studied.',
      'Monotheism — The belief in one god (e.g., ancient Israelites). Contrasted with polytheism — belief in many gods (e.g., ancient Egypt, Greece, Rome).',
    ],
    'geography': [
      'Absolute location — An exact position on Earth\'s surface described by latitude and longitude coordinates.',
      'Relative location — A position described in relation to other places. Example: "north of the river" or "west of the mountains."',
      'Latitude — Imaginary lines running parallel to the equator measuring distance north or south; measured in degrees.',
      'Longitude — Imaginary lines running from pole to pole measuring distance east or west of the Prime Meridian.',
      'Landform — A natural feature of Earth\'s surface. Examples: mountain, plateau, valley, delta, peninsula.',
      'Region — An area that shares one or more common characteristics (physical, cultural, economic, or political).',
      'Climate — The average weather conditions of a region over a long period of time.',
    ],
    'economics': [
      'Scarcity — The condition that exists when resources are limited and cannot satisfy all human wants; the fundamental economic problem.',
      'Supply — The amount of a good or service producers are willing and able to make available at various prices.',
      'Demand — The amount of a good or service consumers are willing and able to buy at various prices.',
      'Opportunity cost — The value of the next-best choice given up when making a decision.',
      'Trade-off — The exchange of one thing for another; every choice involves a trade-off.',
      'Market economy — An economic system in which decisions about production and prices are made by buyers and sellers.',
      'Gross Domestic Product (GDP) — The total value of all goods and services produced within a country in one year; measures economic output.',
    ],
    'immigration': [
      'Immigration — The process of coming to live permanently in a foreign country.',
      'Push factor — A condition in a person\'s home country that drives them to leave (e.g., war, poverty, famine, persecution).',
      'Pull factor — A condition in a new country that attracts immigrants (e.g., economic opportunity, safety, family).',
      'Assimilation — The process by which immigrants adopt the language, customs, and culture of their new country.',
      'Diversity — The variety of different people, cultures, and ideas within a community or society.',
      'Citizenship — Legal membership in a nation that carries rights and responsibilities.',
      'Cultural diffusion — The spread of cultural beliefs, practices, and technologies from one group to another.',
    ],
  };

  const terms = vocabSets[type] ?? [
    `${topic} — The central historical or geographic concept studied today; students will be able to define and analyze it by the end of class.`,
    'Primary source — A document, artifact, or account created at the time of the events being studied.',
    'Secondary source — An account created after the events, interpreting or analyzing primary sources.',
    'Perspective — A point of view shaped by a person\'s identity, experiences, and beliefs.',
    'Cause and effect — A relationship in which one event (cause) produces or contributes to another (effect).',
    'Chronology — The arrangement of events in the order in which they occurred.',
  ];
  return terms.join('\n');
}

function generateSSWarmUp(ctx: Ctx): string {
  const { topic, instrNote } = ctx;
  const type = ssTopic(ctx.tl);

  const warmUps: Partial<Record<SSTopic, string>> = {
    'american-revolution': `Display this quote (without attribution): "The cause of America is in a great measure the cause of all mankind." Ask: "Who do you think said this? Why? What situation might prompt someone to write these words?" Partners discuss (90 seconds). Reveal it was Thomas Paine, Common Sense, 1776. Ask: "Why was convincing regular colonists important — what might have happened without their support?" Bridge to today's lesson on the causes and significance of the American Revolution.${instrNote}`,
    'civil-war': `Show students two editorial cartoons from 1860 — one from a Northern newspaper, one from a Southern newspaper — depicting the same event (e.g., Lincoln's election). Ask: "What differences do you notice? Why might two newspapers show the same event so differently?" Introduce the concept of perspective and sectionalism. Bridge: "Today we'll examine the deep divisions that made the Civil War almost inevitable."${instrNote}`,
    'civil-rights': `Play 45 seconds of audio from Dr. King's "I Have a Dream" speech without context. Ask: "What is the speaker calling for? Who might be in the audience? When might this have been delivered?" Take predictions. Reveal the source. Ask: "What conditions in American society made a speech like this necessary?" Bridge to today's exploration of the Civil Rights Movement.${instrNote}`,
    'us-government': `Ask: "Imagine you have total power to run the school — no principal, no school board, no rules. What three rules would you make first? What could go wrong if one person had all the power?" Discuss briefly. Bridge: "The founders of the United States asked this exact question in 1787. Their answer was the Constitution — a system designed to prevent any one person or group from having too much power."${instrNote}`,
    'ancient-civilizations': `Display a photograph of the Pyramids of Giza, the Parthenon, or the Colosseum. Ask: "How did people build this thousands of years ago — without modern machinery? What does a society need to exist in order to build something like this?" Students quick-write (60 seconds). Share out. Surface: organized labor, government, surplus food, writing, specialization. Bridge: "These are the hallmarks of civilization — and today we'll explore how the ancient ____s built one."${instrNote}`,
    'geography': `Show a world map. Ask: "A friend says, 'I live near a big river, in a flat area that gets very little rain, and the nearest large city is 300 miles north.' Can you find where they might live? What kind of information would narrow it down?" Students work with partners to identify 2–3 possible regions. Bridge: "Today we'll learn the tools geographers use to describe and analyze location precisely."${instrNote}`,
    'economics': `Pose this scenario: "Your school has $1,000 left in the budget. It can buy new library books, new sports equipment, or new art supplies — but only one. How would you decide?" Students vote and defend their choice (2-minute class discussion). Ask: "Does not choosing the other options mean you lose something? What? That's called opportunity cost — and it's at the heart of all economic decisions."${instrNote}`,
    'immigration': `Show students an image of a historical immigrant arriving at Ellis Island next to a contemporary image of a refugee family. Ask: "What do these two people have in common? What might have driven each to leave their home country? What might they hope to find?" Partners discuss (90 seconds). Share out. Ask: "Is immigration a choice, a necessity, or both — and does it matter?" Bridge to today's investigation of immigration patterns, push-pull factors, and impact.${instrNote}`,
  };

  return warmUps[type] ??
    `Display a primary source image, map, or brief document excerpt related to ${topic}. Give students 2 minutes to record three observations and one question. Partners share (60 seconds). Cold-call 3–4 responses and use them to establish what students already know and what they need to learn. Bridge: "Today's lesson will answer the question: [teacher's essential question for the unit]."${instrNote}`;
}

function generateSSDirectInstruction(ctx: Ctx): string {
  const { topic, instrNote } = ctx;
  const type = ssTopic(ctx.tl);

  const steps: Partial<Record<SSTopic, string[]>> = {
    'american-revolution': [
      `Establish timeline context: place the American Revolution (1765–1783) on a class timeline. Show the 13 colonies on a map. Ask: "Who controlled this land? How long had colonists been here?" Establish that most colonists thought of themselves as British subjects with British rights.`,
      `Present the major causes using the acronym STAMP: Salutary Neglect ending, Taxation without representation, Acts of Parliament (Stamp, Townshend, Tea Acts), Massacre in Boston, Political Ideas (Enlightenment — Locke, natural rights). Discuss each with a specific example.`,
      `Read a short excerpt from the Declaration of Independence (the preamble) together. Annotate: "What rights does Jefferson claim are unalienable? What does he say governments are for? What happens when a government fails in its purpose?"`,
      `Briefly discuss the war's outcome and significance: why a colonial victory against the world's most powerful military was unexpected — and why the result mattered globally.${instrNote}`,
    ],
    'us-government': [
      `Explain the Three Branches using a simple T-chart: Name | Who's in it | Main job | Example of power. Fill it in together. Emphasize: "The framers deliberately split power because they feared what one powerful leader could do — they remembered living under a king."`,
      `Model the checks and balances system with a triangle diagram. Walk through: Congress passes a law → President signs or vetoes → Congress can override a veto → Supreme Court can declare a law unconstitutional. Use a real historical example for each step.`,
      `Introduce the Bill of Rights: "Why wasn't the original Constitution enough? What were people afraid of?" Read the First Amendment together and ask: "What 5 freedoms does it protect? Why might freedom of speech be first?"`,
      `Connect to today: "Which branch handled [a current events example]? Was it constitutional? How would we find out?" Model civic reasoning using the vocabulary learned.${instrNote}`,
    ],
    'civil-rights': [
      `Establish historical context: use a timeline from Reconstruction → Jim Crow laws → WWI and WWII → post-war frustration → Montgomery Bus Boycott 1955. Ask: "Why did the Civil Rights Movement peak in the 1950s–60s rather than immediately after the Civil War?"`,
      `Introduce the strategy of nonviolent direct action. Show photographs and a short video clip (2 min) of the sit-ins at the Greensboro Woolworth's counter (1960). Ask: "What risks were these protesters taking? What were they trying to accomplish by not fighting back?"`,
      `Read a short excerpt from Dr. King's "Letter from Birmingham Jail" or a related primary source. Annotate for: his argument, his intended audience, his tone, and his use of evidence.`,
      `Map legislative victories: Civil Rights Act 1964, Voting Rights Act 1965, Fair Housing Act 1968. Ask: "Why did it take legislation — wasn't changing attitudes enough?" Connect to the broader question: how does social change happen?${instrNote}`,
    ],
    'geography': [
      `Introduce the five themes of geography: Location, Place, Human-Environment Interaction, Movement, and Region. Give one example of each using a location students know (their school, city, or state).`,
      `Teach absolute location: latitude (parallels) and longitude (meridians). Demonstrate how to find a location using coordinates on a classroom map. Practice: "Put your finger on 40°N, 74°W — what city is that?" (New York City)`,
      `Introduce relative location and how descriptions change based on the observer's reference point. Practice: "Describe this city's location relative to [another city] in two ways — one using cardinal directions, one using a physical feature."`,
      `Model reading a thematic map (climate, population density, or physical features depending on the unit). Ask: "What patterns do you notice? What might explain the pattern?"${instrNote}`,
    ],
    'economics': [
      `Establish the central economic problem: scarcity. "Resources are limited — time, money, materials. Wants are unlimited. This forces every person, business, and government to make choices."`,
      `Introduce supply and demand using a simple, relatable example: "Before the school year, backpack prices are high (high demand, limited supply). In February, prices drop (lower demand). What happens if a manufacturer produces too many?"`,
      `Draw a simple supply and demand graph. Label the x-axis (quantity) and y-axis (price). Draw supply and demand curves and show the equilibrium point. Ask: "What happens to price if demand suddenly increases? If supply drops?"`,
      `Introduce opportunity cost with a student-centered example: "If you spend your Friday evening playing video games, what is the opportunity cost? If a country spends its budget on military, what is the opportunity cost?" Connect to policy decisions.${instrNote}`,
    ],
  };

  const chosen = steps[type];
  if (chosen) return chosen.map((s, i) => `${i + 1}. ${s}`).join('\n');

  return [
    `1. Provide essential background on ${topic} using a short lecture, annotated map, timeline, or primary/secondary source excerpt. Focus on the most critical information — not every detail.`,
    `2. Highlight key vocabulary, people, dates, and events. Students add these to their notes using a structured format (two-column notes, timeline, or graphic organizer).`,
    `3. Guide students through close reading or structured analysis of a brief document or source related to the lesson.`,
    `4. Connect ${topic} to broader historical or geographic themes: cause/effect, change over time, multiple perspectives, and power dynamics.${instrNote}`,
  ].join('\n');
}

function generateSSGuidedPractice(ctx: Ctx): string {
  const { topic, t, instrNote } = ctx;
  const type = ssTopic(ctx.tl);

  const gpMap: Partial<Record<SSTopic, string>> = {
    'american-revolution': `Document Analysis — Primary Sources (≈${t(0.20)} min)

Pairs analyze one of two short primary sources (differentiate by readiness):
• Source A: Excerpt from Thomas Paine's Common Sense (1776) — "A long habit of not thinking a thing wrong gives it a superficial appearance of being right…"
• Source B: A Loyalist broadside (1775) arguing colonists should remain loyal to Britain.

Using the SOAPS framework:
S — Subject: What is this document about?
O — Occasion: When was it written, and what was happening?
A — Audience: Who was the intended reader?
P — Purpose: Why was it written? What does it want readers to do or believe?
S — Significance: Why does this document matter?

After analysis, each pair joins a pair with the other source. Together they compare: "Who had a stronger argument — and why? What would it take to change the other side's mind?"${instrNote}`,
    'civil-rights': `Document Analysis — "Letter from Birmingham Jail" Excerpt (≈${t(0.20)} min)

Students receive a 2-paragraph excerpt from Dr. King's April 1963 letter. Partners annotate using a protocol:
• Circle every word or phrase that is an appeal to logic (evidence, data, law).
• Underline every appeal to emotion.
• Put a box around every appeal to shared values or common humanity.

Then answer together:
1. Who is King writing to — and why does it matter that it's that specific audience?
2. What is King's strongest argument? Quote one sentence as evidence.
3. What is one counterargument King addresses — and how does he refute it?

Class share: "Was King's argument effective? What historical evidence supports your answer?"${instrNote}`,
    'geography': `Map Interpretation Activity (≈${t(0.20)} min)

Groups of 3 receive a packet of three maps of the same region: (1) physical features, (2) climate zones, (3) population density.

Tasks:
1. Compare the three maps. Record at least 3 patterns you notice across them.
2. Answer: "Why do people cluster in some areas and not others? Which physical features seem most related to high population density?"
3. On the blank outline map, shade in where you predict the region's agricultural areas would be. Explain your prediction using evidence from the three maps.
4. Discuss: "How might climate change affect population distribution in this region?"

Share findings with another group and discuss any differences in interpretation.${instrNote}`,
    'us-government': `Checks and Balances Simulation (≈${t(0.20)} min)

Divide the class into three groups: Congress, President, and Supreme Court.

Scenario 1: Congress passes "The Social Media Safety Act" banning students under 16 from social media. The President signs it. A civil liberties group challenges it in court.
• Congress group: write a 2-sentence argument for why this law is necessary.
• President group: write a 2-sentence signing statement explaining support.
• Supreme Court group: write a 2-sentence ruling — constitutional (First Amendment issue) or unconstitutional? Cite a specific reason.

Scenario 2: The President issues an executive order banning a specific book from public libraries.
• Congress: can they stop this? How?
• Supreme Court: what constitutional question does this raise?
• President: write a defense of the order.

Debrief: "Which branch had the most power in each scenario? Is that typical — or does it depend on the situation?"${instrNote}`,
    'economics': `Economic Decision-Making Simulation (≈${t(0.20)} min)

Groups of 3–4 are each a small business owner selling lemonade. They have $50 to spend on: lemons ($5/dozen), sugar ($3/bag), cups ($4/pack of 50), a stand ($15), and advertising ($5 for flyers).

Round 1: Each group allocates their budget and shares their choices. Compare: "Who spent the most on advertising? Why? What did they give up to do that?"

Round 2: Teacher announces — "A heat wave is forecast. Lemonade demand will double. How does this change your supply plans? How much will you charge?"

Round 3: Teacher announces — "Three new lemonade stands opened nearby. What happens to your sales? How do you respond?"

Debrief: "How did real market forces (supply, demand, competition) affect your decisions? What trade-offs did you face?"${instrNote}`,
  };

  return gpMap[type] ??
    `Document Analysis and Discussion (≈${t(0.20)} min)

Pairs use a structured protocol to analyze a primary or secondary source related to ${topic}:
1. Read the source twice — once for general meaning, once for detail.
2. Identify: What type of source is this? When and why was it created? Who is the audience?
3. Identify the author's main argument or message and cite two pieces of evidence that support it.
4. Discuss: "What perspective is presented? What perspective is missing? How might this source be biased?"

Groups share their most interesting finding. Teacher facilitates a whole-class discussion that connects multiple sources and perspectives.${instrNote}`;
}

function generateSSIndependentPractice(ctx: Ctx): string {
  const { topic, obj, t, instrNote } = ctx;
  const type = ssTopic(ctx.tl);

  const ipMap: Partial<Record<SSTopic, string>> = {
    'american-revolution': `Document-Based Writing (≈${t(0.20)} min)

Students write a paragraph response (8–10 sentences) to this question: "Was the American Revolution justified? Use evidence from at least two sources — including the Declaration of Independence — to support your argument."

Requirements:
• Clear claim sentence in the first line.
• At least two specific pieces of evidence from today's sources or direct instruction.
• Reasoning: explain how each piece of evidence supports the claim.
• Address one counterargument (e.g., Loyalist perspective).
• Concluding sentence connecting to a broader principle.${instrNote}`,
    'us-government': `Civics Analysis: Checks and Balances Case Study (≈${t(0.20)} min)

Students independently read a 1-page description of a historical constitutional conflict (e.g., FDR's court-packing plan, Nixon's Watergate, presidential executive orders). They answer:
1. Which branch(es) were involved?
2. What power or check was exercised — and by whom?
3. Was the outcome fair? What evidence from the Constitution supports your view?
4. What might have happened if no check existed?

Then they write a 3–5 sentence reflection: "What does this case reveal about why the founders designed the system the way they did?"${instrNote}`,
    'geography': `Regional Analysis Writing (≈${t(0.20)} min)

Students choose one of three provided regions and write a 1-page geographic profile that addresses:
• Absolute location (lat/long range) and relative location (describe in relation to surrounding regions).
• At least 3 physical features and their effect on human settlement.
• The dominant climate and how it affects economic activity.
• One example of human-environment interaction — how people have changed the environment, and vice versa.
• One prediction: "How might this region change in the next 50 years due to climate change or development?"${instrNote}`,
  };

  return ipMap[type] ??
    `Independent Analysis and Writing (≈${t(0.20)} min)

Students independently analyze a source or scenario related to ${topic} and write a structured response addressing today's essential question: ${obj}.

Requirements:
• Use at least two pieces of specific evidence (from sources or direct instruction).
• Include at least one vocabulary term from today's lesson, used correctly.
• Address the historical or geographic significance — why does ${topic} matter beyond its immediate context?

Students who finish early: "Write a follow-up question that today's lesson raises but doesn't fully answer. How would a historian investigate it?"${instrNote}`;
}

function generateSSExitTicket(ctx: Ctx): string {
  const { topic } = ctx;
  const type = ssTopic(ctx.tl);

  const etMap: Partial<Record<SSTopic, string[]>> = {
    'american-revolution': [
      `Name two causes of the American Revolution and explain how each contributed to the decision to declare independence.`,
      `"No taxation without representation" — what exactly were colonists demanding, and was their demand reasonable? Write 2–3 sentences.`,
    ],
    'civil-rights': [
      `Why did civil rights leaders choose nonviolent protest rather than armed resistance? Give one historical reason and one strategic reason.`,
      `Write one sentence explaining the significance of the Civil Rights Act of 1964 using the word "discrimination."`,
    ],
    'us-government': [
      `Give one example of each branch checking another. Explain why the founders thought this system was necessary.`,
      `Which branch of government do you think holds the most power in today's political system? Support your view with one specific reason.`,
    ],
    'geography': [
      `What is the difference between absolute and relative location? Give an example of each for the same place.`,
      `Choose a landform and explain how it would affect the decisions of people living near it (settlement, farming, transportation, trade).`,
    ],
    'economics': [
      `Define scarcity in your own words. Give one example of how scarcity affects a decision in your daily life.`,
      `If demand for a product increases but supply stays the same, what happens to price? Explain why using today's vocabulary.`,
    ],
  };

  const items = etMap[type] ?? [
    `In 2–3 sentences, explain the significance of ${topic} in your own words. Use at least one vocabulary term from today.`,
    `Identify one cause and one effect related to today's lesson on ${topic}.`,
    `What question does today's lesson make you want to investigate further? Write a one-sentence essential question.`,
  ];
  return items.join('\n');
}

function generateSSHomework(ctx: Ctx): string {
  const { topic } = ctx;
  const type = ssTopic(ctx.tl);

  const hwMap: Partial<Record<SSTopic, string>> = {
    'american-revolution': `Read the assigned textbook pages on the causes of the American Revolution. Create a cause-and-effect chart: list at least 4 causes (British policies or events) and for each, the colonial response. Write a one-sentence "big idea" at the bottom explaining what all the causes have in common.`,
    'civil-war': `Find one primary source (letter, diary entry, speech, or editorial) from the Civil War era. You can use the provided links or your textbook. Write 3–5 sentences explaining: who wrote it, when, what it says, and what it reveals about the writer's perspective on the war.`,
    'civil-rights': `Research one figure from the Civil Rights Movement not discussed in class today. Write a one-paragraph biography covering: who they were, what they did, what obstacles they faced, and what impact they had. Include the name of one primary source you used.`,
    'us-government': `Read a current news article about an action taken by one of the three branches of government. In 3–5 sentences, identify which branch acted, what power they exercised, and which other branch(es) could check this action and how.`,
    'geography': `Using a map of a region or country of your choice, describe its location (absolute and relative), identify 3 physical features, and explain how those features affect where and how people live. Draw or trace the map and annotate directly on it.`,
    'economics': `Read one short news article about a current economic issue (inflation, employment, housing prices, etc.). In 3–5 sentences, explain: what economic concept from today's lesson connects to this article, how supply or demand is involved, and what trade-off or opportunity cost someone faces in this situation.`,
  };

  return hwMap[type] ??
    `Complete the assigned reading or source analysis related to ${topic}. Alternatively, find one current-events article connected to today's lesson and write a one-paragraph summary explaining the connection to what we studied. Be ready to share your article and connection at the start of next class.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CROSS-SUBJECT GENERATORS (Materials, Success Criteria, Formative, Reflection)
// ═══════════════════════════════════════════════════════════════════════════════

function generateSuccessCriteria(ctx: Ctx): string {
  // Every I-can statement must name the actual skill students are learning today —
  // not a generic subject-area competency. Criteria are derived from the detected
  // instructional target (ctx.tl), NOT just the broad subject name.
  const { obj, isHigh, subject, gradeNum } = ctx;

  if (subject === 'Math') {
    const type = mathTopic(ctx.tl);
    const topicCriteria: Partial<Record<MathTopic, string[]>> = {
      'fractions-compare': [
        'I can compare two fractions by creating common denominators or using benchmark fractions.',
        "I can explain WHY a larger denominator alone doesn't mean a larger fraction, using a diagram.",
        'I can use <, >, and = to correctly order a set of fractions and justify each comparison.',
        isHigh ? 'I can compare and order mixed numbers and fractions on a number line and explain my reasoning.' : 'I can verify my comparisons by drawing fraction bars or placing fractions on a number line.',
      ],
      'fractions-add-sub': [
        'I can find a common denominator for two fractions and explain why one is needed before adding or subtracting.',
        'I can add and subtract fractions with unlike denominators and simplify the result.',
        'I can add and subtract mixed numbers, correctly regrouping when the fraction part requires it.',
        isHigh ? 'I can solve a multi-step problem involving adding and subtracting fractions in a real-world context.' : 'I can explain each step of my calculation in words.',
      ],
      'fractions-multiply-divide': [
        'I can multiply two fractions and explain what the product represents (a fraction of a fraction).',
        'I can divide a fraction by a whole number and a whole number by a fraction using visual models.',
        'I can apply the "multiply by the reciprocal" rule and explain why it works.',
        isHigh ? 'I can solve real-world problems involving multiplication and division of fractions, showing all steps.' : 'I can model fraction multiplication with an area model.',
      ],
      'fractions-equivalent': [
        'I can create an equivalent fraction by multiplying both the numerator and the denominator by the same nonzero whole number.',
        'I can draw a fraction model (rectangle divided into equal parts) to show that two fractions are equivalent.',
        'I can explain, using a model, why multiplying both parts by the same number does not change the value of the fraction.',
        gradeNum <= 5
          ? 'I can find the missing numerator or denominator to complete an equivalent fraction (e.g., ¹⁄₃ = __/9 → ²⁄₆).'
          : 'I can use equivalent fractions to compare fractions and solve real-world problems.',
      ],
      'algebra-equations': [
        'I can identify the variable, coefficient, and constant in an algebraic equation.',
        'I can solve one- and two-step equations by performing the same operation on both sides.',
        'I can check my solution by substituting it back into the original equation.',
        isHigh ? 'I can write and solve an equation from a real-world situation, define my variable, and interpret the solution.' : 'I can explain in words what the solution to an equation means in context.',
      ],
      'algebra-expressions': [
        'I can identify and write an algebraic expression from a word description.',
        'I can evaluate an expression by substituting a given value for the variable.',
        'I can simplify an expression by combining like terms.',
        isHigh ? 'I can apply the distributive property and order of operations to simplify complex expressions.' : 'I can explain the difference between an expression and an equation.',
      ],
      'multiplication': [
        'I can multiply multi-digit numbers using an efficient strategy (standard algorithm, partial products, or area model).',
        'I can estimate a product to check that my answer is reasonable.',
        'I can explain each step of my multiplication strategy in words.',
        isHigh ? 'I can apply multiplication to solve multi-step real-world problems and justify my method.' : 'I can use an area model or partial products to verify my answer.',
      ],
      'multiplication-partial-products': [
        'I can decompose a 2-digit factor into its tens and ones using place value.',
        'I can multiply each part by the other factor to find the partial products.',
        'I can add the partial products together to find the exact final product and explain why this gives the correct answer.',
        gradeNum <= 4
          ? 'I can estimate the product before I calculate and use the estimate to check whether my answer is reasonable.'
          : 'I can use the partial products strategy with 2-digit × 2-digit numbers and model the process with a four-section area model.',
      ],
      'division': [
        'I can divide multi-digit numbers and correctly interpret any remainder in context.',
        'I can estimate a quotient to check that my answer is reasonable.',
        'I can explain the connection between my division problem and a related multiplication fact.',
        isHigh ? 'I can solve multi-step problems involving division with remainders and justify each decision.' : 'I can use multiplication to check my division answer.',
      ],
      'geometry-area-perimeter': [
        'I can identify the correct formula for area or perimeter and explain what each formula measures.',
        'I can calculate the area and perimeter of rectangles, triangles, and composite figures.',
        'I can label answers with correct units (linear units for perimeter; square units for area) and explain why they differ.',
        isHigh ? 'I can decompose a composite figure, calculate the area of each part, and sum the total.' : 'I can draw and label a figure that matches a given area or perimeter.',
      ],
      'geometry-angles': [
        'I can identify and classify angles as acute, right, obtuse, and straight.',
        'I can use a protractor to measure and draw angles accurately.',
        'I can calculate a missing angle using the properties of supplementary and complementary angles.',
        isHigh ? 'I can find missing angles in triangles and parallel-line diagrams, justifying each step.' : 'I can verify my angle measurement by estimating first.',
      ],
      'percentages': [
        'I can convert between a percent, a decimal, and a fraction and explain the relationship.',
        'I can find the percent of a number using at least two different strategies.',
        'I can solve real-world percent problems: discounts, tax, tip, and percent increase/decrease.',
        isHigh ? 'I can work backward from a percent result to find the original amount.' : 'I can estimate a percent to check my answer for reasonableness.',
      ],
      'integers': [
        'I can place positive and negative integers on a number line and compare them using < and >.',
        'I can add integers with the same sign and with different signs, using a model to explain the result.',
        isHigh ? 'I can subtract integers by applying the "add the opposite" rule and explain why it works.' : 'I can use two-color counters to model integer addition and explain zero pairs.',
        'I can describe a real-world situation that matches a given integer expression.',
      ],
      'ratios-proportions': [
        'I can write a ratio in three forms (a:b, a/b, a to b) and explain what each part represents.',
        'I can determine whether two ratios are proportional and explain my reasoning.',
        'I can use a proportion to solve for an unknown value in a real-world situation.',
        isHigh ? 'I can solve unit rate problems and apply the constant of proportionality to graphs and tables.' : 'I can use a ratio table to find equivalent ratios.',
      ],
      'data-statistics': [
        'I can calculate mean, median, mode, and range for a data set.',
        'I can explain which measure of center best represents a given data set and why.',
        'I can construct and interpret a graph (line plot, histogram, or box plot) using real data.',
        isHigh ? 'I can identify outliers, explain how they affect the mean, and decide whether to include or exclude them.' : 'I can explain why the mean and median can be different for the same data set.',
      ],
      'decimals': [
        'I can read, write, and compare decimals to the thousandths place using place value understanding.',
        'I can add and subtract decimals by aligning place values.',
        isHigh ? 'I can multiply and divide decimals and explain where the decimal point belongs in the answer.' : 'I can represent a decimal on a number line and explain what each digit means.',
        'I can convert between fractions and decimals and explain the relationship.',
      ],
      'place-value': [
        'I can identify the value of each digit in a multi-digit number.',
        'I can read and write numbers in standard, word, and expanded form.',
        'I can round numbers to a given place value and explain my reasoning.',
        isHigh ? 'I can compare and order large numbers using place value reasoning.' : 'I can model a number using base-ten blocks and connect each block to its place value.',
      ],
    };

    const specific = topicCriteria[type];
    if (specific) return specific.join('\n');

    // Generic Math fallback — still objective-driven
    return [
      `I can explain the key concept of today's lesson — ${obj} — in my own words with an example.`,
      `I can solve problems that require me to ${obj}, showing every step of my reasoning.`,
      `I can check my answer for reasonableness and identify what would make it incorrect.`,
      isHigh ? `I can apply today's skill to a multi-step real-world problem and justify my strategy.` : `I can explain the strategy I used today to a partner who missed class.`,
    ].join('\n');
  }

  if (subject === 'English Language Arts') {
    const type = elaTopic(ctx.tl);
    const topicCriteria: Partial<Record<ELATopic, string[]>> = {
      'main-idea': [
        "I can state the main idea of a text in one sentence that names WHAT it's about AND the most important thing the author says about it.",
        "I can distinguish between a topic (what it's about) and a main idea (the key message).",
        'I can identify at least two supporting details and explain how each one develops the main idea.',
        isHigh ? 'I can identify an implied main idea when the author never directly states it.' : 'I can use the "zoom out" strategy to move from individual details to the whole-text main idea.',
      ],
      'inference': [
        'I can make an inference using the formula: Text Evidence + Prior Knowledge = Inference.',
        'I can quote specific words from the text that led to my inference.',
        'I can explain the logical connection between the evidence and my inference — not just state both.',
        isHigh ? 'I can evaluate two competing inferences about the same passage and argue which is better supported.' : 'I can distinguish between an inference (text-supported conclusion) and a guess (no text support).',
      ],
      'opinion-argument': [
        'I can write a specific, arguable claim statement — not just a preference or opinion.',
        'I can select evidence that directly supports my claim and explain WHY it supports it (reasoning).',
        'I can write reasoning that connects evidence to claim — not just restate what the evidence says.',
        isHigh ? 'I can acknowledge a counterclaim and write a rebuttal that strengthens my original argument.' : 'I can identify the difference between a strong claim and a weak claim and revise accordingly.',
      ],
      'figurative-language': [
        'I can identify similes, metaphors, personification, hyperbole, and idioms in a text.',
        'I can explain what each example means literally and what it means figuratively.',
        'I can explain WHY an author chose figurative language — what effect it creates for the reader.',
        isHigh ? 'I can write original examples of at least three types of figurative language and explain the intended effect of each.' : 'I can write one original simile and one original metaphor about the same subject.',
      ],
      'narrative': [
        'I can identify and explain the five elements of plot: exposition, rising action, climax, falling action, and resolution.',
        'I can distinguish between internal conflict (character vs. self) and external conflict, and explain why conflict drives a story.',
        "I can analyze how a character's motivation connects to the story's central conflict and resolution.",
        isHigh ? "I can write a narrative opening (3–4 sentences) that establishes character, setting, and conflict simultaneously." : 'I can identify where the climax occurs and explain why it is the turning point — not just the most exciting event.',
      ],
      'authors-purpose': [
        "I can identify whether an author's purpose is to persuade, inform, or entertain (PIE) and explain how I know.",
        'I can cite specific text features, word choices, or structural patterns that reveal the purpose.',
        "I can explain how knowing the author's purpose changes how I read or evaluate the text.",
        isHigh ? "I can evaluate how effectively the author achieved their purpose and support my evaluation with textual evidence." : 'I can find one specific clue word or structural feature in a passage that reveals its purpose.',
      ],
      'theme': [
        'I can identify the theme of a text — the universal message — without confusing it with the plot summary.',
        'I can cite at least two pieces of evidence (character actions, dialogue, or events) that support the theme.',
        "I can explain how the theme connects to real life beyond the story's specific characters or setting.",
        isHigh ? 'I can compare the themes of two texts and evaluate which author communicates the theme more effectively.' : "I can write the theme as a complete sentence (not just a one-word topic like 'friendship').",
      ],
      'grammar-sentences': [
        'I can identify simple, compound, complex, and compound-complex sentences by their structure.',
        'I can identify and correct sentence fragments by adding what is missing.',
        'I can identify and correct run-on sentences using at least two different strategies.',
        isHigh ? 'I can deliberately vary sentence structure in my writing and explain the effect of each choice.' : 'I can combine two simple sentences into a compound or complex sentence.',
      ],
      'vocabulary-context': [
        'I can identify four types of context clues: definition, synonym, antonym, and example.',
        'I can use context clues to determine the probable meaning of an unfamiliar word.',
        'I can identify which type of clue helped me and explain exactly how I used it.',
        isHigh ? 'I can use prefix, suffix, and root word knowledge alongside context clues to determine word meaning.' : 'I can write a sentence that provides a strong context clue for a given vocabulary word.',
      ],
      'summarizing': [
        'I can identify the most important events or ideas in a text and distinguish them from minor details.',
        'I can write a summary in my own words that captures the text\'s main points without copying sentences.',
        'I can use signal words (first, then, next, finally) to sequence events accurately in a summary.',
        isHigh ? 'I can write an objective summary that does not include my personal opinion or evaluation.' : 'I can use the "somebody–wanted–but–so–then" framework to summarize a narrative.',
      ],
      'compare-contrast': [
        'I can identify specific similarities and differences between two texts, characters, events, or ideas.',
        'I can use a graphic organizer (Venn diagram or T-chart) to organize comparison points before writing.',
        'I can write a compare-contrast paragraph that uses transition words (similarly, however, in contrast).',
        isHigh ? 'I can evaluate which similarities or differences are most significant and explain why.' : 'I can find at least two similarities and two differences and explain the significance of each.',
      ],
      'poetry': [
        'I can identify poetic elements: stanza, line break, rhyme scheme, and at least two sound devices.',
        'I can explain how the poet uses imagery (sensory details) to create a specific mood or feeling.',
        'I can analyze how line breaks and white space create emphasis or pacing in a poem.',
        isHigh ? "I can write a short poem that deliberately uses at least three poetic devices and explain each choice." : 'I can annotate a poem by identifying figurative language and explaining what it means.',
      ],
    };

    const specific = topicCriteria[type];
    if (specific) return specific.join('\n');

    // Generic ELA fallback — objective-driven
    return [
      `I can explain the key skill of today's lesson — ${obj} — in my own words with a text-based example.`,
      `I can apply today's skill to a text and explain my thinking using specific evidence.`,
      `I can produce written work that demonstrates my ability to ${obj}.`,
      isHigh ? `I can evaluate an author's use of today's skill and explain its effect on the reader.` : `I can use today's strategy in my independent reading or writing and explain my reasoning.`,
    ].join('\n');
  }

  if (subject === 'Science') {
    const type = sciTopic(ctx.tl);
    const topicCriteria: Partial<Record<ScienceTopic, string[]>> = {
      'food-chains': [
        'I can identify producers, primary consumers, secondary consumers, and decomposers in a food web.',
        'I can trace the path of energy from the sun through at least three organisms in a food chain.',
        'I can explain the 10% rule and why so little energy transfers from one trophic level to the next.',
        'I can predict what would happen to other organisms if one species in a food web disappeared — and explain the cascade.',
      ],
      'ecosystems': [
        'I can define ecosystem and distinguish between biotic and abiotic factors with specific examples of each.',
        'I can explain how biotic and abiotic factors depend on each other to maintain ecosystem stability.',
        'I can explain what carrying capacity means and describe what causes a population to overshoot and crash.',
        'I can give evidence for why biodiversity makes an ecosystem more resilient to change.',
      ],
      'cells': [
        'I can identify at least six organelles in a plant cell and an animal cell and state the function of each.',
        'I can explain three similarities and three differences between plant cells and animal cells.',
        'I can state the three parts of the cell theory and explain why it is called a scientific theory.',
        'I can connect my microscope observations to the cell structures I labeled in my diagram.',
      ],
      'forces-motion': [
        'I can define force, identify its unit (Newton), and give examples of contact and non-contact forces.',
        "I can state Newton's First Law and give a real-world example that demonstrates inertia.",
        'I can calculate acceleration using F = ma when given any two of the three values.',
        'I can draw a force diagram showing balanced and unbalanced forces and predict whether motion will change.',
      ],
      'water-cycle': [
        'I can name and define the four main processes of the water cycle: evaporation, condensation, precipitation, and collection.',
        'I can explain WHY condensation occurs — the scientific relationship between air temperature and water vapor capacity.',
        "I can trace a water molecule's complete journey through the cycle using correct scientific sequence and vocabulary.",
        'I can connect a real weather observation (clouds, rain, humidity) to a specific stage of the water cycle.',
      ],
      'scientific-method': [
        'I can write a testable hypothesis in if–then format that clearly identifies the independent and dependent variables.',
        'I can identify at least three controlled variables in an experiment and explain why each must stay constant.',
        'I can design a fair-test procedure with numbered steps, a materials list, and a blank data table.',
        isHigh ? 'I can analyze sample data to write a conclusion that confirms or revises the original hypothesis with specific evidence.' : 'I can distinguish between an observation (what I measured) and an inference (what it means).',
      ],
      'adaptations': [
        'I can define adaptation and distinguish between structural adaptations and behavioral adaptations.',
        "I can explain how a specific adaptation increases an organism's chance of survival in its environment.",
        'I can connect natural selection to why advantageous adaptations become more common over time.',
        isHigh ? 'I can explain why an adaptation that is beneficial in one environment might be harmful in another.' : 'I can give two examples of adaptations and match each to the specific survival challenge it solves.',
      ],
      'life-cycles': [
        'I can sequence and label the stages of both complete and incomplete metamorphosis.',
        'I can explain the purpose of each stage in a life cycle — not just name it.',
        'I can compare the life cycles of two different organisms and explain why they differ.',
        'I can give evidence for how a life cycle structure is adapted to help the organism survive and reproduce.',
      ],
      'genetics': [
        'I can define gene, allele, dominant, and recessive trait and use each term correctly.',
        'I can use a Punnett square to predict the probability of offspring inheriting a trait.',
        "I can distinguish between an organism's genotype (genetic makeup) and phenotype (expressed trait).",
        isHigh ? 'I can explain how incomplete dominance and codominance differ from simple dominant-recessive inheritance.' : 'I can give a real-world example of a dominant and recessive trait in humans.',
      ],
      'plants': [
        'I can identify the main parts of a plant (roots, stem, leaves, flower, seed) and explain the function of each.',
        'I can explain how photosynthesis works — what goes in, what comes out, and where it happens.',
        'I can describe at least two methods of pollination and explain why pollination is essential for reproduction.',
        isHigh ? 'I can trace matter and energy through a plant from sunlight to stored sugar.' : 'I can explain why plants need both sunlight and water to survive.',
      ],
      'matter-states': [
        'I can identify the three states of matter and describe the arrangement and movement of particles in each.',
        'I can explain what happens at the particle level when matter changes state (melting, freezing, evaporation, condensation).',
        'I can give real-world examples of each state change and identify the energy involved.',
        isHigh ? 'I can explain why temperature changes cause state changes in terms of particle energy.' : 'I can classify common materials as solid, liquid, or gas and explain my reasoning.',
      ],
      'energy': [
        'I can distinguish between potential energy and kinetic energy and give examples of each.',
        'I can explain how energy is converted from one form to another (not created or destroyed).',
        'I can identify at least three forms of energy (thermal, light, sound, electrical, mechanical) in everyday situations.',
        isHigh ? 'I can calculate kinetic energy using KE = ½mv² and explain what each variable represents.' : 'I can trace an energy conversion through at least two steps (e.g., chemical → mechanical → thermal).',
      ],
    };

    const specific = topicCriteria[type];
    if (specific) return specific.join('\n');

    // Generic Science fallback
    return [
      `I can define the key vocabulary from today's lesson and use it to describe my observations.`,
      `I can explain ${obj} using specific scientific evidence — not just memory or opinion.`,
      `I can connect today's investigation or activity to the scientific concept we are studying.`,
      `I can ask one new testable question about what we investigated today.`,
    ].join('\n');
  }

  // Social Studies + Other subjects
  const type = ssTopic(ctx.tl);
  const ssTopicCriteria: Partial<Record<SSTopic, string[]>> = {
    'american-revolution': [
      'I can identify at least three specific causes of the American Revolution and explain how each created colonial tension.',
      'I can explain the main argument of the Declaration of Independence and how it justified breaking from Britain.',
      'I can evaluate which cause was most significant and support my choice with historical evidence.',
      isHigh ? 'I can analyze the Revolution from both Patriot and Loyalist perspectives and explain why colonists disagreed.' : 'I can place major events of the Revolution in chronological order and identify the turning point.',
    ],
    'civil-war': [
      "I can identify economic, political, and social causes of the Civil War and explain how they were connected to each other.",
      "I can explain what the Emancipation Proclamation did — and what it didn't do — and why Lincoln issued it when he did.",
      'I can identify the significance of at least one major battle and explain how it affected the outcome of the war.',
      isHigh ? 'I can evaluate the effectiveness of Union and Confederate leadership using specific historical evidence.' : 'I can compare daily life for soldiers and civilians during the Civil War using primary and secondary sources.',
    ],
    'civil-rights': [
      'I can identify at least three major events or strategies of the Civil Rights Movement and explain their impact.',
      'I can explain the significance of the Civil Rights Act and the Voting Rights Act — what they changed and what they left unresolved.',
      'I can analyze a primary source from the movement and explain the perspective of its author.',
      isHigh ? 'I can evaluate the effectiveness of nonviolent resistance as a strategy, using specific events as evidence.' : 'I can explain why the Montgomery Bus Boycott is considered a turning point in the movement.',
    ],
    'us-government': [
      "I can name the three branches of the U.S. government and explain each branch's primary function.",
      'I can explain how checks and balances prevent any one branch from becoming too powerful.',
      'I can describe the process by which a bill becomes a law, identifying where it can be stopped.',
      isHigh ? 'I can evaluate whether the system of checks and balances is working effectively today, using a current example.' : 'I can explain why the Founders divided power among three branches instead of concentrating it in one.',
    ],
    'ancient-civilizations': [
      'I can identify at least three characteristics that define a civilization.',
      'I can explain how geography shaped the development of the civilization we studied today.',
      'I can describe specific achievements (writing, architecture, government, or trade) and explain their lasting impact.',
      isHigh ? "I can compare today's civilization to one we've previously studied and evaluate which had greater long-term historical influence." : 'I can connect one achievement of this ancient civilization to something in the modern world.',
    ],
    'geography': [
      'I can read and interpret a map using its key, scale, and directional indicator.',
      'I can explain how physical geography (landforms, climate, water) influenced how people lived and where they settled.',
      'I can use geographic vocabulary (latitude, longitude, region, landform) correctly in written and verbal explanations.',
      isHigh ? 'I can analyze how geographic factors shaped a specific historical event or cultural practice.' : 'I can identify a landform on a map and explain how it affected human activity in that region.',
    ],
    'immigration': [
      'I can define immigration and explain at least two push factors and two pull factors that drive it.',
      'I can describe the experiences of immigrants arriving in the United States during a specific historical period.',
      'I can explain how immigration shaped American culture, economy, and society.',
      isHigh ? 'I can compare immigration patterns from two different historical periods and explain what changed and why.' : 'I can explain the difference between voluntary and involuntary migration using historical examples.',
    ],
    'westward-expansion': [
      "I can explain the concept of Manifest Destiny and describe how it justified westward expansion.",
      'I can identify at least three groups affected by westward expansion and explain the impact on each.',
      'I can trace major routes west (Oregon Trail, Santa Fe Trail) and explain the challenges pioneers faced.',
      isHigh ? 'I can evaluate whether westward expansion was beneficial or harmful overall, supporting my argument with evidence from multiple perspectives.' : 'I can explain the impact of westward expansion on Native American nations using at least two specific examples.',
    ],
    'world-war': [
      'I can identify the major causes of the war (MAIN: Militarism, Alliances, Imperialism, Nationalism) and explain how they triggered the conflict.',
      'I can describe the significance of at least one major battle or turning point and its effect on the outcome.',
      'I can explain how the war changed the political, economic, or social landscape of the nations involved.',
      isHigh ? 'I can evaluate the long-term consequences of the peace treaty and explain how it shaped future events.' : 'I can identify which nations were on each side and explain why they chose their alliance.',
    ],
    'reconstruction': [
      'I can explain the goals of Reconstruction and identify at least two major achievements of the era.',
      'I can describe the role of Freedmen\'s Bureau, constitutional amendments, and Black political participation during Reconstruction.',
      'I can explain how and why Reconstruction ended and what the consequences were for formerly enslaved people.',
      isHigh ? 'I can evaluate whether Reconstruction succeeded or failed, supporting my argument with evidence from the period.' : 'I can identify at least two ways the lives of formerly enslaved people changed — and two ways they did not — during Reconstruction.',
    ],
    'economics': [
      'I can define supply, demand, and price, and explain how they are related.',
      'I can read a supply-and-demand graph and predict what happens to price when supply or demand changes.',
      'I can explain scarcity and explain why it forces every economic actor to make trade-offs.',
      isHigh ? 'I can analyze a real-world market event (price change, shortage, or surplus) using supply-and-demand principles.' : 'I can give a real-world example of supply and demand in action and explain the outcome.',
    ],
    'industrial-revolution': [
      'I can identify at least three major inventions of the Industrial Revolution and explain how each changed daily life.',
      'I can explain how the Industrial Revolution shifted the economy from agriculture to manufacturing.',
      'I can describe working conditions in factories during the Industrial Revolution and explain why reform movements emerged.',
      isHigh ? 'I can evaluate whether the Industrial Revolution improved or worsened quality of life for different groups, using evidence for each group.' : 'I can explain at least two ways the Industrial Revolution changed where and how people lived.',
    ],
    'native-american': [
      "I can describe at least two distinct Native American nations and explain how their cultures were shaped by their environments.",
      'I can explain the impact of European contact and westward expansion on Native American communities.',
      'I can analyze a primary source from a Native American perspective and explain what it reveals about their experience.',
      isHigh ? "I can evaluate the long-term consequences of U.S. government policies toward Native Americans and connect them to present-day issues." : 'I can explain how Native American nations resisted displacement and describe at least one specific example.',
    ],
    'colonial-america': [
      'I can describe life in at least two of the three colonial regions (New England, Middle, Southern) and explain how geography shaped each.',
      'I can explain the economic systems of the colonies and the role of trade in colonial society.',
      'I can analyze the relationship between the colonies and Britain before the Revolutionary era.',
      isHigh ? 'I can evaluate the degree to which colonial society offered equality or opportunity, and for whom.' : 'I can identify at least two ways colonial life differed from life in England and explain the reasons.',
    ],
  };

  const ssCriteria = subject === 'Social Studies' ? ssTopicCriteria[type as SSTopic] : undefined;
  if (ssCriteria && ssCriteria.length) return ssCriteria.join('\n');

  // Fully generic fallback (Other subjects or unmatched SS topics)
  return [
    `I can explain what today's lesson is about — ${obj} — in my own words using key vocabulary from class.`,
    `I can apply today's concept to analyze a source, scenario, or real-world example.`,
    `I can explain the significance of what I learned — why it matters beyond the immediate lesson.`,
    isHigh ? `I can evaluate multiple perspectives on today's topic and construct a supported argument with evidence.` : `I can identify at least one cause and one consequence connected to today's lesson.`,
  ].join('\n');
}

function generateMaterials(ctx: Ctx): string {
  const { subject, topic } = ctx;
  const type = subject === 'Math' ? mathTopic(ctx.tl) : null;

  const baseMaterials: Record<string, string[]> = {
    'Math': [
      'Pencils and colored pencils or pens',
      'Graph paper or lined notebooks',
      'Dry-erase boards and markers (one per student or pair)',
      'Anchor chart paper and markers',
    ],
    'English Language Arts': [
      'Printed text excerpt or class reading books',
      'Pencils, pens, and highlighters (2–3 colors)',
      'Student writing journals or composition books',
      'Anchor chart paper and markers',
    ],
    'Science': [
      'Science journals or lab notebooks (one per student)',
      'Pencils and colored pencils',
      'Anchor chart paper and markers',
    ],
    'Social Studies': [
      'Textbooks or printed primary/secondary source documents',
      'Colored pencils or highlighters',
      'Student notebooks or graphic organizer handouts',
      'Anchor chart paper and markers',
    ],
    'Other': [
      'Notebooks or lined paper',
      'Pencils and pens',
      'Whiteboard and dry-erase markers',
      'Anchor chart paper and markers',
    ],
  };

  const specialMaterials: Record<string, string[]> = {
    'fractions-compare': ['Fraction tiles or fraction bars (physical or printed)', 'Rulers for drawing fraction bars'],
    'fractions-add-sub': ['Fraction strips (cut or pre-made)', 'Number lines printed on laminated cards'],
    'fractions-multiply-divide': ['Grid paper for area models', 'Fraction tiles (optional)'],
    'geometry-area-perimeter': ['Rulers and/or measuring tapes', 'Colored square tiles or grid paper', 'Scissors (for composite figure activities)'],
    'geometry-angles': ['Protractors (one per student)', 'Pre-cut angle cards for sorting', 'Rulers'],
    'multiplication': ['Graph paper for area models', 'Base-ten blocks (for lower grades)'],
    'division': ['Graph paper', 'Base-ten blocks (optional, for concrete learners)'],
    'data-statistics': ['Graph paper', 'Rulers', 'Data sets on cards (teacher prepared)', 'Colored markers for graphing'],
    'algebra-equations': ['Algebra tiles or balance scale model (physical or illustrated)'],
    'integers': ['Two-sided counters (red/yellow)', 'Number line strip (laminated, −20 to 20)'],
  };

  const base = baseMaterials[subject] ?? baseMaterials['Other'];
  const special = type && specialMaterials[type] ? specialMaterials[type] : [];

  const scienceExtras: Partial<Record<ScienceTopic, string[]>> = {
    'cells': ['Microscopes (one per pair or group)', 'Prepared slides (onion skin, cheek cells)', 'Blank cell diagram handouts'],
    'forces-motion': ['Spring scales (one per group)', 'Textbooks of similar weight (for friction lab)', 'Three surface samples (smooth, carpet, sandpaper)'],
    'water-cycle': ['Zip-lock bags (one per group)', 'Water and blue food coloring', 'Tape'],
    'food-chains': ['Role cards (producer, consumer, decomposer, sun)', 'Ball of yarn or string', 'Printed food web diagram'],
    'scientific-method': ['Toy car and ramp materials', 'Ruler or measuring tape', 'Stopwatch'],
  };

  if (subject === 'Science') {
    const sciType = sciTopic(ctx.tl);
    const extras = scienceExtras[sciType] ?? [`Materials for ${topic} investigation (see teacher preparation notes)`, 'Safety goggles (if needed for hands-on work)'];
    return [...base, ...extras]
      .map(m => `• ${m}`).join('\n');
  }

  return [...base, ...special]
    .map(m => `• ${m}`).join('\n');
}

// ── Required Materials (lesson-essential) ─────────────────────────────────────

function generateRequiredMaterials(ctx: Ctx): string {
  // Returns only the materials a teacher must have to run the lesson as written
  return generateMaterials(ctx);
}

// ── Optional / Supplemental Materials ────────────────────────────────────────

function generateOptionalMaterials(ctx: Ctx, multimediaMode: MultimediaMode = 'none'): string {
  const { subject } = ctx;
  const items: string[] = [];

  // Multimedia tech (only when requested)
  if (multimediaMode !== 'none') {
    items.push('Projector or interactive whiteboard');
    items.push('Teacher device for displaying video or simulation');
    if (multimediaMode === 'interactive' || multimediaMode === 'both') {
      items.push('Student devices (for accessing interactive simulation — optional)');
    }
  }

  // Universal differentiation support
  items.push('Sentence frames or vocabulary reference cards (support for below-level learners and ELLs)');
  items.push('Extension task cards or open-ended challenge problems (above-level enrichment)');

  // Subject-specific supplemental
  const subjectExtras: Record<string, string[]> = {
    'Math': [
      'Calculator (for checking computation after manual work)',
      'Multiplication tables or math reference cards (for students who need them)',
    ],
    'English Language Arts': [
      'Bilingual dictionary or translation resource (for EL students)',
      'Audiobook or read-aloud access to the text',
    ],
    'Science': [
      'Additional lab materials if extending or repeating the investigation',
      'Science reference cards with vocabulary visuals',
    ],
    'Social Studies': [
      'Atlas or additional map resources',
      'Primary source extension documents for advanced study',
    ],
  };
  const extras = subjectExtras[subject] ?? ['Reference materials for extension activities'];
  items.push(...extras);

  return items.map(i => `• ${i}`).join('\n');
}

// ── Teacher Preparation ───────────────────────────────────────────────────────

function generateTeacherPrep(ctx: Ctx): string {
  const { subject, topic, tl, isYoung, isElem } = ctx;
  const tasks: string[] = [];

  // Universal prep tasks (every lesson)
  tasks.push(`Review today's lesson sequence and all worked examples before class. Anticipate likely student errors and prepare your responses.`);
  tasks.push(`Prepare anchor chart: write the lesson title and key vocabulary ahead of class. Leave space to add student thinking and co-constructed definitions during Direct Instruction.`);

  // Subject- and topic-specific prep
  if (subject === 'Math') {
    const mt = mathTopic(tl);
    type MathPrepKey = 'fractions-compare' | 'fractions-add-sub' | 'fractions-multiply-divide' | 'multiplication' | 'geometry-area-perimeter' | 'geometry-angles' | 'algebra-equations' | 'data-statistics' | 'integers';
    const mathPrep: Partial<Record<MathPrepKey, string[]>> = {
      'fractions-compare': [
        `Pre-cut fraction tile strips (one set per pair), or open the digital fraction bar tool and test it before class.`,
        `Draw one worked example for each of the four comparison strategies on chart paper or the board (same denominator, same numerator, benchmark ½, common denominator). Verify each example is correct before class.`,
      ],
      'fractions-add-sub': [
        `Prepare fraction strip sets — one set per pair. Pre-cut from printed sheets or distribute pre-made strips.`,
        `Write three worked examples showing the common-denominator steps on chart paper. Verify each answer before class.`,
      ],
      'fractions-multiply-divide': [
        `Prepare grid paper area models for the Direct Instruction examples. Check all products.`,
        `Prepare a "flip and multiply" visual with a number-line explanation for why division by a fraction works.`,
      ],
      'multiplication': [
        `Prepare area model templates (one per student) or set up the grid paper.`,
        `If using base-ten blocks, organize sets by group before class.`,
      ],
      'geometry-area-perimeter': [
        `Cut composite figure examples on cardstock for the hands-on exploration.`,
        `Verify ruler availability — one per student or per pair.`,
      ],
      'geometry-angles': [
        `Check that protractors are available and undamaged — one per student.`,
        `Prepare pre-cut angle sorting cards with angle measures labeled on the back.`,
      ],
      'algebra-equations': [
        `Set up algebra tile sets or draw a balance scale model on the whiteboard as a visual anchor.`,
        `Write and solve three example equations. Verify that each solution is correct before class.`,
      ],
      'data-statistics': [
        `Pre-organize data sets on index cards or printed strips — one set per group.`,
        `Set up blank graph paper or draw empty graph templates on the board.`,
      ],
      'integers': [
        `Prepare two-sided counters (one bag per student or pair).`,
        `Tape a laminated number line (−20 to 20) to each desk or table.`,
      ],
    };
    const specTasks = mathPrep[mt as MathPrepKey];
    if (specTasks) tasks.push(...specTasks);
    else tasks.push(`Prepare worked examples for ${topic}. Verify all computations for accuracy before class.`);

  } else if (subject === 'Science') {
    const st = sciTopic(tl);
    type SciPrepKey = 'food-chains' | 'cells' | 'water-cycle' | 'forces-motion' | 'scientific-method';
    const sciPrep: Partial<Record<SciPrepKey, string[]>> = {
      'food-chains': [
        `Print and cut role cards (Producer, Consumer, Decomposer, Sun) — one set per group.`,
        `Pre-cut yarn lengths for the food web string activity (8–10 pieces per group).`,
        `Print the food web diagram handout — one per student.`,
      ],
      'cells': [
        `Set up microscopes and load slides before class. Confirm image is in focus for at least one objective.`,
        `Prepare blank cell diagram handouts — one per student.`,
        `If using a projected digital microscope, test the connection and image quality.`,
      ],
      'water-cycle': [
        `Prepare zip-lock bag stations: add water with blue food coloring, seal, and label — one per group.`,
        `Identify the sunniest window in the room, or set up a lamp for groups without window access.`,
      ],
      'forces-motion': [
        `Assemble friction lab stations: spring scale, textbook, three surface samples. Test each spring scale.`,
        `Check that three different surface types (smooth, carpet, sandpaper) are available at each station.`,
      ],
      'scientific-method': [
        `Set up toy car ramp stations — one per group. Test ramp angles to confirm a measurable range.`,
        `Prepare recording sheets with hypothesis, procedure, and data table sections.`,
      ],
    };
    const specTasks = sciPrep[st as SciPrepKey];
    if (specTasks) tasks.push(...specTasks);
    else {
      tasks.push(`Gather and organize all lab or investigation materials. Test equipment to confirm it works correctly.`);
      tasks.push(`Print or copy any handouts, graphic organizers, or data recording sheets — one per student.`);
    }

  } else if (subject === 'English Language Arts') {
    tasks.push(`Print or copy the reading text or graphic organizer — one per student. Confirm readability is appropriate for grade level.`);
    tasks.push(`Select 2–3 strong model responses or annotated examples to display during Direct Instruction.`);
    if (isYoung || isElem) {
      tasks.push(`Pre-highlight key vocabulary terms in the text using highlighter tape or a projected version.`);
    }
  } else if (subject === 'Social Studies') {
    tasks.push(`Print all primary and secondary source documents — one per student or pair. Confirm text is legible.`);
    tasks.push(`If using maps, verify they are accurate, labeled, and legible at the projected scale.`);
    tasks.push(`Prepare graphic organizers or note-taking templates and print copies before class.`);
  }

  // Universal closing prep tasks
  tasks.push(`Arrange student seating to match the lesson structure (pairs for guided practice, small groups for discussion).`);
  tasks.push(`Open any digital resources, links, or simulations on the classroom device. Test audio and display before students arrive.`);

  return tasks.map((t, i) => `${i + 1}. ${t}`).join('\n');
}

// ── Lesson at a Glance ────────────────────────────────────────────────────────

function generateLessonAtAGlance(ctx: Ctx, lesson: GeneratedLesson): LessonAtAGlance {
  const { obj, dMin, topic } = ctx;

  // ── Count blueprint items per section (authoritative — the same numbers
  //    used in the Activity Sheet, Presentation, and Assessment) ────────────
  const bp = lesson.blueprint ?? extractBlueprint(lesson);

  const countItems = (items: { n?: number }[] | undefined, fallbackText: string): number => {
    if (items && items.length > 0) return items.length;
    // Fallback: count "1." / "1)" style numbered items in the raw text
    return (fallbackText.match(/^\d+[.)]\s/gm) ?? []).length || 0;
  };

  const warmUpCount   = countItems(bp.warmUp?.items,              lesson.warmUp);
  const diCount       = countItems(bp.directInstruction?.items,   lesson.directInstruction);
  const gpCount       = countItems(bp.guidedPractice?.items,      lesson.guidedPractice);
  const ipCount       = countItems(bp.independentPractice?.items, lesson.independentPractice);
  const etCount       = countItems(bp.exitTicket?.items,          lesson.exitTicket);

  // Human-readable item labels — vary by subject so ELA/Science/SS don't say "problems"
  const isMath = ctx.subject === 'Math';
  const practiceWord = isMath ? 'problem' : 'item';

  const plural = (n: number, word: string) => `${n} ${word}${n !== 1 ? 's' : ''}`;

  // Format: "Section Name — N descriptive label"
  // The renderer in LessonPlanner splits on " — " to display section + count as two lines.
  const mainStudentTasks = [
    `Warm-Up — ${plural(warmUpCount || 5, 'review question')}`,
    `Direct Instruction — ${plural(diCount || 4, 'worked example')}`,
    `Guided Practice — ${plural(gpCount || 4, `collaborative ${practiceWord}`)}`,
    `Independent Practice — ${plural(ipCount || 8, `independent ${practiceWord}`)}`,
    `Exit Ticket — ${plural(etCount || 2, 'assessment question')}`,
  ];

  // ── Lesson-specific teacher action list ──────────────────────────────────
  // Actions are derived from count data — more natural, less repetitive phrasing.
  const mainTeacherActions = [
    `Launch warm-up: ${plural(warmUpCount || 5, 'review question')} to activate prior knowledge`,
    `Model ${plural(diCount || 4, 'worked example')} with think-alouds; call on students after each one`,
    `Guide ${plural(gpCount || 4, `collaborative ${practiceWord}`)}; circulate and give real-time feedback`,
    `Release to independent work (${plural(ipCount || 8, practiceWord)}); use formative data to pull small groups`,
    `Collect ${plural(etCount || 2, 'exit ticket item')}; use results to plan tomorrow's lesson`,
  ];

  // ── Required resources (from requiredMaterials if available) ─────────────
  const materialLines = (lesson.requiredMaterials || lesson.materials)
    .split('\n')
    .map(l => l.replace(/^[•\-]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, 4);

  return {
    objective: `Students will be able to ${obj}.`,
    mainStudentTasks,
    mainTeacherActions,
    requiredResources: materialLines,
    duration: `${dMin} minutes`,
  };
}

function generateFormativeAssessment(ctx: Ctx): string {
  // Rule 4: Formative assessment must name WHAT to look for — specific skills and
  // error patterns — not just HOW to check (whiteboard, cold-call). These are
  // lesson-specific checkpoints, not generic monitoring strategies.
  const { subject } = ctx;

  const checkpoint = (() => {
    if (subject === 'Math') {
      const type = mathTopic(ctx.tl);
      const checks: Partial<Record<MathTopic, string>> = {
        'fractions-compare': `Lesson-specific checkpoint — check FOR: (1) Students comparing fractions by finding common denominators or using benchmarks (½) — not just looking at which denominator is bigger. (2) Statements that include a justification ("because..."), not just a symbol. Red flag: a student who writes ¾ > ⅔ because "4 > 3" is using the wrong reasoning even if the answer is correct. During the "show me" check: require students to draw a model alongside their symbol.`,
        'fractions-add-sub': `Lesson-specific checkpoint — check FOR: (1) Students finding a common denominator BEFORE operating — watch for ½ + ⅓ = 2/5 (the most common error: adding numerators and denominators straight across). (2) Correct renaming of both fractions. (3) Simplified answers. During guided practice: ask "why can't we just add the numerators?" — students who can answer this understand; students who can't need reteaching before independent practice.`,
        'fractions-multiply-divide': `Lesson-specific checkpoint — check FOR: (1) Students multiplying numerators × numerators and denominators × denominators (not cross-multiplying). (2) For division: students writing the reciprocal and multiplying — not guessing. Red flag: any student who says "invert and multiply" without being able to explain why. Ask: "What would dividing 6 by ½ look like on a number line? How many halves fit in 6?" If they can't answer, they're computing without understanding.`,
        'algebra-equations': `Lesson-specific checkpoint — check FOR: (1) Students performing the same operation on BOTH sides — watch for students who only modify one side. (2) Students checking by substituting the answer back into the original equation. Red flag: a student who writes 3x = 15 → x = 15 − 3 = 12. During "show me": require the substitution check on every problem, not just when they're unsure.`,
        'multiplication': `Lesson-specific checkpoint — check FOR: (1) Correct place-value alignment — when using standard algorithm, second partial product must be shifted left. (2) Estimation before computing — if the estimate and answer are far apart, something went wrong. Red flag: 47 × 23 where students write 1,421 (common error: not shifting the tens row). During guided examples, ask for the estimate first before any student writes a digit.`,
        'geometry-area-perimeter': `Lesson-specific checkpoint — check FOR: (1) Correct formula selection — area vs. perimeter. Common error: using A = l × w for perimeter. (2) Correct units: perimeter answers need linear units; area answers need square units. Red flag: any answer without units — this signals the student has not connected the formula to what it measures. Ask: "What does 24 cm² mean? Could you point to it on your diagram?"`,
        'percentages': `Lesson-specific checkpoint — check FOR: (1) Correct decimal conversion (20% → 0.20, not 0.2÷10). (2) Common error: 20% of 80 = 80 ÷ 20 = 4 instead of 0.20 × 80 = 16. During "show me": always ask for an estimate first — "Is 20% of 80 closer to 4 or 16?" A student who says 4 hasn't built number sense around percents.`,
        'integers': `Lesson-specific checkpoint — check FOR: (1) Sign errors — students frequently write −3 + (−4) = 1 (treating addition as subtraction) or −3 − (−4) = −7 (not applying "add the opposite"). (2) During whiteboard check: require a number line or counter model alongside the equation. A correct answer with no model means the student may be guessing using a memorized rule they don't understand.`,
        'algebra-expressions': `Lesson-specific checkpoint — check FOR: (1) Students correctly identifying like terms before combining (3x and 5x can combine; 3x and 5 cannot). (2) Students applying the distributive property completely — 3(x + 4) = 3x + 12, not 3x + 4. Red flag: any student who distributes only to the first term. During "show me": ask students to underline all like terms before writing a single number.`,
        'ratios-proportions': `Lesson-specific checkpoint — check FOR: (1) Students setting up ratios with matching units in the same position (top and bottom). (2) Cross-multiplying correctly — students frequently multiply the wrong pair. (3) Students interpreting the solution in context, not just writing a number. Ask: "Your answer is 12 — what does 12 represent in this situation?" A student who can't answer has found the number but not the meaning.`,
        'data-statistics': `Lesson-specific checkpoint — check FOR: (1) Mean errors: students dividing by the wrong n (counting only non-zero values, or forgetting a data point). (2) Median errors: students not ordering the data before finding the middle. (3) Mode errors: students listing the number that appears "most" without checking all values. Ask students to narrate their steps aloud during the guided check — errors become audible before they become written.`,
      };
      return checks[type] ??
        `Lesson-specific checkpoint: after each worked example, pose a parallel problem on mini-whiteboards. All boards up simultaneously. Look for: correct setup (not just correct answer), appropriate labels, evidence of an estimate. If more than 30% show the same setup error, pause and address it before continuing — a wrong procedure will compound across every subsequent problem.`;
    }

    if (subject === 'English Language Arts') {
      const type = elaTopic(ctx.tl);
      const checks: Partial<Record<ELATopic, string>> = {
        'main-idea': `Lesson-specific checkpoint — check FOR: (1) Students stating a main idea, not a topic — "monarch butterflies" is a topic; "monarch butterflies make one of nature's most extraordinary migrations" is a main idea. (2) Main idea statements that apply to the WHOLE text, not just one paragraph. Red flag: any main idea that could be a sentence from only one section. During guided reading: ask "Does this idea appear in every paragraph, or just one?" before accepting any answer.`,
        'inference': `Lesson-specific checkpoint — check FOR: (1) Every inference paired with a QUOTED word or phrase — paraphrases don't count as text evidence. (2) Inferences that go beyond what the text says without going past what the text supports. Red flag: "She was angry because of her mom" with no quoted support — that's a guess, not an inference. Ask: "Show me the exact words that led you to that conclusion." Students who point to the text understand; students who shrug do not.`,
        'opinion-argument': `Lesson-specific checkpoint — check FOR: (1) Claims that are arguable — "dogs are better than cats" is arguable; "dogs can be good pets" is not. (2) Evidence that is relevant (supports the specific claim). (3) Reasoning that explains the connection — not just "this shows my claim is right." Red flag: a reasoning sentence that only restates the evidence without explaining HOW it proves the claim.`,
        'figurative-language': `Lesson-specific checkpoint — check FOR: (1) Students naming the TYPE correctly. (2) Students explaining both literal and figurative meaning. (3) Students explaining the EFFECT — why the author chose this device. Red flag: "it's a metaphor" with no further explanation — that's identification, not analysis. Require a complete response: "This is a [type] because [how it works]. The author used it to [effect on reader]."`,
        'narrative': `Lesson-specific checkpoint — check FOR: (1) Students identifying the CLIMAX as the turning point — not just the most exciting moment. Ask: "After this point, does the conflict get better, worse, or resolved? If yes, this is probably the climax." (2) Students distinguishing internal from external conflict with specific evidence. Red flag: a student who places the climax at the end of the story has confused climax with resolution.`,
        'grammar-sentences': `Lesson-specific checkpoint — check FOR: (1) Fragment identification — students should name WHAT is missing (subject, verb, or complete thought), not just call it "wrong." (2) Run-on correction — at least two different strategies attempted. Red flag: a student who corrects every run-on the same way (all periods, or all commas + conjunctions) hasn't internalized multiple strategies. Require students to label the correction method used.`,
        'vocabulary-context': `Lesson-specific checkpoint — check FOR: (1) Students naming the TYPE of context clue (definition, synonym, antonym, example) — not just the definition of the word. (2) Students pointing to the specific word or phrase in the sentence that served as the clue. Red flag: "I just knew what it meant" — that means the student already knew the word and didn't use a context clue strategy. Push back: "Which words in the sentence gave it away?"`,
        'authors-purpose': `Lesson-specific checkpoint — check FOR: (1) Students identifying purpose AND citing a specific text feature, word choice, or structural pattern that reveals it. Red flag: "I could tell because it sounded persuasive" — that's a feeling, not evidence. Require: "The author's purpose is ___ because [specific quote or structural feature]." During guided reading, cold-call students and insist on both parts of the answer.`,
        'theme': `Lesson-specific checkpoint — check FOR: (1) Theme stated as a universal message (complete sentence), not a one-word topic. "Friendship" is a topic; "True friendship means standing by someone when it costs you something" is a theme. (2) At least two pieces of specific text evidence (character action, dialogue, event) cited in support. Red flag: any theme statement that only applies to the specific story (not a universal truth about human experience).`,
      };
      return checks[type] ??
        `Lesson-specific checkpoint: before releasing to independent work, ask students to find and quote one piece of text evidence that supports today's key concept. Students who can't locate specific evidence (not just paraphrase) need teacher support before working independently.`;
    }

    if (subject === 'Science') {
      const type = sciTopic(ctx.tl);
      const checks: Partial<Record<ScienceTopic, string>> = {
        'food-chains': `Lesson-specific checkpoint — check FOR: (1) Energy arrows pointing FROM prey TO predator (energy flows up the chain from producers). Common error: arrows drawn in the direction of "eats" (predator → prey). (2) Students who can trace energy from the sun through at least three steps — not just list organisms. (3) Cascade prediction: "If rabbits disappeared, what happens to foxes? To grass?" Students should name specific organisms and explain the direction of change (increase/decrease), not just say "things change."`,
        'cells': `Lesson-specific checkpoint — check FOR: (1) Students correctly distinguishing plant from animal cells by citing specific structures (cell wall, chloroplasts, central vacuole) — not just by shape. (2) Organelle–function connections: "nucleus = control center" is memorized; "the nucleus directs cell activity by sending instructions through RNA" is understood. (3) Common lab error: students call the large central vacuole "the nucleus." Pause and address whole-class if this appears in more than two groups before microscope work continues.`,
        'forces-motion': `Lesson-specific checkpoint — check FOR: (1) Students who apply F = ma correctly identify which value is unknown and rearrange before computing. Common error: "F = ma so a = F + m" (adding instead of dividing). (2) Force diagrams: arrows must be proportional in length to magnitude. (3) Students who can explain the difference between balanced forces (no change in motion) and unbalanced (acceleration) using specific numbers from today's examples — not just repeating definitions.`,
        'water-cycle': `Lesson-specific checkpoint — check FOR: (1) The WHY behind condensation — "it got cold" is incomplete; "cooler air holds less water vapor, so excess moisture condenses into droplets" is the scientific explanation. (2) Students correctly sequencing the cycle in a closed system (the sealed bag). Ask: "What is the 'cloud' inside the bag, and what process created it?" — students who can name condensation AND explain the particle-level reason understand the mechanism, not just the label.`,
        'scientific-method': `Lesson-specific checkpoint — check FOR: (1) Hypotheses in if–then format WITH a causal explanation — "if we increase ramp height, the car travels farther because gravity pulls it with greater effect over a larger drop." Reject hypotheses that predict without explaining. (2) Variables: students must name the independent variable (what they change), the dependent variable (what they measure), AND at least two controlled variables. Red flag: a student who lists "everything else" as controlled hasn't identified specific variables.`,
        'ecosystems': `Lesson-specific checkpoint — check FOR: (1) Students correctly classifying biotic vs. abiotic — common error: calling sunlight or water "biotic." (2) Students who can explain DEPENDENCY, not just categorize: "What would happen to all biotic factors in this ecosystem if rainfall dropped by 80%?" A student who answers with a specific food web cascade (less water → grass dies → herbivores decline → carnivores follow) understands the system, not just the vocabulary.`,
        'adaptations': `Lesson-specific checkpoint — check FOR: (1) Students distinguishing structural (physical feature) from behavioral (action/pattern) adaptations with specific examples. (2) The causal chain: "The [adaptation] helps the organism survive because [specific environmental challenge it addresses]." Red flag: "polar bears are white to blend in" without explaining what they're hiding from or hunting. Push for specificity: "Blend in from what? Why does that matter for survival?"`,
      };
      return checks[type] ??
        `Lab checkpoint: at a predetermined mid-point, reconvene the class and ask 2–3 groups to share their data or observations. Identify any systematic errors — wrong measurement technique, missed variables, mislabeled data — before groups continue. If a critical error appears across multiple groups, address it whole-class using one correct group's approach as a model.`;
    }

    // Social Studies / Other
    return `Lesson-specific checkpoint: during guided source analysis, require students to quote specific words or phrases from the source — not describe it in general terms. "The source suggests the colonists were angry" is insufficient; "The source states 'taxation without representation is tyranny,' which shows that colonists viewed British taxes as a violation of their rights" demonstrates actual engagement with the text. Circulate and redirect any student who summarizes rather than cites before releasing to independent work.`;
  })();

  // Universal monitoring strategies — apply to every lesson regardless of topic
  const universal = [
    `Circulate actively during guided practice — target 5–8 specific students for brief verbal checks and take anecdotal notes on both the error type and which students are making it. This drives your small-group reteach decisions.`,
    `Adjust pacing in real time: if more than 30% of the class shows the same misconception, pause and reteach that specific step before continuing. Use a correct student's explanation (not just your own re-explanation) when possible — peer explanation often reaches students more effectively.`,
    subject === 'Science'
      ? `Before releasing to independent work: ask 2–3 students to explain the procedure or scientific principle back to you in their own words. Any critical step missing from their explanation signals a gap that must be addressed whole-class before the investigation proceeds.`
      : subject === 'Math'
        ? `"Show me" parallel check: after each worked example, pose a parallel problem. All students solve on whiteboards and hold up simultaneously — this prevents the "I understand when you do it" illusion and surfaces the gap between watching and doing.`
        : `Before independent work: cold-call students to explain the concept in their own words. A student who can paraphrase accurately understands; a student who restates your exact phrasing may be echoing rather than understanding.`,
  ];

  return [checkpoint, '', ...universal].join('\n');
}

// ── ELA differentiation — routes by detected topic, mirrors Math pattern ────────

function generateELADifferentiation(ctx: Ctx): string {
  const { isHigh } = ctx;
  const type = elaTopic(ctx.tl);

  // support[] = scaffolding for students who need more; enrich[] = extension for mastery students
  const maps: Partial<Record<ELATopic, { support: string[]; enrich: string[] }>> = {
    'main-idea': {
      support: [
        'Provide a two-part graphic organizer: "This text is about: ___" (topic) → "The most important thing the author says: ___" (main idea). Students complete both before writing a combined sentence.',
        'Pre-highlight one supporting detail per paragraph so students focus on the relationship between details and the main idea, not on locating them.',
        'Offer a partially annotated paragraph where the first supporting detail is already circled and labeled — students find and label the remaining details.',
        'Allow students to state the main idea verbally to a partner first before writing it, to reduce the writing barrier.',
      ],
      enrich: [
        'Students identify a SECOND possible main idea for the same text and write a paragraph arguing which interpretation is better supported by the evidence.',
        "Students find an implied main idea in a passage where the author never directly states it — then write the 'missing' main idea sentence as the author should have written it.",
        isHigh ? 'Students write their own informational paragraph, then exchange with a partner who must find the main idea and cite supporting details — authors evaluate whether their writing was clear.' : 'Students draw a "main idea umbrella" for a new text: one idea at the top, three supporting details as the spokes below.',
      ],
    },
    'inference': {
      support: [
        'Provide printed "evidence cards" — key sentences from the text — that students physically sort and match to a set of inference cards. Sorting before writing reduces the blank-page barrier.',
        'Offer the sentence frame: "The text says \'___.\' This makes me think ___ because I know that ___." Require all three parts to count as a complete inference.',
        'Pre-model one complete inference in writing on the handout — students use it as an anchor example while completing the remaining inferences independently.',
        'Allow students to highlight the exact text evidence BEFORE writing the inference — this builds the habit of returning to the text first.',
      ],
      enrich: [
        'Students find two places in the text where different readers could draw competing inferences from the same evidence. They write both inferences and argue which is better supported.',
        isHigh ? 'Students write a "counter-inference" for each inference they made — a plausible interpretation of the same evidence that reaches a different conclusion — then explain why their original inference is stronger.' : 'Students rate each inference on a 1–3 evidence scale (1 = thin support, 3 = strong support) and explain their rating in one sentence.',
        'Students select the strongest inference from the class discussion, draft a paragraph defending it with quoted evidence, and read it aloud to a small group for feedback.',
      ],
    },
    'opinion-argument': {
      support: [
        'Provide a pre-written claim. Students only need to select the best evidence from a given list and write the reasoning that connects it. This isolates the reasoning step — the hardest part.',
        'Offer a sentence frame for reasoning: "This evidence shows ___ because ___. Without [evidence], we cannot be sure that [claim]." Students fill in the blanks before writing in full sentences.',
        'Provide an "argument sandwich" graphic organizer: Claim (top bread) → Evidence (filling) → Reasoning (bottom bread). Students build the sandwich before drafting.',
        'Allow students to argue orally with a partner first. Partners record the best reasons — then students select from their partner\'s notes to build the written argument.',
      ],
      enrich: [
        'Students write a full counterargument paragraph for the opposing side, then write a rebuttal that directly addresses each counterargument. Both must use specific evidence.',
        isHigh ? 'Students read two opposing editorials on the same topic, identify each author\'s claim and strongest evidence, then evaluate in writing which argument is more logically sound and why.' : 'Students write a "steel man" of the opposing view — the strongest possible version of the argument they disagree with — then explain what it would take to change their mind.',
        'Students create a peer "argument audit": trade papers, check that every claim is arguable, every piece of evidence is relevant, and every reasoning sentence explains a connection. Return with written feedback.',
      ],
    },
    'figurative-language': {
      support: [
        'Provide a two-column matching activity: figurative language examples on the left, device types and literal meanings on the right. Students match before writing original examples.',
        'Offer an "identify + explain" template: "Type: ___ | Literal meaning: ___ | Figurative meaning: ___ | Effect: ___." Students complete one column at a time, not the whole analysis at once.',
        'Give students a list of sentence starters for writing original figurative language: "As ___ as ___" (simile), "___ IS a ___" (metaphor), "The ___ [verb]ed like a person who ___" (personification). Remove the blank-page barrier.',
        'Pre-highlight figurative language in the shared text — students only need to identify the type and explain the meaning, not locate the examples.',
      ],
      enrich: [
        'Students write a single paragraph about any topic, deliberately using five different types of figurative language, then underline each and annotate why they chose it and what effect it creates.',
        isHigh ? 'Students find three examples of figurative language in song lyrics, advertisements, or speeches not from class. They identify each type, analyze the intended audience, and evaluate how effectively the device achieves its purpose.' : 'Students rewrite a passage from a class text, replacing all figurative language with literal language, then explain in a short paragraph what was lost in the translation.',
        'Students design a "Figurative Language Field Guide" — an illustrated reference card with one original example of each device, the literal translation, and the effect. This becomes a classroom resource.',
      ],
    },
    'narrative': {
      support: [
        'Provide a pre-filled story arc organizer with the exposition already labeled. Students identify and label only the remaining four elements (rising action events, climax, falling action, resolution).',
        'Offer a "conflict identification card": three sentence starters — "The external conflict is ___ vs. ___," "The internal conflict is that the character wants ___ but ___," and "The conflict is resolved when ___." Students complete all three.',
        'Allow students to use sticky notes to physically mark the story arc on a printed text before labeling — the kinesthetic step supports abstract reading comprehension.',
        'Provide a plot diagram with dotted-line brackets showing approximate page ranges for each section, so students focus on analysis rather than locating the sections.',
      ],
      enrich: [
        'Students rewrite the climax of today\'s mentor text with a different turning point, then explain in writing how every rising action event would need to change to make the new climax believable.',
        isHigh ? 'Students analyze two texts with the same theme and compare how each author constructs their climax — then argue in writing which climax is more effective and why.' : 'Students write the "deleted scene" — a scene that would fit between two events in the story but was never written. It must be consistent with character motivation and advance the existing plot.',
        'Students write a narrative opening (six sentences) that introduces a protagonist, establishes a setting, and creates immediate tension — with no backstory and no direct statement of the conflict.',
      ],
    },
    'grammar-sentences': {
      support: [
        'Provide a color-coded reference card: simple = green, compound = blue, complex = yellow, compound-complex = orange. Students color-highlight a passage before labeling sentence types.',
        'For fragment identification: students circle the subject (who?) and underline the verb (does what?) in each group of words. If either is missing or if there\'s no complete thought, it\'s a fragment.',
        'Provide a run-on repair menu with four numbered strategies: (1) add a period, (2) add a semicolon, (3) add a comma + FANBOYS conjunction, (4) add a subordinating conjunction. Students number which strategy they used for each repair.',
        'Allow students to build sentences using pre-printed subject, verb, and clause cards that snap together physically before writing — the manipulation step supports syntax understanding.',
      ],
      enrich: [
        'Students take a paragraph from their own writing and deliberately revise it to include at least one of each sentence type (simple, compound, complex, compound-complex). They label each type in the margin and write a one-sentence note about the stylistic effect of each choice.',
        isHigh ? 'Students analyze a paragraph from a published author, identify every sentence type, and write a response explaining how the author\'s sentence variety creates pacing, emphasis, or tone — with specific examples quoted.' : 'Students write a 6-sentence paragraph about any topic where consecutive sentences intentionally vary in type: simple, compound, complex, compound, simple, complex. They label each.',
        'Students create a "Sentence Repair Workshop" — they intentionally write five fragments and five run-ons, then exchange with a partner. Partners correct each error and label the correction strategy used.',
      ],
    },
    'vocabulary-context': {
      support: [
        'Provide a context clue strategy card with signal words highlighted by type: definition ("is," "means," "refers to"), synonym ("also called," "or," "in other words"), antonym ("however," "unlike," "but"), example ("such as," "including," "for example"). Students scan for signal words before reading for meaning.',
        'For each target sentence, pre-underline the context clue phrase so students focus on interpreting the clue — not locating it.',
        'Offer a four-column organizer: Unknown Word | Context Clue Type | Clue Words | Probable Meaning. Students complete one column at a time.',
        'Allow students to work in pairs — one student finds the clue word, one student writes the meaning. Rotate roles after each word.',
      ],
      enrich: [
        'Students write original sentences for a partner, embedding a specific context clue type (assigned by the teacher) for each of four vocabulary words. Partners must identify both the word meaning AND the clue type used — authors confirm or correct.',
        isHigh ? 'Students deconstruct five multi-syllabic words using prefix + root + suffix analysis and context clues simultaneously — comparing what each method predicts and evaluating which is more reliable for that word.' : 'Students create an "Unknown Word Toolkit" — a personal reference card listing signal words for each of the four context clue types, with one original example sentence they wrote for each type.',
        'Students find five unfamiliar words in their independent reading book, apply a context clue strategy to each, then look up the actual definition and rate how accurate the strategy was (scale of 1–3). They write a reflection on which clue type they find most reliable.',
      ],
    },
    'authors-purpose': {
      support: [
        'Provide the PIE anchor chart (Persuade, Inform, Entertain) with a signal-word column for each: persuade = "should," "must," "you need to"; inform = "research shows," "scientists found," "defined as"; entertain = dialogue, descriptive language, humor.',
        'Pre-select one sentence from the text and label its purpose (e.g., "This is an Inform sentence — the author uses the word \'research shows\'"). Students use this as an anchor to analyze two more sentences independently.',
        'Allow students to code the text with P, I, or E in the margin before writing any full sentences — reduces the writing barrier while building the skill.',
        'Provide a sentence frame: "The author\'s purpose is to ___ because the text uses ___ [text feature/word choice], which is a signal that the author wants to ___."',
      ],
      enrich: [
        "Students read two texts on the same topic — one to persuade, one to inform. They compare how each author's word choice, structure, and evidence selection shifts based on purpose, and write a paragraph arguing which approach is more effective for a specific audience.",
        isHigh ? "Students write the same content in two different purposes — for example, write a paragraph about school lunch policy once to inform and once to persuade. Then they analyze in writing what specific choices they changed and why." : "Students find one real-world example of each purpose (P, I, E) outside of school — an article, an ad, a story — and explain in one paragraph per example how the text signals its purpose.",
        "Students evaluate whether their assigned text successfully achieved its purpose: 'Did the author actually persuade/inform/entertain you? Which techniques worked, and which didn't?' They write a 3–5 sentence evaluation using specific textual evidence.",
      ],
    },
    'summarizing': {
      support: [
        'Provide a "somebody-wanted-but-so-then" graphic organizer for narrative texts, or a "main idea + 3 key details" organizer for informational texts. Students complete the organizer first, then convert it to sentences.',
        'Pre-number the paragraphs. Students write one word that captures the topic of each paragraph before writing any summary sentences — the list of words becomes the skeleton of their summary.',
        'Provide a list of 10 sentences from the text — 5 important and 5 minor details — mixed together. Students circle the 5 important ones before writing the summary, so the task becomes selection rather than generation.',
        'Allow students to dictate the summary to a partner or record it verbally before writing — the spoken version often flows more naturally than a first written attempt.',
      ],
      enrich: [
        'Students write three summaries of the same text at different word limits: 50 words, 25 words, 10 words. Each compression requires a decision about what truly matters — and the 10-word version reveals their deepest understanding.',
        isHigh ? 'Students compare their summary to a classmate\'s and identify every point of agreement and disagreement. For each disagreement, they argue in writing which is more important — the point they included or the point their partner included.' : 'Students annotate their own summary: underline the main idea sentence, circle each key detail, and check that every sentence connects to the main idea. Revise anything that doesn\'t.',
        'Students summarize the same text from two different perspectives (e.g., protagonist and antagonist, or scientist and citizen) — then explain what each perspective chose to emphasize and why.',
      ],
    },
    'theme': {
      support: [
        'Provide an anchor chart with the difference between topic (one word or short phrase) and theme (a complete sentence with a universal message). Include three examples: Topic: Loyalty → Theme: True loyalty means choosing honesty over comfort.',
        'Offer a theme-building frame: "In this story, [character] learns that [universal truth about life]. This matters beyond the story because [real-world connection]."',
        'Pre-identify one piece of evidence (a specific character action or line of dialogue) and explain how it connects to the theme. Students find and connect two more pieces using the same pattern.',
        'Allow students to talk through the theme with a partner using the frame before writing it independently.',
      ],
      enrich: [
        'Students find a second possible theme in the same text and write a paragraph arguing which theme is better supported — using at least three specific pieces of text evidence for each.',
        isHigh ? 'Students compare the themes of two texts they have read this year, evaluate which text communicates its theme more effectively, and write a structured comparison paragraph with textual evidence from both.' : 'Students write their own short narrative (8–10 sentences) that expresses the same theme from a different situation — the theme must emerge from character choices, not be stated directly.',
        'Students write a "theme defense" — a structured paragraph arguing that their identified theme is the central message, not just a minor one, using three pieces of evidence ranked from least to most convincing.',
      ],
    },
    'compare-contrast': {
      support: [
        'Pre-fill one column of the Venn diagram or T-chart with teacher-selected evidence. Students complete the second column and the overlapping section independently.',
        'Provide transition word cards: "Similarly, ___ and ___ both..." | "However, ___ while ___..." | "In contrast, ___..." | "One key difference is that ___..." Students select and complete the most appropriate frame for each comparison point.',
        'Allow students to color-code the text first — highlight similarities in yellow, differences in blue — before filling in any organizer.',
        'Limit the task: students identify the TWO most important similarities and the TWO most important differences before attempting a written paragraph.',
      ],
      enrich: [
        'Students evaluate which difference is most SIGNIFICANT — not just the most obvious — and write a paragraph arguing why that difference matters more than the others.',
        isHigh ? 'Students write a compare-contrast essay that moves beyond surface features (what they look like) to deeper analysis (how their purposes, impacts, or meanings differ) — with specific evidence for every claim.' : 'Students create a visual comparison (side-by-side diagram, illustrated chart) that makes the most important similarities and differences immediately visible to a reader who hasn\'t read the text.',
        'Students compare something from today\'s text to something from their own life or independent reading, finding at least two genuine similarities and explaining what each comparison reveals about both subjects.',
      ],
    },
    'poetry': {
      support: [
        'Pre-mark the rhyme scheme on the first stanza with letters (A, B, A, B) — students identify and mark the scheme for subsequent stanzas independently.',
        'Provide a sensory detail table: See | Hear | Smell | Taste | Touch. Students list at least one image per row from the poem before discussing imagery as a whole.',
        'Annotate one poetic device in the text with its name and a brief explanation (e.g., "This is personification — the moon is described as if it can watch and wait."). Students identify two more devices using the annotation as a model.',
        'Read the poem aloud twice — once for feeling, once for structure. Students close their eyes during the first reading and note emotional responses before analyzing technical elements.',
      ],
      enrich: [
        'Students rewrite a stanza of the class poem in prose, then compare the two versions in writing: what is gained in the prose version? What is lost? What does this reveal about why the poet chose the line breaks and compressed language they did?',
        isHigh ? 'Students research one poetic device used in today\'s poem (e.g., extended metaphor, enjambment, anaphora) and find three published poems that use the same device. They write a comparative analysis of how each poet\'s use differs.' : 'Students write their own 8–12 line poem on any topic using at least three devices from today\'s lesson. They annotate each device in the margin with its name and a sentence about the intended effect.',
        'Students perform a "slow read" of a new poem: they read one line at a time, stop, predict what the next line might do, read it, and note whether their prediction was right. They record five predictions and five actual lines, then reflect on what the gaps reveal about the poem\'s craft.',
      ],
    },
  };

  const topicData = maps[type];
  if (topicData) {
    const { support, enrich } = topicData;
    return [
      'SUPPORT STRATEGIES:',
      ...support.map(s => `• ${s}`),
      '',
      'EXTENSION / ENRICHMENT STRATEGIES:',
      ...enrich.map(s => `• ${s}`),
    ].join('\n');
  }

  // Generic ELA fallback when topic isn't in the map
  return [
    'SUPPORT STRATEGIES:',
    `• Provide a graphic organizer that breaks today's skill into two or three explicit steps — students complete each step before moving to the next.`,
    `• Pre-highlight key sections of the text so students focus on the most relevant evidence rather than the full passage.`,
    `• Offer sentence frames that name the specific skill: provide language that mirrors the structure of a strong response for today's task.`,
    `• Allow students to respond verbally to a partner before writing — the spoken version reveals thinking that the writing barrier can hide.`,
    '',
    'EXTENSION / ENRICHMENT STRATEGIES:',
    `• Students apply today's skill to a second, more complex text not used in class — then compare how the two texts required different strategies.`,
    `• Students create a reference resource (annotated example, illustrated guide, or teaching script) that explains today's skill for a student who was absent.`,
    `• Students write a metacognitive reflection: "When is this skill easy for me? When is it hard? What exactly makes the hard cases harder?"`,
  ].join('\n');
}

function generateDifferentiation(ctx: Ctx): string {
  const { subject, topic } = ctx;

  if (subject === 'Math') return generateMathDifferentiation(ctx);
  if (subject === 'English Language Arts') return generateELADifferentiation(ctx);

  if (subject === 'Science') {
    const type = sciTopic(ctx.tl);
    const sciSupport: Partial<Record<ScienceTopic, string[]>> = {
      'food-chains': [
        'Provide pre-labeled role cards (Sun, Producer, Herbivore, Carnivore, Decomposer) with a sentence on the back describing energy input and output for that role.',
        'Offer a partially completed food web diagram — two organisms already placed and connected. Students add at least three more organisms with labeled arrows.',
        'Provide a sentence frame for cascade predictions: "If ___ disappears, then ___ will [increase/decrease] because ___."',
        'Allow students to act out the food web simulation before drawing it — the physical experience anchors the abstract energy-flow concept.',
      ],
      'cells': [
        'Provide a pre-labeled cell diagram with three organelles already identified. Students add and label the remaining organelles using their notes.',
        'Offer an "organelle job description" reference card: each organelle is described as a job role (e.g., "Mitochondria — Power Plant: generates energy for the cell from nutrients"). Students match organelles to functions before the diagram.',
        'Allow students to draw and label the cell diagram from their microscope observation rather than from memory during independent practice.',
        'Provide a two-column T-chart (Plant Cell | Animal Cell) with three rows pre-filled. Students complete the remaining rows.',
      ],
      'forces-motion': [
        'Provide a worked F = ma example with all steps labeled: "Step 1: Write the formula. Step 2: Identify what you know. Step 3: Substitute. Step 4: Solve. Step 5: Include units."',
        'Offer a force diagram template with arrows already drawn at right angles — students label the arrows with the force name and magnitude.',
        'Allow students to use a balance scale model (physical or illustrated) during guided practice to connect the balanced/unbalanced forces concept to a concrete representation.',
        'Provide a "real life to science" reference: a seatbelt image with the inertia label, a ramp image with friction labeled — students connect concept to example before working independently.',
      ],
      'water-cycle': [
        'Provide a labeled water cycle diagram with the four processes already named. Students only need to draw the arrows and add two observation notes per process.',
        'Offer sentence starters for the bag observation: "The droplets on the inside of the bag were formed by ___ because ___."',
        'Allow students to sequence a set of printed "water molecule journey" cards before writing the sequence in their own words.',
        'Provide a vocabulary reference card with condensation, evaporation, precipitation, and collection defined in student-friendly language with diagram icons.',
      ],
      'scientific-method': [
        'Provide a hypothesis template: "If [independent variable], then [dependent variable], because [scientific reasoning]." Students fill in each bracket rather than writing from scratch.',
        'Offer a "variable sorting card" activity: students sort printed variable cards into "independent," "dependent," and "controlled" columns before writing.',
        'Provide a partially completed data table — column headers and row labels already filled in. Students focus on collecting and recording data, not designing the table structure.',
        'Pre-number procedure steps 1–5. Students fill in the action for each step rather than sequencing from scratch.',
      ],
    };

    const sciEnrich: Partial<Record<ScienceTopic, string[]>> = {
      'food-chains': [
        `Design a food web for a different biome (Arctic, coral reef, or rainforest). Label every organism's role, draw energy-flow arrows, and write a paragraph predicting what would happen if the top predator was removed.`,
        'Research the actual 10% energy transfer rule: calculate how many kilograms of grass it takes to support one lion, working backward through the food chain. Show all calculations.',
        `Create a "Species Impact Report": choose one organism from today's food web and research its real-world conservation status. Write two paragraphs explaining how its decline would affect the web — and what would need to change to prevent a cascade.`,
      ],
      'cells': [
        'Design an "organelle analogy" for a system OTHER than a factory or school. The analogy must include at least seven organelles, and every connection must be scientifically accurate.',
        'Research a real cellular disorder (cancer, sickle cell anemia, or mitochondrial disease) and write a paragraph explaining which organelle is affected, what goes wrong at the cellular level, and how scientists are working to address it.',
        `Compare prokaryotic cells to the eukaryotic cells studied today. Create a three-column chart (Plant Cell | Animal Cell | Prokaryote) and write a paragraph explaining what the differences reveal about the evolution of complexity.`,
      ],
      'forces-motion': [
        `Apply Newton's three laws to the design of a safety system (seatbelt, airbag, or crumple zone). Write a paragraph explaining which law each component addresses and why engineers had to account for it.`,
        'Calculate the force needed to accelerate three different objects to the same speed. Show all work using F = ma and explain why the results differ.',
        `Research a real engineering challenge where forces needed to be carefully calculated (a bridge, spacecraft re-entry, or sports equipment design). Write a one-page explanation of the forces involved and how engineers addressed them.`,
      ],
    };

    const support = sciSupport[type] ?? [
      `Provide a vocabulary reference card with illustrated definitions for today's key terms and labeled diagrams.`,
      'Pair with a supportive partner during lab; assign specific, bounded roles (recorder, materials manager, measurer).',
      'Offer a partially completed lab sheet with sentence starters for the analysis and conclusion questions.',
      'Allow students to draw and label diagrams instead of writing full sentences for initial observations, then convert diagrams to sentences.',
    ];
    const enrich = sciEnrich[type] ?? [
      `Design a follow-up investigation that extends today's experiment with a new independent variable. Write a complete hypothesis, procedure, and blank data table.`,
      `Research a current scientific discovery related to today's concept and write a one-paragraph summary explaining the connection to what we learned in class.`,
      `Create an "Expert Explainer" — a visual guide (illustrated step-by-step, annotated diagram, or comic strip) that teaches today's concept to a student who was absent, including all key vocabulary.`,
    ];

    return [
      'SUPPORT STRATEGIES:',
      ...support.map(s => `• ${s}`),
      '',
      'EXTENSION / ENRICHMENT STRATEGIES:',
      ...enrich.map(s => `• ${s}`),
    ].join('\n');
  }

  if (subject === 'Social Studies') {
    const type = ssTopic(ctx.tl);
    const ssSupport: Partial<Record<SSTopic, string[]>> = {
      'american-revolution': [
        'Provide an annotated version of the Declaration of Independence excerpt with difficult phrases defined in context (e.g., "unalienable rights" = rights that cannot be taken away).',
        'Offer a cause-and-effect graphic organizer with "British Action" on the left and "Colonial Response" on the right — three rows pre-filled, students complete three more.',
        'Provide a timeline with major events labeled from 1763–1783. Students add two events they researched and annotate each with a sentence explaining its significance.',
        'Allow students to choose their viewpoint (Patriot or Loyalist) and fill in a structured argument card before writing any full-paragraph response.',
      ],
      'civil-war': [
        'Provide a three-column graphic organizer (Political | Economic | Social) with one cause already placed in each column. Students add at least two more per column.',
        'Offer a primary source excerpt with vocabulary support: difficult words underlined with definitions in the margin.',
        'Provide a timeline of major battles and legislation from 1861–1865. Students rank the three events they think were most significant and justify each ranking in one sentence.',
        'Allow students to use a "Then / Now" reference card linking Civil War legislation to present-day context — this helps students who struggle to connect historical events to significance.',
      ],
      'civil-rights': [
        'Provide a sequence graphic organizer of five key events from the movement with dates and blank "significance" boxes. Students complete one box at a time.',
        'Offer an annotated primary source excerpt (speech, letter, or news article) with annotations identifying the speaker\'s purpose and intended audience.',
        'Allow students to identify the event they find most significant and fill in a structured response frame before writing a full paragraph.',
        'Provide a "Strategy Analysis Card" — nonviolent resistance strategies (sit-ins, boycotts, marches) with a brief description of each and the specific result it achieved.',
      ],
    };
    const ssEnrich: Partial<Record<SSTopic, string[]>> = {
      'american-revolution': [
        `Find a primary source written by a Loyalist during the Revolution. Compare their argument to a Patriot source from the same period. Write a paragraph explaining what each valued most and why their conclusions differed.`,
        `Connect one cause of the American Revolution to a modern political event where citizens have disputed government authority. Write 2–3 paragraphs explaining the parallel and where it breaks down.`,
        `Write a speech as either a Patriot delegate or a Loyalist merchant at a colonial town meeting in 1775. The speech must include at least three historically accurate arguments and acknowledge the strongest counterargument.`,
      ],
      'civil-war': [
        `Research an enslaved person or free Black American during the Civil War era (Harriet Tubman, Frederick Douglass, or Robert Smalls). Write a narrative account of how the war intersected with their life — using at least one primary source.`,
        `Compare Lincoln's position on slavery in 1858 (Lincoln-Douglas debates) to his position in 1863 (Emancipation Proclamation). Analyze what changed and what factors drove the shift.`,
        `Evaluate: was the Civil War primarily fought over slavery or states' rights? Write a structured argument with at least three pieces of historical evidence, addressing the strongest counterargument.`,
      ],
    };

    const support = ssSupport[type] ?? [
      'Provide an annotated version of the primary source with difficult words defined in context.',
      'Offer a graphic organizer with guiding questions to scaffold document analysis (who wrote it? when? why? what is the key argument? what evidence supports it?).',
      'Allow use of a timeline reference sheet during source analysis so students focus on historical interpretation rather than memorizing dates.',
      'Pair with a supportive reader during primary source work; student contributes ideas verbally while the partner records.',
    ];
    const enrich = ssEnrich[type] ?? [
      `Research a primary source not discussed in class that offers a different perspective on today's topic. Write a comparative analysis: what does this source add to our understanding?`,
      `Draw connections between today's historical event or concept and a current world event, explaining the parallel in 2–3 paragraphs and noting where the parallel breaks down.`,
      `Create a "Then and Now" visual: research specifically how today's topic shaped the present day, using at least one researched fact to anchor the connection.`,
    ];

    return [
      'SUPPORT STRATEGIES:',
      ...support.map(s => `• ${s}`),
      '',
      'EXTENSION / ENRICHMENT STRATEGIES:',
      ...enrich.map(s => `• ${s}`),
    ].join('\n');
  }

  // Other subjects
  return [
    'SUPPORT STRATEGIES:',
    `• Provide sentence frames, visual aids, or step-by-step reference cards specific to today's task.`,
    `• Break the task into smaller numbered steps with visual cues and checkpoints so students can self-monitor progress.`,
    `• Allow partner support during independent practice — one student explains the step, the other records.`,
    `• Pre-teach the three most critical vocabulary terms in a small group before the main lesson.`,
    '',
    'EXTENSION / ENRICHMENT STRATEGIES:',
    `• Apply today's concept to an unfamiliar or real-world context not covered in the lesson — students explain the connection in writing.`,
    `• Create a teaching resource (mini-poster, illustrated guide, or video script) explaining today's concept for someone who wasn't in class.`,
    `• Design a challenge question for classmates that requires applying today's learning in a new way — include an answer key.`,
  ].join('\n');
}

function generateExtensionActivity(ctx: Ctx): string {
  const { subject, topic, isHigh } = ctx;

  if (subject === 'Math') return generateMathExtension(ctx);

  const extMap: Record<string, string> = {
    'English Language Arts': isHigh
      ? `Students who demonstrate mastery write an analytical essay or argument that builds on today's lesson: they must select two published texts that illustrate contrasting approaches to ${topic}, analyze the effect of each, and argue which is more effective. Minimum 4 paragraphs. They share their thesis and strongest evidence with a peer for feedback before submitting.`
      : `Students who finish early become "Peer Text Coaches": they select a passage from their independent reading book, annotate it applying today's skill (${topic}), then conference with a partner who hasn't read it. The coach teaches the partner what they found and why it matters. This deepens understanding through explanation.`,
    'Science': isHigh
      ? `Students who demonstrate mastery design a complete investigation proposal for a research question that extends today's work on ${topic}. The proposal must include: a clearly stated question, hypothesis (if–then format), identification of all variables, a step-by-step procedure, a data table, and a description of how they would analyze the results. They share the proposal with the class for critique.`
      : `Students who finish early create an "Expert Explainer" — a visual guide (poster, comic strip, or illustrated step-by-step) that teaches ${topic} to a student who wasn't in class. It must include all key vocabulary from today, a diagram or drawing, and at least one real-world example. Display finished work in the room.`,
    'Social Studies': isHigh
      ? `Students write a 1–2 paragraph historical argument connecting ${topic} to a current event or contemporary issue. They must cite at least two sources (one from today's class materials, one from their own research) and present their argument to the class in 2–3 minutes.`
      : `Students create a "Then and Now" comparison: research how today's topic (${topic}) connects to something in the world today. Write or draw a comparison showing what stayed the same, what changed, and why it matters. Share with a partner.`,
  };

  return extMap[subject] ??
    (isHigh
      ? `Students who demonstrate mastery create a challenge problem or extension question related to ${topic} that requires synthesis-level thinking. They write a full solution with explanation and present it to the class as a 2-minute "mini-lesson," fielding questions from classmates.`
      : `Students who finish early create a "Teaching Tool" — a poster, visual guide, or written explanation — for ${topic} that a student who missed class could use to catch up. It must include key vocabulary, a clear explanation with an example, and a practice question with the answer.`);
}

function generateHomework(ctx: Ctx): string {
  const { subject } = ctx;
  if (subject === 'Math') return generateMathHomework(ctx);
  if (subject === 'English Language Arts') return generateELAHomework(ctx);
  if (subject === 'Science') return generateScienceHomework(ctx);
  if (subject === 'Social Studies') return generateSSHomework(ctx);

  const { topic } = ctx;
  return `Review today's notes and complete any unfinished classwork related to ${topic}. Write 2–3 sentences answering: "What was the most important thing you learned today, and how does it connect to something you already knew?" Be ready to share one key takeaway or remaining question at the start of next class.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION ORCHESTRATORS
// ═══════════════════════════════════════════════════════════════════════════════

function generateVocabulary(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathVocabulary(ctx);
  if (ctx.subject === 'English Language Arts') return generateELAVocabulary(ctx);
  if (ctx.subject === 'Science') return generateScienceVocabulary(ctx);
  if (ctx.subject === 'Social Studies') return generateSSVocabulary(ctx);
  // Generic fallback: generate lesson-specific vocabulary from topic and objective
  const { topic, obj } = ctx;
  return [
    `${topic} — The central concept of today's lesson: to ${obj}. Students will build and refine this definition through examples, discussion, and practice.`,
    `Prior knowledge — What students already know that connects directly to ${topic}; the foundation today's new learning builds on.`,
    `Model — A representation, diagram, or worked example that illustrates how ${topic} works in a real context.`,
    `Evidence — Specific facts, data, examples, or observations used to explain or justify a claim about ${topic}.`,
    `Application — Using understanding of ${topic} to solve a new problem, answer a new question, or explain a new situation.`,
  ].join('\n');
}

function generateWarmUp(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathWarmUp(ctx);
  if (ctx.subject === 'English Language Arts') return generateELAWarmUp(ctx);
  if (ctx.subject === 'Science') return generateScienceWarmUp(ctx);
  if (ctx.subject === 'Social Studies') return generateSSWarmUp(ctx);
  const { topic, obj, t } = ctx;
  return `Quick Write (≈${t(0.08)} min): "Write everything you already know about ${topic}. Include any examples, questions, or connections that come to mind." Students write independently, then share with a partner. Cold-call 2–3 pairs to surface prior knowledge and misconceptions. Record key ideas on the board.

Bridge to today's lesson: "Today we are going to ${obj}. Some of what you just shared connects directly — and some ideas we may need to revisit by the end of class."

Display the learning target and read it aloud together.`;
}

function generateDirectInstruction(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathDirectInstruction(ctx);
  if (ctx.subject === 'English Language Arts') return generateELADirectInstruction(ctx);
  if (ctx.subject === 'Science') return generateScienceDirectInstruction(ctx);
  if (ctx.subject === 'Social Studies') return generateSSDirectInstruction(ctx);
  const { topic, obj, t } = ctx;
  return [
    `1. Hook and define (≈${t(0.08)} min): Introduce ${topic} with a concrete, real-world example students can visualize. State the definition in simple terms, write it on the board, and ask students to record it in their notes alongside their own paraphrase.`,
    `2. Worked Example #1 — Think-aloud (≈${t(0.10)} min): Work through the first complete example of ${topic} step by step while narrating every decision aloud. Pause before each step and ask: "What should I do next — and why?" Record the solution visibly. Students mirror the same problem on their activity sheet.`,
    `3. Worked Example #2 — Guided (≈${t(0.08)} min): Present a second example that varies one key element. Prompt students to predict each step before you demonstrate. Ask: "What changed? What stayed the same? How does that affect our work?" Students complete the matching version on their activity sheet alongside the teacher.`,
    `4. Reference display: Post the key steps, vocabulary, or decision criteria for ${topic} where students can see them throughout practice. Students copy or annotate as needed.`,
  ].join('\n');
}

function generateGuidedPractice(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathGuidedPractice(ctx);
  if (ctx.subject === 'English Language Arts') return generateELAGuidedPractice(ctx);
  if (ctx.subject === 'Science') return generateScienceGuidedPractice(ctx);
  if (ctx.subject === 'Social Studies') return generateSSGuidedPractice(ctx);
  const { topic, t } = ctx;
  return `Guided Practice — ${topic} (≈${t(0.20)} min)

Round 1 — Whole class (items 1–2): Students attempt each item on their activity sheet before the teacher reveals and explains the answer. Cold-call one student to narrate their reasoning step by step.

Round 2 — Partner work (items 3–4): Pairs work simultaneously while teacher circulates. Listen for the most common error; address it to the whole class before moving on.

Round 3 — Individual try (item 5): Each student completes independently, then compares with their partner. Any disagreement becomes a discussion: "Which answer is correct — and how do you know?"

Teacher checks for understanding before releasing students to independent practice. If more than one-third of the class shows the same misconception, pause and reteach that step with a different example of ${topic}.`;
}

function generateIndependentPractice(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathIndependentPractice(ctx);
  if (ctx.subject === 'English Language Arts') return generateELAIndependentPractice(ctx);
  if (ctx.subject === 'Science') return generateScienceIndependentPractice(ctx);
  if (ctx.subject === 'Social Studies') return generateSSIndependentPractice(ctx);
  const { topic, obj, t } = ctx;
  return `Independent Practice — ${topic} (≈${t(0.20)} min)

Students work silently and independently to ${obj}. Success criteria are posted and visible.

Teacher circulates during the first 3 minutes without helping — observe and note which students are stuck, which are succeeding, and what errors appear most often. After 3 minutes, address the most common error to the whole class with a brief reteach using a new example of ${topic}.

Support: Pull 2–3 students who need it to a small-group table. Reteach using a simpler entry point for ${topic} and gradually increase complexity.

Extension: Students who finish early write a 2–3 sentence explanation of ${topic} in their own words, then create their own example problem and solution for a partner to check.`;
}

function generateExitTicket(ctx: Ctx): string {
  if (ctx.subject === 'Math') return generateMathExitTicket(ctx);
  if (ctx.subject === 'English Language Arts') return generateELAExitTicket(ctx);
  if (ctx.subject === 'Science') return generateScienceExitTicket(ctx);
  if (ctx.subject === 'Social Studies') return generateSSExitTicket(ctx);
  const { topic, obj } = ctx;
  return [
    `In 2–3 complete sentences, explain ${topic} in your own words. Write as if you are explaining it to a student who was absent today.`,
    `Show one complete example that demonstrates your ability to ${obj}. Label every step.`,
    `Rate your confidence with today's learning (1 = not yet, 5 = I could teach it): ___. Write one specific question you still have about ${topic}.`,
  ].join('\n');
}

function generateReflectionNotes(): string {
  return [
    "What evidence did I collect today showing students met (or didn't meet) the learning objective?",
    'Which students need additional support? What is my specific next step for each?',
    'What went well in this lesson that I want to replicate or build on?',
    'What would I change if I taught this lesson again — and how exactly would I change it?',
    'Were there any misconceptions that surfaced? How will I address them at the start of next class?',
    'How well did the pacing align to the time allocations? What would I adjust?',
  ].join('\n');
}

// ═══════════════════════════════════════════════════════════════════════════════
// RULE 10 — TIERED INDEPENDENT PRACTICE
// Generates Below-Level, On-Level (= standard IP), and Above-Level versions.
// All three address the same objective and prepare for the same Exit Ticket.
// Only scaffolding, cognitive demand, and degree of independence vary.
// ═══════════════════════════════════════════════════════════════════════════════

function generateIPBelow(ctx: Ctx): string {
  const { subject, topic, t, instrNote } = ctx;

  if (subject === 'Math') {
    const type = mathTopic(ctx.tl);
    const map: Partial<Record<MathTopic, string>> = {
      'fractions-compare': `Below-Level — Comparing Fractions (≈${t(0.20)} min)

📌 Support: Fraction bar strips are provided. Cut them out or use them as a visual reference.

Part A — Same denominator (circle <, >, or =):
1. ¾ ○ ¼       2. 2/5 ○ 4/5       3. 5/8 ○ 3/8       4. 1/6 ○ 5/6
Sentence frame: "I know ___ > ___ because when the denominator is the same, the fraction with the [larger / smaller] numerator is greater."

Part B — Same numerator (circle <, >, or =):
1. 1/2 ○ 1/4       2. 1/3 ○ 1/8       3. 1/5 ○ 1/10
Sentence frame: "I know ___ > ___ because when the numerator is the same, the fraction with the [larger / smaller] denominator has bigger pieces."

Part C — Benchmark (closest to 0, ½, or 1 — mark on the number line):
     0 ————————— ½ ————————— 1
1. 3/8   2. 7/8   3. 5/10

Challenge (optional): Order from least to greatest: ¾, ½, ¼, 1${instrNote}`,

      'fractions-add-sub': `Below-Level — Adding Fractions (≈${t(0.20)} min)

📌 Support: Use the fraction bar model on your reference card to check each answer.

Part A — Same denominator (fill in the sum):
1. 1/5 + 2/5 = ___     2. 2/6 + 3/6 = ___     3. 4/10 + 3/10 = ___     4. 3/8 + 4/8 = ___
Reminder: Add the [numerators / denominators — circle one]. Keep the denominator the [same / different — circle one].

Part B — Draw and solve: Draw fraction bars showing 2/6 + 3/6. Sum = ___ Simplified = ___

Part C — Word problem with guided setup:
"Maria ran 2/8 of a mile before school and 3/8 after school. How far did she run in all?"
Equation: ___ + ___ = ___    Answer: ___
Challenge (optional): ¼ + 2/8. Hint: What do you notice about 8 and 4?${instrNote}`,

      'algebra-equations': `Below-Level — Solving Equations (≈${t(0.20)} min)

📌 Support: Think of the equation as a balance scale. Whatever you do to one side, do the same to the other.

Part A — One-step equations only (show every step; check by substituting back):
1. x + 5 = 12   →   x + 5 − ___ = 12 − ___   →   x = ___   Check: ___ + 5 = 12 ✓
2. x − 4 = 9    (show work below; check answer)
3. 3x = 24      (show work below; check answer)
4. x ÷ 6 = 7    (show work below; check answer)

Part B — Word problem with guided setup:
"There are some students in a class. 8 more students joined, and now there are 23. How many students were there at the start?"
Equation: x + ___ = ___    Solve: x = ___

Challenge (optional): x + 5 = 2x − 3. Hint: get all x's on one side first.${instrNote}`,

      'multiplication': `Below-Level — Multiplication (≈${t(0.20)} min)

📌 Support: Draw an array for any problem you're unsure of: rows = first factor, columns = second factor.

1. Use the array to write the fact: 3 rows of 4 dots = ___ × ___ = ___  (Answer: 3 × 4 = 12)
2. Fill in the missing factor: 4 × ___ = 24  (Answer: 6)
3. Fill in the missing factor: ___ × 6 = 42  (Answer: 7)
4. Fill in the missing factor: 7 × ___ = 35  (Answer: 5)
5. Shelves hold 6 books each. Fill in: 1 shelf = 6, 2 shelves = ___, 3 shelves = ___, 4 shelves = ___, 5 shelves = ___  (Answer: 12, 18, 24, 30)
6. Write your own multiplication word problem where the answer is 36. Solve it.  (Answer: Open response)${instrNote}`,

      'fractions-equivalent': `Below-Level — Equivalent Fractions (≈${t(0.20)} min)

📌 Support: A fraction bar strip reference card is provided. Use it to check that both fractions shade the same total area.

1. ¹⁄₂ = __/4. Multiplier ×2 is shown: multiply both parts by 2. Fill in the blank.  (Answer: ²⁄₄)
2. ¹⁄₃ = __/6. Write the multiplier: × ___, then complete: ¹⁄₃ = ___/6.  (Answer: ×2. ²⁄₆)
3. ¹⁄₄ = __/8. Write the multiplier and the complete equation.  (Answer: ×2. ²⁄₈)
4. ²⁄₃ = __/6. Hint: 3 × ___ = 6, so multiply both parts by that number.  (Answer: ×2. ⁴⁄₆)
5. Circle the fraction equal to ¹⁄₂:   ²⁄₄     ³⁄₅     ²⁄₆  (Answer: ²⁄₄)
6. Complete this sentence: ¹⁄₂ = ²⁄₄ because I multiplied both the ___ and the ___ by ___.  (Answer: numerator, denominator, 2)${instrNote}`,
    };
    return map[type] ?? `Below-Level Independent Practice — ${topic} (≈${t(0.20)} min)

📌 Support: Your reference card shows the procedure and a completed example. Use it throughout this activity.

1. Look at the worked example on your reference card. Solve a similar problem your teacher writes on the board. Label each step.  (Answer: Completed by student — teacher verifies each step is labeled)
2. Solve another problem, this time without looking at the reference card. If you get stuck, check one step at a time.  (Answer: Completed by student — teacher verifies)
3. Write one sentence explaining: what is the first step of today's strategy, and why do you start there?  (Answer: Completed by student — teacher verifies explanation matches the strategy)
4. Check your answer to Problem 2 using the inverse operation. Write the checking equation.  (Answer: Completed by student — teacher verifies checking equation is correct)${instrNote}`;
  }

  if (subject === 'English Language Arts') {
    const type = elaTopic(ctx.tl);
    const map: Partial<Record<ELATopic, string>> = {
      'main-idea': `Below-Level — Main Idea (≈${t(0.20)} min)

📌 Support: Key sentences in the reading are already highlighted. Focus on those first.

Task 1 — Paragraph main idea (complete the sentence frame):
"The main idea of Paragraph 1 is ___ because the author includes [supporting detail 1: ___] and [supporting detail 2: ___]."

Task 2 — Overall main idea (circle the best answer):
The main idea of the whole passage is:
A. ___   B. ___   C. ___   D. ___
[Teacher provides four options based on the class text.]

Task 3 — Supporting detail (1 sentence):
"The most important supporting detail is [quote: ___] because ___."

Challenge (optional): Write the main idea of the whole passage in your own words — without looking at the choices above.${instrNote}`,

      'inference': `Below-Level — Making Inferences (≈${t(0.20)} min)

📌 Support: Evidence cards from the text are pre-cut for you. Match each piece of evidence to an inference before writing.

Task 1 — Sort and match: Match the three text evidence cards to the three inference cards on your desk. (Sorting before writing helps you see the connection.)

Task 2 — Write your inference (use the full sentence frame for each):
Inference A: "The text says '___ [exact words].' This makes me think ___ because I know that ___."
Inference B: Same frame.

Task 3 — Circle the evidence in the text: Open the original passage and circle the exact words you used in your inference. (If you can't find them, revise your inference.)

Challenge (optional): Write one more inference that you figured out WITHOUT a matching card.${instrNote}`,

      'opinion-argument': `Below-Level — Argument Writing (≈${t(0.20)} min)

📌 Support: Your claim is provided below. You only need to find evidence and write the reasoning.

Claim (already written for you): ___________________________
[Teacher pre-writes the class-agreed claim from Direct Instruction.]

Task 1 — Choose evidence: From the list of 5 sentences provided, circle the 2 that BEST support the claim.

Task 2 — Write the reasoning: For each piece of evidence you circled, complete the frame:
"This evidence supports the claim because ___. Without it, we couldn't be sure that ___."

Task 3 — Closing sentence: "For these reasons, I believe ___."

Challenge (optional): Write your OWN counterclaim — the strongest argument against your position — in 1 sentence.${instrNote}`,

      'summarizing': `Below-Level — Summarizing (≈${t(0.20)} min)

📌 Support: A numbered list of the text's paragraphs is provided. Write ONE word for each paragraph's topic before writing any sentences.

Task 1 — One word per paragraph: Paragraph 1: ___ Paragraph 2: ___ Paragraph 3: ___ Paragraph 4: ___

Task 2 — Build the summary using the frame:
"This text is mostly about ___ [topic]. The author explains that ___ [main idea]. Two important details are ___ and ___. The author's purpose is to ___."

Task 3 — Check your summary: Does it include the main idea? ___  Does it leave out minor details? ___  Is it shorter than the original? ___

Challenge (optional): Rewrite your summary in exactly 15 words.${instrNote}`,
    };
    return map[type] ?? `Below-Level Independent Practice — ${topic} (≈${t(0.20)} min)

📌 Support: A vocabulary reference card and one completed example from Direct Instruction are provided.

Task 1 — Anchor to the model: Using the completed example as your guide, apply the same process to the first practice item. Use this frame: "I can see ___ because the text says '___'."
Task 2 — Second practice item with a partial scaffold: [Teacher provides a partially completed version of the second practice item — the first step is filled in; students complete the remaining steps.]
Task 3 — Explain: "Today's skill is useful when ___. I used it by ___."
Challenge (optional): Complete task 3 from the On-Level practice independently.${instrNote}`;
  }

  if (subject === 'Science') {
    const type = sciTopic(ctx.tl);
    const map: Partial<Record<ScienceTopic, string>> = {
      'food-chains': `Below-Level — Forest Food Web Analysis (≈${t(0.20)} min)

📌 Support: On your food web diagram, three organisms are already labeled (Oak Tree = Producer; Bacteria = Decomposer; Robin = Secondary Consumer). Use the word bank to label the remaining four.
Word bank: Primary Consumer, Primary Consumer, Secondary Consumer, Tertiary Consumer

Task 1 — Label the diagram: Add the correct role label to Caterpillar, Mouse, Fox, and Red-Tailed Hawk.

Task 2 — Energy math (steps are scaffolded):
The oak tree stores 10,000 J.
Step 1: Caterpillar gets 10,000 × 10% = ___ J
Step 2: Robin gets ___ × 10% = ___ J
Step 3: Red-Tailed Hawk gets ___ × 10% = ___ J

Task 3 — Cascade prediction (circle the correct word in each bracket):
"If the Robin population crashes, Caterpillars will [increase / decrease] because they have [fewer / more] predators. This means Oak Tree leaves will [gain / lose] more damage because ___."

Challenge (optional): Complete tasks 4–5 from the On-Level practice.${instrNote}`,

      'cells': `Below-Level — Cell Structure and Function (≈${t(0.20)} min)

📌 Support: On your cell diagram, five organelles are already labeled. Add the remaining three using the word bank below.
Word bank: Chloroplasts, Endoplasmic Reticulum, Golgi Apparatus

Task 1 — Complete the diagram: Label the three unlabeled organelles.

Task 2 — Matching (draw a line from each organelle to its function):
Mitochondria     •     • Directs cell activity; contains DNA
Nucleus          •     • Produces energy from nutrients
Cell Membrane    •     • Controls what enters and exits the cell
Ribosome         •     • Builds proteins

Task 3 — Sentence frames:
"The [organelle] is important because without it, the cell could not ___. It is like a ___ because both ___."

Challenge (optional): Draw a quick sketch of an animal cell from memory. Label at least 4 organelles.${instrNote}`,

      'water-cycle': `Below-Level — The Water Cycle (≈${t(0.20)} min)

📌 Support: A labeled water cycle diagram with arrows is provided. Refer to it as you answer each question.

Task 1 — Label the processes: On your diagram, write the correct process name next to each arrow:
liquid → gas (solar heat) = ___     gas → droplets (cooling) = ___
droplets → ground (falling) = ___   ground → lakes/rivers = ___

Task 2 — Use the bag observation (from Guided Practice):
"The droplets on the inside of the bag were formed by ___ because ___."
"The water at the bottom of the bag represents ___."

Task 3 — Sentence frame (complete the cycle description):
"Energy from the ___ causes water to evaporate. The water vapor rises, cools, and ___. Then it falls as ___ and collects in ___, starting the cycle again."

Challenge (optional): Without looking at the diagram, draw and label the complete water cycle from memory.${instrNote}`,

      'forces-motion': `Below-Level — Forces and Motion (≈${t(0.20)} min)

📌 Support: The formula F = ma is on your reference card with one worked example. Use it as a guide.

Task 1 — Label the force diagram (arrows already drawn):
Your diagram shows a box being pushed to the right. Label: Applied Force, Friction, Normal Force, Gravity. Circle the direction the box will move.

Task 2 — F = ma practice (scaffolded):
Problem: A 10 kg cart is pushed with 30 N of force. Find acceleration.
Step 1: Write the formula: a = F ÷ m
Step 2: Substitute: a = ___ ÷ ___
Step 3: Solve: a = ___ m/s²
Problem 2 (on your own): A 5 kg ball is pushed with 20 N. Find acceleration. Show all steps.

Task 3 — Balanced or unbalanced? (circle one and explain):
A book sits still on a desk. Balanced / Unbalanced — because "___."

Challenge (optional): What force would be needed to accelerate a 15 kg object at 3 m/s²? Show work.${instrNote}`,
    };
    return map[type] ?? `Below-Level Independent Practice — ${topic} (≈${t(0.20)} min)

📌 Support: A vocabulary reference card with labeled diagrams and one worked example from Direct Instruction is provided.

Task 1 — Identify: Using your reference card, locate the key concept from today's lesson. Complete this sentence: "The most important idea from today's lesson on ${topic} is ___."
Task 2 — Guided practice: Complete the first two tasks from the On-Level practice with the following support: (a) use your reference card to check vocabulary; (b) for each answer, write one sentence explaining HOW you got it.
Task 3 — Explain your reasoning: "I know this is correct because ___."
Challenge (optional): Attempt task 3 from the On-Level practice with a partner.${instrNote}`;
  }

  if (subject === 'Social Studies') {
    const type = ssTopic(ctx.tl);
    const map: Partial<Record<SSTopic, string>> = {
      'civil-war': `Below-Level — Civil War Causes (≈${t(0.20)} min)

📌 Support: A three-column graphic organizer (Political | Economic | Social) is provided with 2 causes already placed in each column.

Task 1 — Add causes: Using today's notes or reading, add at least 2 more causes to ANY column of your organizer.

Task 2 — Primary source (3-sentence excerpt provided, vocabulary annotated):
Answer: "Who wrote this? ___   When? ___   What is the main argument? ___."

Task 3 — Ranking (put 1, 2, 3 in order from most to least important):
___ Slavery expanding into new territories
___ Economic differences between North and South
___ Election of Abraham Lincoln
"I ranked ___ as #1 because ___."

Challenge (optional): Write 2–3 sentences connecting one cause to a specific event from the timeline.${instrNote}`,

      'american-revolution': `Below-Level — American Revolution (≈${t(0.20)} min)

📌 Support: A cause-and-effect graphic organizer is provided with 2 rows already completed.

Task 1 — Complete the organizer: Add 2 more British actions and their colonial responses to the table (left: British Action | right: Colonial Response).

Task 2 — Key terms (match each term to its meaning):
Taxation without representation  •  • A formal list of complaints against a government
Boycott                          •  • Refusing to buy or use something as a protest
Petition                         •  • Taxing a group that has no political voice

Task 3 — Viewpoint (circle one and complete the frame):
I am a [Patriot / Loyalist]. I believe ___. My strongest reason is ___.

Challenge (optional): Write 2 sentences explaining what the other side (the one you didn't choose) would argue.${instrNote}`,
    };
    return map[type] ?? `Below-Level Independent Practice — ${topic} (≈${t(0.20)} min)

📌 Support: An annotated primary source excerpt and a graphic organizer with 2 rows pre-completed are provided.

Task 1 — Document analysis: Using the annotated source, answer: "Who created this? When? What is the main message or argument?"
Task 2 — Add to the graphic organizer: Complete 2 more rows using information from today's reading or notes.
Task 3 — Sentence frame response: "The most significant event or idea from today's lesson is ___ because ___."
Challenge (optional): Complete the first open-response question from the On-Level practice.${instrNote}`;
  }

  return `Below-Level Independent Practice — ${topic} (≈${t(0.20)} min)

📌 Support: A reference card with key vocabulary and a worked example from Direct Instruction is available.

Task 1 — Recognition: Study the class example. Complete: "This example shows ___ because ___."
Task 2 — Guided practice (tasks 1–2 from On-Level): Work through the first two On-Level tasks with your reference card available. Show every step.
Task 3 — Self-check: "My answer makes sense because ___."
Challenge (optional): Attempt On-Level task 3 on your own.${instrNote}`;
}

function generateIPAbove(ctx: Ctx): string {
  const { subject, topic, t, instrNote, isHigh } = ctx;

  if (subject === 'Math') {
    const type = mathTopic(ctx.tl);
    const map: Partial<Record<MathTopic, string>> = {
      'fractions-compare': `Above-Level — Comparing Fractions (≈${t(0.20)} min)

Task 1 — Create challenge problems: Write 3 fraction comparison problems for a partner that each require a DIFFERENT strategy (same denominator, same numerator, common denominator). Solve each and write an answer key. Trade with a partner and solve theirs.

Task 2 — Mathematical argument: "Explain in 3–4 sentences why the common denominator strategy ALWAYS works, using an area model as your evidence. Could you compare any two fractions this way? Are there faster methods? When would you choose each?"

Task 3 — Error analysis: Here is a student's work: 3/4 > 7/8 because 3 < 7 and 4 < 8. What went wrong? Correct the error, explain it in 2 sentences, and write a "hint" the student could have used to catch their own mistake.${instrNote}`,

      'fractions-add-sub': `Above-Level — Adding Fractions (≈${t(0.20)} min)

Task 1 — Multi-step word problem (no scaffolding): "Mia used 3/8 of a bag of flour on Monday, 1/4 on Tuesday, and 5/8 on Wednesday. She started with 2 full bags. How much flour is left? Show all work."

Task 2 — Create and solve: Write a two-step word problem involving fraction addition where the answer is exactly 1½. Your problem must describe a real situation. Solve it and explain why your equation matches the story.

Task 3 — Justify: "A student says you can add fractions by adding the numerators AND the denominators (e.g., 1/2 + 1/3 = 2/5). Write a paragraph explaining why this is wrong. Use an area model, a word problem, and a number line in your explanation."${instrNote}`,

      'algebra-equations': `Above-Level — Solving Equations (≈${t(0.20)} min)

Task 1 — Create a word problem: Write a real-life scenario that produces the equation 3x + 7 = 28. Solve it. Explain why each algebraic step maps back to a step in the real situation.

Task 2 — Error analysis (two worked examples are shown): Identify the error in each, explain what conceptual misunderstanding caused it, and write a one-sentence "rule" the student could use to avoid that error in the future.

Task 3 — Generalize: ${isHigh
  ? '"Consider the equation ax + b = c. Without any specific numbers, describe in words the steps to solve for x and explain why each step is mathematically valid."'
  : '"If you double both sides of a true equation, is it still true? If you square both sides? Test your answer with numbers, then explain WHY it works or doesn\'t."'}${instrNote}`,

      'fractions-multiply-divide': `Above-Level — Multiplying and Dividing Fractions (≈${t(0.20)} min)

1. Multi-step: Mia used ³⁄₈ of a bag of flour on Monday, ¹⁄₄ on Tuesday, and ⁵⁄₈ on Wednesday. She started with 2 full bags. Write and solve the equation for how much flour remains. Show all work.  (Answer: Total used = ³⁄₈ + ²⁄₈ + ⁵⁄₈ = ¹⁰⁄₈ = ¹¼. Remaining = 2 − ¹¼ = ¾ bags.)
2. Create and solve: Write a two-step word problem involving fraction multiplication where the final answer is exactly ¹⁄₄. Your problem must describe a real situation. Show all work.  (Answer: Open response — check that equation matches story and answer is ¹⁄₄.)
3. Justify: A student says you multiply fractions by adding numerators AND adding denominators. Write a paragraph disproving this, using a number line, a word problem, and an area model.  (Answer: Open response — check three evidence types used.)${instrNote}`,

      'fractions-equivalent': `Above-Level — Equivalent Fractions (≈${t(0.20)} min)

1. Write an equivalent fraction for each using the given multiplier, then identify the simplest form: ²⁄₃ with ×5, ³⁄₇ with ×4, ⁵⁄₈ with ×3. For each answer, divide both parts by their GCF to confirm.  (Answer: ¹⁰⁄₁₅ → ²⁄₃; ¹²⁄₂₈ → ³⁄₇; ¹⁵⁄₂₄ → ⁵⁄₈)
2. Create a set of four equivalent fractions for ³⁄₇. Show the multiplier for each. Then explain why there are infinitely many equivalent fractions for any given fraction.  (Answer: ⁶⁄₁₄ via ×2, ⁹⁄₂₁ via ×3, ¹²⁄₂₈ via ×4, ¹⁵⁄₃₅ via ×5. Open response for explanation.)
3. Explain in 3 sentences why multiplying both numerator and denominator by the same nonzero number keeps a fraction's value unchanged. Include an area model description as evidence.  (Answer: Open response)${instrNote}`,
    };
    return map[type] ?? `Above-Level Independent Practice — ${topic} (≈${t(0.20)} min)

1. Solve a problem at advanced difficulty chosen by your teacher. Show all steps using today's strategy. Then solve the same problem a different way and compare — which method is more efficient and why?  (Answer: Completed by student — teacher verifies both methods and comparison)
2. Write your own problem that uses today's skill with one additional layer of complexity. Solve it completely, showing all steps and a checking equation.  (Answer: Completed by student — teacher verifies problem matches skill, solution is complete)
3. Explain in 3–4 sentences: under what conditions does today's strategy always produce a correct answer? Is there a case where a different approach would be necessary? Give a specific example.  (Answer: Completed by student — teacher verifies mathematical reasoning is sound)${instrNote}`;
  }

  if (subject === 'English Language Arts') {
    const type = elaTopic(ctx.tl);
    const map: Partial<Record<ELATopic, string>> = {
      'main-idea': `Above-Level — Main Idea (≈${t(0.20)} min)

Task 1 — Competing interpretations: Identify a SECOND possible main idea for the same text. Write a paragraph arguing which interpretation is better supported — using at least three pieces of evidence for each. Your argument must address why the competing interpretation is reasonable but ultimately weaker.

Task 2 — Write your own: Write a two-paragraph informational text on a topic of your choice. Each paragraph must have a clear, distinct main idea supported by two specific details. Trade with a partner who must identify both main ideas and your two strongest details — without any hints from you. Evaluate: did your writing communicate what you intended?

Task 3 — Critique: "A student says the main idea of this article is [teacher inserts a plausible but incorrect interpretation]. Write 3–4 sentences arguing why you disagree, citing specific evidence."${instrNote}`,

      'inference': `Above-Level — Making Inferences (≈${t(0.20)} min)

Task 1 — Competing inferences: Find two places in the text where the SAME piece of evidence could support two different inferences. Write both inferences and argue — using additional context from the text — which is better supported.

Task 2 — Counter-inference: For each inference you made, write the strongest ALTERNATIVE interpretation of the same evidence — the best argument against your inference. Then explain in 1–2 sentences why your original inference is still stronger.

Task 3 — Evidence quality: Rate each of your inferences on a 1–3 scale (1 = thin support, 2 = adequate support, 3 = strongly supported). Write one sentence for each rating explaining what would make a "1" a "3" — what additional evidence would the author need to provide?${instrNote}`,

      'opinion-argument': `Above-Level — Argument Writing (≈${t(0.20)} min)

Task 1 — Steel man the opposition: Write the strongest possible version of the counterargument — the BEST case for the position you oppose. Then write a two-paragraph rebuttal that directly addresses each point in your steel man argument. Use specific evidence for every claim.

Task 2 — Evaluate two arguments: ${isHigh
  ? 'Read two published editorials on the same topic (teacher provides). Identify each author\'s claim, evidence, and reasoning. Then evaluate: which argument is more logically sound? Which relies more on emotional appeal? Which would persuade a skeptical reader — and why?'
  : 'Compare your argument paragraph to a classmate\'s. Identify: (a) the strongest point in their argument, (b) the weakest point, (c) one piece of evidence you wish you had used. Write a paragraph synthesizing the two arguments.'}

Task 3 — Meta-analysis: "What makes this topic genuinely arguable? Identify the core value disagreement that makes reasonable people reach different conclusions — not a factual dispute, but a difference in what people prioritize."${instrNote}`,

      'theme': `Above-Level — Theme (≈${t(0.20)} min)

Task 1 — Second theme: Identify a second theme in the same text and write a paragraph arguing which theme is more central — using at least three pieces of evidence for each, and addressing why the other theme, while present, is secondary.

Task 2 — Cross-text theme: Connect today's text to another text you've read this year. Write a structured comparison paragraph: same theme, different situation — analyze how each author develops the theme and which does it more effectively.

Task 3 — Write a theme narrative: Write a 10–12 sentence narrative that expresses today's theme WITHOUT ever stating it directly. The theme must emerge entirely from character choices, consequences, and specific details.${instrNote}`,
    };
    return map[type] ?? `Above-Level Independent Practice — ${topic} (≈${t(0.20)} min)

Task 1 — Transfer and apply: Apply today's literacy skill to a text you select independently (your independent reading book, a news article, or a poem not used in class). Write a 3–4 sentence analysis demonstrating the same skill — name what you found, quote the evidence, and explain its significance.
Task 2 — Create: Write a short original piece (1 paragraph or 6–10 sentences) that intentionally uses today's skill with craft. Annotate your own writing in the margin: name the skill at each key moment and explain the effect you intended.
Task 3 — Metacognitive reflection: "When is this skill easy and intuitive? When does it get hard, and what specifically makes the hard cases harder? What separates a strong response from a weak one?"${instrNote}`;
  }

  if (subject === 'Science') {
    const type = sciTopic(ctx.tl);
    const map: Partial<Record<ScienceTopic, string>> = {
      'food-chains': `Above-Level — Forest Food Web Analysis (≈${t(0.20)} min)

Task 1 — New biome food web: Choose a real biome (Arctic tundra, Amazon rainforest, or coral reef — NOT temperate forest). Research and construct a food web with at least 8 real organisms. Calculate the energy at each trophic level if producers start with 2,000,000 J. Show all calculations.

Task 2 — Comparative cascade analysis: "In our temperate forest, the Red-Tailed Hawk is the top predator. In your food web, which is the top predator? If both top predator populations crashed simultaneously, which ecosystem would recover more quickly — and why? Use the 10% rule and cascade logic for both ecosystems in your answer."

Task 3 — Ecologist's report: "A city council is proposing to develop 50% of the oak forest for housing. As the ecologist who studied this food web, write a 3–4 paragraph report explaining the ecosystem consequences, supported by specific cascade predictions and energy calculations. Conclude with a recommendation."${instrNote}`,

      'cells': `Above-Level — Cell Structure and Function (≈${t(0.20)} min)

Task 1 — Original analogy: Design a cell analogy for a system you choose — NOT a factory or school. Your analogy must include at least 7 organelles with scientifically accurate connections AND identify at least one place where the analogy breaks down (no analogy is perfect — name the limitation explicitly).

Task 2 — Research synthesis: How did the invention of the electron microscope change our understanding of cell structure compared to what light microscopes revealed? Write a paragraph explaining the specific structures that were discovered or better understood — and what that means for biology.

Task 3 — Design a disease: "Create a fictional cellular disease that targets one specific organelle. Describe: (a) which organelle is affected, (b) what cellular processes fail and why, (c) what symptoms would appear in the organism, (d) what a scientist might test to confirm this diagnosis."${instrNote}`,

      'forces-motion': `Above-Level — Forces and Motion (≈${t(0.20)} min)

Task 1 — Engineering application: A car safety system uses seatbelts, airbags, and crumple zones. Explain which of Newton's three laws each component specifically addresses, what would happen without it, and why engineers had to calculate forces carefully when designing each.

Task 2 — Multi-object calculation: Three objects — 2 kg, 8 kg, and 15 kg — are each pushed with 24 N of force. Calculate the acceleration of each. Then write a paragraph explaining what the results reveal about the relationship between mass and acceleration, and why this matters in a real engineering context.

Task 3 — Design challenge: You need to design a crash test for a vehicle. What forces would you measure? Which Newton's law governs each phase of the crash? Write a complete experimental design including: question, hypothesis, variables, procedure, data table, and what a "successful" safety system would look like in the data.${instrNote}`,
    };
    return map[type] ?? `Above-Level Independent Practice — ${topic} (≈${t(0.20)} min)

Task 1 — Design a follow-up investigation: Extend today's experiment with a new, more complex research question. Write a complete hypothesis (if-then format with scientific reasoning), identify all variables, write a step-by-step procedure, and design a data table. Explain how you would analyze the results.
Task 2 — Real-world connection: Research a current scientific application or discovery directly related to today's concept. Write a one-page explanation connecting what we learned in class to the real-world example — including what scientists still don't know.
Task 3 — Create an Expert Explainer: Write, draw, or design an illustrated guide that teaches today's concept to a student at least two grade levels below you. The guide must be scientifically accurate, use all key vocabulary correctly, and include at least one original analogy you created.${instrNote}`;
  }

  if (subject === 'Social Studies') {
    const type = ssTopic(ctx.tl);
    const map: Partial<Record<SSTopic, string>> = {
      'civil-war': `Above-Level — Civil War (≈${t(0.20)} min)

Task 1 — Structured argument: "Was the Civil War primarily caused by slavery or states' rights?" Write a structured argument with at least 4 pieces of historical evidence, a direct acknowledgment of the strongest counterargument, and a rebuttal. Evidence must be specific — quote documents, name legislation, cite events.

Task 2 — Primary source deep read: Find this source: "What to the Slave is the Fourth of July?" — Frederick Douglass, 1852. Read the opening 3 paragraphs. Write a paragraph analyzing: the speaker's purpose, intended audience, use of irony, and why this speech matters as historical evidence about the causes of the Civil War.

Task 3 — Historical parallel: Identify one issue in American politics today that echoes the tensions that led to the Civil War. Write 2–3 paragraphs explaining the historical parallel, where it holds, and where it breaks down. Use specific historical and contemporary evidence.${instrNote}`,

      'american-revolution': `Above-Level — American Revolution (≈${t(0.20)} min)

Task 1 — Loyalist primary source: Find and analyze one primary source written by a Loyalist during the Revolution. Compare it to a Patriot source from the same period. Write a paragraph explaining what each author valued most and why they reached different conclusions from the same set of events.

Task 2 — Modern parallel: Connect one cause of the American Revolution to a modern political event where citizens have disputed government authority. Write 2–3 paragraphs explaining the parallel — and where it breaks down. Use at least one source for the modern event.

Task 3 — Historical argument: "The American Revolution was primarily an economic conflict, not a political one." Write a structured argument agreeing OR disagreeing, using at least four historical facts as evidence and addressing the strongest counterargument.${instrNote}`,
    };
    return map[type] ?? `Above-Level Independent Practice — ${topic} (≈${t(0.20)} min)

Task 1 — Primary source research: Find a primary source NOT used in class that offers a new or different perspective on today's topic. Write a paragraph analyzing the source (author, purpose, audience, bias, historical significance) and explaining what it adds to our understanding.
Task 2 — Historical argument: Take a position on the most significant cause, event, or consequence related to today's lesson. Write a structured argument with at least 3 pieces of specific evidence and a rebuttal of the strongest counterargument.
Task 3 — Connect to the present: Draw a specific, researched connection between today's historical content and a current event or ongoing issue. Write 2–3 paragraphs explaining the parallel and where it breaks down.${instrNote}`;
  }

  return `Above-Level Independent Practice — ${topic} (≈${t(0.20)} min)

Task 1 — Transfer: Apply today's skill to an unfamiliar or real-world context not covered in the lesson. Explain your reasoning in 3–4 sentences.
Task 2 — Create: Design an original problem, scenario, or investigation that requires today's skill at a deeper level. Write a complete solution. The task must require synthesis-level thinking — not just the application of a procedure.
Task 3 — Evaluate and generalize: "When does today's skill or concept apply reliably? Are there situations where it breaks down or requires modification? Identify one such situation and explain what additional knowledge is needed to handle it."${instrNote}`;
}

function generateTieredIndependentPractice(ctx: Ctx): TieredPractice {
  return {
    belowLevel: generateIPBelow(ctx),
    onLevel:    generateIndependentPractice(ctx),
    aboveLevel: generateIPAbove(ctx),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// RULE 11 — MULTIMEDIA INTEGRATION
// Optional recommendations for video and/or interactive resources that reinforce
// the lesson objective. Each recommendation includes title, platform, duration,
// URL/search path, rationale, placement, and embedded discussion prompts.
// ═══════════════════════════════════════════════════════════════════════════════

function generateMultimediaResources(ctx: Ctx, mode: MultimediaMode): string {
  if (mode === 'none') return '';

  const { subject, topic } = ctx;

  interface MediaRec {
    title: string;
    platform: string;
    duration: string;
    url: string;
    why: string;
    placement: string;
    pauseAndDiscuss: string;
  }

  // ── Video recommendations ──────────────────────────────────────────────────

  const sciType = subject === 'Science' ? sciTopic(ctx.tl) : null;
  const mathType = subject === 'Math' ? mathTopic(ctx.tl) : null;
  const elaType = subject === 'English Language Arts' ? elaTopic(ctx.tl) : null;
  const ssType = subject === 'Social Studies' ? ssTopic(ctx.tl) : null;

  function getVideo(): MediaRec {
    if (sciType === 'food-chains') return {
      title: 'Food Chains and Webs',
      platform: 'Generation Genius',
      duration: '5–8 min',
      url: 'generationgenius.com → search "Food Chains"',
      why: 'Animated energy-flow sequence directly mirrors today\'s oak tree → caterpillar → robin → hawk chain. Vocabulary (producer, consumer, decomposer, food web) is introduced at an accessible pace for grades 3–6.',
      placement: 'Before Guided Practice — use it as the bridge between Direct Instruction and the yarn simulation.',
      pauseAndDiscuss: 'Pause at the energy pyramid segment: "Which organisms in OUR food web match these roles? Where would the red-tailed hawk appear on this pyramid?" Pause again at the web disruption scene: "What would happen in today\'s forest if the robin were removed instead?"',
    };
    if (sciType === 'cells') return {
      title: 'Cells',
      platform: 'BrainPOP',
      duration: '5 min',
      url: 'brainpop.com → Science → Cellular Life → Cells',
      why: 'Clear, engaging overview of cell structure and organelle function. The animation of organelles working together reinforces the "cell as a system" concept from Direct Instruction.',
      placement: 'Before Guided Practice — after students have labeled the diagram, use the video to confirm their work and fill gaps.',
      pauseAndDiscuss: 'Pause when the nucleus appears: "What does our diagram label the nucleus as? What would happen to the whole cell if the nucleus stopped working?" Pause at the mitochondria: "Why do muscle cells have MORE mitochondria than skin cells?"',
    };
    if (sciType === 'water-cycle') return {
      title: 'The Water Cycle',
      platform: 'Generation Genius',
      duration: '6–9 min',
      url: 'generationgenius.com → search "Water Cycle"',
      why: 'Visualizes all four water cycle stages at a molecular level — especially condensation, which is the most commonly misunderstood process. Reinforces the sealed-bag observation from Guided Practice.',
      placement: 'During Direct Instruction — show after introducing condensation (step 3) to give students a molecular-level visual before they observe it in the bag.',
      pauseAndDiscuss: 'Pause at condensation: "Look at our bag — is this happening there too?" Pause at precipitation: "What would need to be different in our bag for precipitation to occur?"',
    };
    if (sciType === 'forces-motion') return {
      title: 'Newton\'s Laws of Motion',
      platform: 'Crash Course Kids (YouTube)',
      duration: '4–5 min per episode',
      url: 'youtube.com → search "Crash Course Kids Newton\'s Laws"',
      why: 'Three short episodes, one per law, with clear real-world examples (seatbelts, skateboards, rockets). Directly reinforces the F = ma work from Direct Instruction.',
      placement: 'Before Guided Practice — after introducing all three laws, play the episode matching today\'s law (or all three if time allows).',
      pauseAndDiscuss: 'Pause after the first law: "Give me a real example of inertia from our classroom right now." After F = ma: "If we doubled the force in today\'s lab, what would happen to acceleration — predict before we test it."',
    };
    if (mathType === 'fractions-compare' || mathType === 'fractions-add-sub' || mathType === 'fractions-multiply-divide') return {
      title: `Fractions — ${topic}`,
      platform: 'Khan Academy',
      duration: '5–8 min',
      url: `khanacademy.org → search "${topic}" in 4th/5th Grade Math`,
      why: 'Sal Khan\'s worked examples closely mirror the Direct Instruction problem format. The visual area models on screen match the models students drew during DI.',
      placement: 'Before Guided Practice — use as a "second exposure" to the modeled examples, letting students follow along and predict each step before it is revealed.',
      pauseAndDiscuss: 'Pause before each answer is revealed: "What step comes next? What is Khan about to do — and why?" After the video: "Which example was closest to the class examples? Which was the most different?"',
    };
    if (elaType === 'main-idea') return {
      title: 'Main Idea',
      platform: 'Flocabulary',
      duration: '3–4 min',
      url: 'flocabulary.com → search "Main Idea"',
      why: 'Hip-hop format is highly engaging for grades 3–8. Lyrics reinforce the distinction between topic (what it\'s about) and main idea (the most important thing the author says), which is the most common student misconception.',
      placement: 'Warm-Up Hook — play before introducing the lesson to activate prior knowledge and generate energy.',
      pauseAndDiscuss: 'After the video: "Write the main idea of the song — not the topic — in one sentence. Compare with a partner." If time allows, ask: "Which lyric in the song best explains the difference between topic and main idea?"',
    };
    if (elaType === 'inference') return {
      title: 'Making Inferences',
      platform: 'BrainPOP',
      duration: '5 min',
      url: 'brainpop.com → ELA → Reading and Writing → Making Inferences',
      why: 'Models the text-evidence + background-knowledge formula in an engaging format. The movie-screenshot example is a strong anchor for students who struggle with the abstract concept of "reading between the lines."',
      placement: 'During Direct Instruction — show after your first modeled think-aloud to give students a second model from a different source before they try it together.',
      pauseAndDiscuss: 'Pause before Annie reveals her inference: "What do YOU infer from that evidence? What do you know from your own experience that supports it?" After the video: "How is the process Annie used the same as the sentence frame we used?"',
    };
    if (ssType === 'civil-war') return {
      title: 'The Civil War: Crash Course US History #20',
      platform: 'YouTube (Crash Course)',
      duration: '12 min',
      url: 'youtube.com → search "Crash Course US History #20 Civil War"',
      why: 'Fast-paced, academically rigorous overview that addresses causes, key events, and the role of slavery directly — with specific dates, legislation, and figures. Appropriate for middle and high school.',
      placement: 'Before Independent Practice — use as a review and content extension after Guided Practice to deepen the causal analysis students will write independently.',
      pauseAndDiscuss: 'Pause at 3:30 (causes discussion): "Is John Green\'s ranking of causes the same as yours? Where do you agree? Where do you disagree — and what evidence would change your mind?" Pause at 8:00: "Which piece of evidence from this video would strengthen your argument most?"',
    };
    if (ssType === 'american-revolution') return {
      title: 'The American Revolution: Crash Course US History #8',
      platform: 'YouTube (Crash Course)',
      duration: '12 min',
      url: 'youtube.com → search "Crash Course US History #8 American Revolution"',
      why: 'Covers causes, key figures, and the Loyalist perspective — giving students exposure to the side often underrepresented in textbooks. Academically appropriate for grades 6–12.',
      placement: 'Before Independent Practice — use to deepen understanding of both Patriot and Loyalist perspectives before students write their structured argument.',
      pauseAndDiscuss: 'Pause at the Loyalist segment: "What reasons does this present for staying loyal to Britain? Would YOU have been a Patriot or Loyalist — and what would it take to change your mind?" After: "Which cause from the video was NOT on your graphic organizer? Add it."',
    };
    // Generic fallback by subject
    if (subject === 'Math') return {
      title: `${topic} — Video Lesson`,
      platform: 'Khan Academy',
      duration: '5–10 min',
      url: `khanacademy.org → search "${topic}"`,
      why: `Khan Academy's worked examples for ${topic} closely parallel Direct Instruction. The visual step-by-step format lets students hear the same reasoning modeled a second time from a different voice.`,
      placement: 'Before Guided Practice — play after Direct Instruction so students get a second exposure to the modeled thinking before practicing together.',
      pauseAndDiscuss: 'Pause before each answer is revealed and ask students to predict the next step. After: "Which example was most similar to ours? What was different about how Khan set it up?"',
    };
    if (subject === 'English Language Arts') return {
      title: `${topic} — Video Lesson`,
      platform: 'Flocabulary or BrainPOP',
      duration: '3–6 min',
      url: `flocabulary.com or brainpop.com → search "${topic}"`,
      why: `A video second exposure to ${topic} gives students an additional modeled example before they attempt Guided Practice — especially beneficial for students who process auditory and visual input more effectively than text alone.`,
      placement: 'During Direct Instruction — show after your modeled think-aloud as a second model from a different source.',
      pauseAndDiscuss: 'Pause mid-video and ask: "What step or strategy are they using right now? Is it the same as our class method?" After: "What did the video explain in a way that was different from how I explained it?"',
    };
    return {
      title: `${topic} — Educational Video`,
      platform: 'YouTube (search educational channels) or BrainPOP',
      duration: '4–8 min',
      url: `YouTube search: "${topic} lesson for students" (preview before class to confirm appropriateness and accuracy)`,
      why: `A well-chosen video gives students a second modeled exposure to ${topic} from a different voice and visual format — supporting students who benefit from multiple representations.`,
      placement: 'Before Guided Practice — use to bridge Direct Instruction and student practice.',
      pauseAndDiscuss: 'Pause at a key moment and ask: "What concept just appeared? How does this connect to what we learned?" After: "What did the video show that we DIDN\'T cover? What question does it raise for you?"',
    };
  }

  // ── Interactive recommendations ────────────────────────────────────────────

  function getInteractive(): MediaRec {
    if (sciType === 'food-chains') return {
      title: 'Build a Food Web',
      platform: 'National Geographic Education',
      duration: '10–15 min',
      url: 'nationalgeographic.org/interactive/build-a-food-web (search "National Geographic food web interactive")',
      why: 'Students construct and disrupt food webs in a digital environment, observing cascade effects in real time. Directly parallels the yarn simulation from Guided Practice — now at larger ecological scale.',
      placement: 'Extension Activity or Before Independent Practice — gives students an additional data set to reference when writing cascade predictions.',
      pauseAndDiscuss: 'After students trigger a population collapse: "How does what happened here match what the yarn did in our simulation? Which is more realistic — and why?" Teacher tip: have students screenshot or sketch the food web they built before disrupting it so they can reference it during Independent Practice.',
    };
    if (sciType === 'forces-motion') return {
      title: 'Forces and Motion: Basics',
      platform: 'PhET Interactive Simulations (University of Colorado)',
      duration: '10–15 min',
      url: 'phet.colorado.edu → search "Forces and Motion: Basics"',
      why: 'Students apply forces to objects and observe acceleration in real time — directly testing F = ma with visual feedback. The "friction" mode allows direct comparison across surfaces, mirroring today\'s lab.',
      placement: 'During Guided Practice or Independent Practice — use as a virtual extension of the friction lab.',
      pauseAndDiscuss: 'After students test several mass/force combinations: "Does the simulation confirm our F = ma formula? What did you notice about what happens when you apply the same force to objects of different mass?" Teacher tip: assign specific questions: "Find the force needed to accelerate the 5 kg box at 4 m/s². Show your equation BEFORE clicking Apply."',
    };
    if (sciType === 'water-cycle') return {
      title: 'Water Cycle Diagram — Interactive',
      platform: 'PBS LearningMedia',
      duration: '10–15 min',
      url: 'pbslearningmedia.org → search "water cycle interactive"',
      why: 'Students click through each stage of the water cycle with animated molecular-level visualizations — especially strong for condensation and evaporation, the two processes most likely to cause misconceptions.',
      placement: 'During Direct Instruction or Lesson Closure — use the interactive diagram to check understanding after students have completed the sealed-bag observation.',
      pauseAndDiscuss: 'Ask students to trace their water molecule\'s journey through the interactive before writing the homework reflection. Teacher tip: have students predict which path their molecule will take before clicking — then check their prediction.',
    };
    if (mathType === 'fractions-compare' || mathType === 'fractions-add-sub') return {
      title: 'Fractions: Equality (or Fractions: Intro)',
      platform: 'PhET Interactive Simulations',
      duration: '10–15 min',
      url: 'phet.colorado.edu → search "Fractions Equality"',
      why: 'Students build fractions with visual area models and number lines — seeing equivalence and comparison as spatial relationships rather than symbolic rules. Directly reinforces the benchmark fraction and area model work from Direct Instruction.',
      placement: 'During Guided Practice — students use the simulation to verify their fraction comparisons before writing final answers.',
      pauseAndDiscuss: 'Ask: "Build ¾ and 7/8 in the simulation. Which is larger? How can you tell by looking at the areas?" Teacher tip: require students to screenshot their model for each problem and paste it into their digital notes — this creates a visual record of their reasoning.',
    };
    if (elaType === 'main-idea' || elaType === 'inference' || elaType === 'summarizing' || elaType === 'authors-purpose') return {
      title: 'Leveled Reading with Guided Comprehension',
      platform: 'ReadWorks (readworks.org) or CommonLit (commonlit.org)',
      duration: '15–20 min',
      url: 'readworks.org (free) or commonlit.org (free) — search for a paired text on the same topic as today\'s lesson',
      why: 'A second, leveled text on the same topic lets students practice today\'s skill (main idea, inference, summarizing) with a fresh passage — deepening the skill through application rather than repetition of the same text.',
      placement: 'Independent Practice — use as the reading for the independent practice task when students have already analyzed the class text together.',
      pauseAndDiscuss: 'Before releasing students: "Before you start reading, preview the title and first sentence. Predict: what do you think the main idea will be?" After: "Did your prediction match? What clue gave it away — or fooled you?" Teacher tip: CommonLit provides teacher-facing discussion questions and student writing prompts already aligned to ELA standards.',
    };
    if (ssType === 'civil-war' || ssType === 'american-revolution' || ssType === 'civil-rights') return {
      title: 'Primary Source Analysis Tools',
      platform: 'Stanford History Education Group (SHEG)',
      duration: '15–20 min',
      url: 'sheg.stanford.edu → Civic Online Reasoning or Reading Like a Historian',
      why: 'SHEG\'s Reading Like a Historian lessons provide scaffolded primary source analysis with sourcing, contextualization, and corroboration questions built in — aligned with the historical thinking skills practiced today.',
      placement: 'Independent Practice or Extension — use a SHEG lesson on today\'s topic as the independent practice primary source analysis task.',
      pauseAndDiscuss: 'Before students begin: "What do you need to know about the SOURCE before you can trust what it SAYS?" After: "Did the primary source change or deepen your understanding of today\'s topic? How?" Teacher tip: SHEG lessons include teacher guides with common student errors and discussion facilitation notes.',
    };
    return {
      title: `${topic} — Interactive Simulation or Practice`,
      platform: 'PhET Simulations, Khan Academy, or CK-12',
      duration: '10–15 min',
      url: `phet.colorado.edu, khanacademy.org, or ck12.org → search "${topic}"`,
      why: `An interactive resource gives students an additional hands-on opportunity to explore ${topic} — connecting the abstract concepts from Direct Instruction to visual, manipulable representations.`,
      placement: 'During Guided Practice or as a Lesson Closure activity.',
      pauseAndDiscuss: 'Before students begin: "What are you expecting to see? Make a prediction." After 5 minutes: "Was your prediction correct? What surprised you?" After completing: "How does what you discovered here connect to the lesson?" Teacher tip: assign a specific, answerable question rather than open exploration — students with a clear goal engage more productively with interactive tools.',
    };
  }

  // ── Assemble output ────────────────────────────────────────────────────────

  function formatRec(rec: MediaRec, icon: string, type: string): string {
    return [
      `${icon} ${type.toUpperCase()} RESOURCE`,
      `Title:       ${rec.title}`,
      `Platform:    ${rec.platform}`,
      `Duration:    ${rec.duration}`,
      `URL/Search:  ${rec.url}`,
      `Why it fits: ${rec.why}`,
      `Placement:   ${rec.placement}`,
      `Pause & discuss: ${rec.pauseAndDiscuss}`,
    ].join('\n');
  }

  const sections: string[] = [];
  if (mode === 'video' || mode === 'both') {
    sections.push(formatRec(getVideo(), '📺', 'Video'));
  }
  if (mode === 'interactive' || mode === 'both') {
    sections.push(formatRec(getInteractive(), '🖥️', 'Interactive'));
  }
  return sections.join('\n\n');
}

// ── Timing allocation ─────────────────────────────────────────────────────────

/** Distribute total minutes across sections using largest-remainder rounding — guarantees sum === total. */
function allocateTimes(total: number, pcts: number[]): number[] {
  const raw = pcts.map(p => total * p);
  const floor = raw.map(Math.floor);
  const deficit = total - floor.reduce((a, b) => a + b, 0);
  const byRem = raw
    .map((r, i) => ({ i, rem: r - Math.floor(r) }))
    .sort((a, b) => b.rem - a.rem);
  return floor.map((f, i) => f + (byRem.findIndex(x => x.i === i) < deficit ? 1 : 0));
}

/** Compute section times guaranteed to sum exactly to dMin, accounting for multimedia. */
export function computeSectionTimes(dMin: number, mode: MultimediaMode = 'none'): SectionTimes {
  const mmMin = mode === 'none' ? 0 : mode === 'both' ? 15 : mode === 'interactive' ? 12 : 7;
  const remaining = dMin - mmMin;
  // Proportions: warmUp ~15.5%, DI ~26.7%, GP ~20%, IP ~26.7%, ET ~11.1% — sum exactly 100%
  const pcts = [0.155, 0.267, 0.200, 0.267, 0.111];
  const [warmUp, directInstruction, guidedPractice, independentPractice, exitTicket] =
    allocateTimes(remaining, pcts);
  return {
    warmUp,
    directInstruction,
    ...(mmMin > 0 ? { multimedia: mmMin } : {}),
    guidedPractice,
    independentPractice,
    exitTicket,
  };
}

// ── Standard plain-language lookup ───────────────────────────────────────────

/** Return a plain-language description for a recognized CCSS/NGSS code, or undefined. */
function lookupStandardPlainText(standard: string): string | undefined {
  const norm = standard
    .replace(/^CCSS\.MATH\.CONTENT\./i, '')
    .replace(/^CCSS\.ELA-LITERACY\./i, '')
    .replace(/^CCSS\./i, '')
    .replace(/^NGSS\.\s*/i, '')
    .trim();

  const map: Record<string, string> = {
    // Fractions — Number & Operations
    '4.NF.A.1': 'Explain why two fractions are equivalent and generate equivalent fractions.',
    '4.NF.A.2': 'Compare two fractions with different numerators and different denominators by creating common denominators or numerators, or by comparing to a benchmark fraction such as ½.',
    '4.NF.B.3': 'Understand a fraction a/b with a > 1 as a sum of fractions 1/b.',
    '5.NF.A.1': 'Add and subtract fractions with unlike denominators, including mixed numbers, by replacing given fractions with equivalent fractions.',
    '5.NF.A.2': 'Solve word problems involving addition and subtraction of fractions referring to the same whole.',
    '5.NF.B.4': 'Apply and extend understanding of multiplication to multiply a fraction or whole number by a fraction.',
    '5.NF.B.7': 'Apply and extend understanding of division to divide unit fractions by whole numbers and whole numbers by unit fractions.',
    // Operations
    '3.OA.A.1': 'Interpret products of whole numbers as the total number of objects in equal groups.',
    '3.OA.B.5': 'Apply properties of operations as strategies to multiply and divide.',
    '4.NBT.B.5': 'Multiply a whole number up to four digits by a one-digit number, and multiply two two-digit numbers, using strategies based on place value and properties of operations.',
    '5.NBT.B.6': 'Find whole-number quotients and remainders with multi-digit dividends and one-digit divisors, using strategies based on place value.',
    // Geometry & Measurement
    '3.MD.C.7': 'Relate area to the operations of multiplication and addition.',
    '4.MD.A.3': 'Apply the area and perimeter formulas for rectangles in real-world and mathematical problems.',
    '4.MD.C.5': 'Recognize angles as geometric shapes formed wherever two rays share a common endpoint, and understand concepts of angle measurement.',
    '4.G.A.1': 'Draw points, lines, line segments, rays, angles (right, acute, obtuse), and perpendicular and parallel lines.',
    // Algebra & Expressions
    '6.EE.B.5': 'Understand solving an equation or inequality as a process of answering a question: which values make the equation or inequality true?',
    '6.EE.B.7': 'Solve real-world and mathematical problems by writing and solving equations of the form x + p = q and px = q.',
    '7.EE.B.4': 'Use variables to represent quantities in a real-world or mathematical problem, and construct simple equations and inequalities to solve problems.',
    // Ratios & Proportions
    '6.RP.A.1': 'Understand the concept of a ratio and use ratio language to describe a ratio relationship between two quantities.',
    '6.RP.A.2': 'Understand the concept of a unit rate a/b associated with a ratio a:b, and use rate language.',
    '6.RP.A.3': 'Use ratio and rate reasoning to solve real-world and mathematical problems.',
    '7.RP.A.2': 'Recognize and represent proportional relationships between quantities.',
    // Integers & Number System
    '6.NS.C.5': 'Understand that positive and negative numbers describe quantities having opposite directions or values.',
    '7.NS.A.1': 'Apply and extend understanding of addition and subtraction to add and subtract rational numbers.',
    '7.NS.A.2': 'Apply and extend understanding of multiplication and division and of fractions to multiply and divide rational numbers.',
    // Statistics
    '6.SP.B.5': 'Summarize numerical data sets in relation to their context.',
    // ELA Reading
    'RI.4.2': 'Determine the main idea of a text and explain how it is supported by key details; summarize the text.',
    'RI.5.2': 'Determine two or more main ideas of a text and explain how they are supported by key details.',
    'RL.4.1': 'Refer to details and examples in a text when explaining what the text says explicitly and when drawing inferences.',
    'RL.5.1': 'Quote accurately from a text when explaining what the text says explicitly and when drawing inferences.',
    'RI.6.6': "Determine an author's point of view or purpose in a text and explain how it is conveyed in the text.",
    'RL.6.2': 'Determine a theme or central idea of a text and how it is conveyed through particular details; provide a summary distinct from personal opinions.',
    // ELA Writing & Language
    'W.4.1': 'Write opinion pieces on topics or texts, supporting a point of view with reasons and information.',
    'W.5.1': 'Write opinion pieces on topics or texts, supporting a point of view with logically ordered reasons.',
    'L.4.1': 'Demonstrate command of the conventions of standard English grammar and usage when writing or speaking.',
    'L.5.4': 'Determine or clarify the meaning of unknown and multiple-meaning words and phrases based on grade 5 reading and content.',
    // NGSS Science
    'MS-LS2-1': 'Analyze and interpret data to provide evidence for the effects of resource availability on organisms and populations of organisms in an ecosystem.',
    'MS-LS2-3': 'Develop a model to describe the cycling of matter and flow of energy among living and nonliving parts of an ecosystem.',
    'MS-PS2-2': 'Plan an investigation to provide evidence that the change in an object\'s motion depends on the sum of the forces on the object and the mass of the object.',
  };

  return map[norm] ?? map[norm.toUpperCase()];
}

// ── Content quality validation ────────────────────────────────────────────────

/** Remove double punctuation, trailing whitespace, and stray double spaces. */
function sanitizeContent(text: string): string {
  return text
    .split('\n').map(line => line.trimEnd()).join('\n')  // trailing spaces per line
    .replace(/\.{2,}/g, '.')      // double/triple periods → single
    .replace(/!{2,}/g, '!')       // double exclamation marks → single
    .replace(/\?{2,}/g, '?')      // double question marks → single
    .replace(/\.,/g, '.')         // period-comma → period
    .replace(/\. \./g, '.')       // period space period → period
    .replace(/ {2,}/g, ' ')       // multiple spaces → single
    .trim();
}

/** Apply sanitizeContent to all text fields of the lesson in place. */
function sanitizeLesson(lesson: GeneratedLesson): void {
  const textFields: (keyof GeneratedLesson)[] = [
    'objective', 'successCriteria', 'materials', 'vocabulary',
    'warmUp', 'directInstruction', 'guidedPractice', 'independentPractice',
    'formativeAssessment', 'exitTicket', 'differentiation', 'extensionActivity',
    'homework', 'reflectionNotes',
  ];
  for (const key of textFields) {
    const val = lesson[key];
    if (typeof val === 'string') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (lesson as any)[key] = sanitizeContent(val);
    }
  }
  if (lesson.teacherPrep)         lesson.teacherPrep         = sanitizeContent(lesson.teacherPrep);
  if (lesson.requiredMaterials)   lesson.requiredMaterials   = sanitizeContent(lesson.requiredMaterials);
  if (lesson.optionalMaterials)   lesson.optionalMaterials   = sanitizeContent(lesson.optionalMaterials);
  if (lesson.multimediaResources) lesson.multimediaResources = sanitizeContent(lesson.multimediaResources);
  if (lesson.tieredPractice) {
    lesson.tieredPractice.belowLevel = sanitizeContent(lesson.tieredPractice.belowLevel);
    lesson.tieredPractice.onLevel    = sanitizeContent(lesson.tieredPractice.onLevel);
    lesson.tieredPractice.aboveLevel = sanitizeContent(lesson.tieredPractice.aboveLevel);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// POST-GENERATION CONTENT VALIDATOR
// ═══════════════════════════════════════════════════════════════════════════════

/** Phrases that indicate planning placeholders rather than completed lesson content. */
const PLACEHOLDER_PHRASES = [
  '4–6 problems', '4-6 problems', '6–8 problems', '6-8 problems',
  '8–12 problems', '8-12 problems', '10-12 problems', '10–12 problems',
  'quantity per grade-level norms',
  'students work independently on a practice set',
  'minimum 6 problems', 'minimum of six problems', 'minimum of 6 problems',
  'assigned practice problems on',
  'worked example 1', 'second application with new numbers',
  'apply with one added constraint', 'work through one complete example',
];

/**
 * Per-topic excluded concept signals.
 * A section FAILS when any of these phrases appear in a lesson whose mathTopic
 * does not match the concept — e.g., "reciprocal" in a fractions-equivalent lesson.
 */
const EXCLUDED_CONCEPT_SIGNALS: Partial<Record<MathTopic, { phrase: string; concept: string }[]>> = {
  'fractions-equivalent': [
    { phrase: 'reciprocal', concept: 'fraction division (wrong topic)' },
    { phrase: 'flip and multiply', concept: 'fraction division (wrong topic)' },
    { phrase: 'invert and multiply', concept: 'fraction division (wrong topic)' },
    { phrase: 'dividing fractions', concept: 'fraction division (wrong topic)' },
    { phrase: 'a fraction of a fraction', concept: 'fraction multiplication (wrong topic)' },
  ],
  'fractions-multiply-divide': [
    { phrase: 'equivalent fraction', concept: 'fraction equivalence — check routing' },
  ],
};

/**
 * Post-generation validator: checks every section for placeholder language and
 * excluded-concept contamination. Stores structured results in lesson.validationReport
 * and logs a warning for every section that fails.
 */
function validateLesson(lesson: GeneratedLesson, inputs: LessonInputs): ValidationSectionResult[] {
  const mathType: MathTopic | null = inputs.subject === 'Math'
    ? mathTopic(inputs.skillToTeach.toLowerCase())
    : null;

  const excludedSignals: { phrase: string; concept: string }[] =
    mathType ? (EXCLUDED_CONCEPT_SIGNALS[mathType] ?? []) : [];
  const requiredConcept = mathType ?? 'general';

  const allSections: [string, string][] = [
    ['successCriteria',     lesson.successCriteria     ?? ''],
    ['vocabulary',          lesson.vocabulary          ?? ''],
    ['warmUp',              lesson.warmUp              ?? ''],
    ['directInstruction',   lesson.directInstruction   ?? ''],
    ['guidedPractice',      lesson.guidedPractice      ?? ''],
    ['independentPractice', lesson.independentPractice ?? ''],
    ['formativeAssessment', lesson.formativeAssessment ?? ''],
    ['exitTicket',          lesson.exitTicket          ?? ''],
    ['homework',            lesson.homework            ?? ''],
    ['differentiation',     lesson.differentiation     ?? ''],
  ];

  const lower = (s: string) => s.toLowerCase();
  const results: ValidationSectionResult[] = [];

  for (const [name, text] of allSections) {
    if (!text) continue;
    const tl = lower(text);
    const placeholders = PLACEHOLDER_PHRASES.filter(p => tl.includes(lower(p)));
    const excluded = excludedSignals.filter(sig => tl.includes(sig.phrase.toLowerCase()));
    const hasPlaceholders = placeholders.length > 0;
    const excludedConcepts = excluded.map(e => `"${e.phrase}" → ${e.concept}`);
    const failed = hasPlaceholders || excludedConcepts.length > 0;

    const result: ValidationSectionResult = {
      section: name,
      status: failed ? 'FAIL' : 'PASS',
      requiredConcept,
      excludedConceptsDetected: excludedConcepts,
      hasPlaceholders,
      notes: [
        ...placeholders.map(p => `Placeholder: "${p}"`),
        ...excludedConcepts.map(c => `Excluded: ${c}`),
      ].join(' | ') || 'OK',
    };
    results.push(result);

    if (failed) {
      console.warn(
        `[TeachBud Validator] ${name}: FAIL\n` +
        `  Skill: "${inputs.skillToTeach}"\n` +
        `  Required concept: ${requiredConcept}\n` +
        (excludedConcepts.length ? `  Excluded concepts detected: ${excludedConcepts.join(' | ')}\n` : '') +
        (placeholders.length ? `  Placeholder phrases: ${placeholders.join(', ')}\n` : '') +
        `  → Add this topic to the generator map or check routing.`
      );
    }
  }

  // ── Tiered practice completeness (all three tiers are required when present) ──
  if (lesson.tieredPractice) {
    const tiers: [string, string][] = [
      ['tieredPractice.belowLevel', lesson.tieredPractice.belowLevel ?? ''],
      ['tieredPractice.onLevel',    lesson.tieredPractice.onLevel    ?? ''],
      ['tieredPractice.aboveLevel', lesson.tieredPractice.aboveLevel ?? ''],
    ];
    for (const [name, text] of tiers) {
      const isEmpty = !text || text.trim().length < 20;
      if (isEmpty) {
        results.push({
          section: name,
          status: 'FAIL',
          requiredConcept,
          excludedConceptsDetected: [],
          hasPlaceholders: false,
          notes: 'Section is empty — no student problems generated',
        });
        console.warn(`[TeachBud Validator] ${name}: FAIL — section is empty (required for tiered lessons)`);
        continue;
      }
      const tl = lower(text);
      const placeholders = PLACEHOLDER_PHRASES.filter(p => tl.includes(lower(p)));
      const excluded = excludedSignals.filter(sig => tl.includes(sig.phrase.toLowerCase()));
      const hasPlaceholders = placeholders.length > 0;
      const excludedConcepts = excluded.map(e => `"${e.phrase}" → ${e.concept}`);
      const failed = hasPlaceholders || excludedConcepts.length > 0;
      results.push({
        section: name,
        status: failed ? 'FAIL' : 'PASS',
        requiredConcept,
        excludedConceptsDetected: excludedConcepts,
        hasPlaceholders,
        notes: [
          ...placeholders.map(p => `Placeholder: "${p}"`),
          ...excludedConcepts.map(c => `Excluded: ${c}`),
        ].join(' | ') || 'OK',
      });
      if (failed) {
        console.warn(
          `[TeachBud Validator] ${name}: FAIL\n` +
          `  Skill: "${inputs.skillToTeach}"\n` +
          `  Required concept: ${requiredConcept}\n` +
          (excludedConcepts.length ? `  Excluded concepts detected: ${excludedConcepts.join(' | ')}\n` : '') +
          (placeholders.length ? `  Placeholder phrases: ${placeholders.join(', ')}\n` : '') +
          `  → Tiered practice tier must be regenerated.`
        );
      }
    }
  }

  lesson.validationReport = results;
  return results;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ENTRY POINT
// ═══════════════════════════════════════════════════════════════════════════════

export function generateLesson(inputs: LessonInputs): GeneratedLesson {
  // ── ALIGNMENT RULE ─────────────────────────────────────────────────────────
  // The Skill to Teach is the authoritative instructional target.
  // Do not broaden it, replace it, reinterpret it, or substitute a related skill.
  // Every section below must directly support inputs.skillToTeach.
  // ───────────────────────────────────────────────────────────────────────────
  const ctx = makeCtx(inputs);
  const { title, grade, gradeLabel, subject, topic, standard, obj } = ctx;
  const { skillToTeach, duration, tieredPractice, multimediaMode = 'none' } = inputs;

  const lesson: GeneratedLesson = {
    title,
    gradeLabel,
    grade,
    subject,
    topic,
    skillToTeach,     // passed through UNCHANGED — the authoritative target
    standard: standard.trim(),
    duration,
    objective: `Students will be able to ${obj}.`,
    successCriteria: generateSuccessCriteria(ctx),
    materials: generateMaterials(ctx),
    vocabulary: generateVocabulary(ctx),
    warmUp: generateWarmUp(ctx),
    directInstruction: generateDirectInstruction(ctx),
    guidedPractice: generateGuidedPractice(ctx),
    independentPractice: generateIndependentPractice(ctx),
    formativeAssessment: generateFormativeAssessment(ctx),
    exitTicket: generateExitTicket(ctx),
    differentiation: generateDifferentiation(ctx),
    extensionActivity: generateExtensionActivity(ctx),
    homework: generateHomework(ctx),
    reflectionNotes: generateReflectionNotes(),
  };

  // Rule 10 — Tiered Independent Practice
  if (tieredPractice) {
    lesson.tieredPractice = generateTieredIndependentPractice(ctx);
  }

  // Rule 11 — Multimedia Integration
  if (multimediaMode && multimediaMode !== 'none') {
    lesson.multimediaResources = generateMultimediaResources(ctx, multimediaMode);
  }

  // Permanent new sections (Rules per uploaded spec)
  lesson.requiredMaterials = generateRequiredMaterials(ctx);
  lesson.optionalMaterials = generateOptionalMaterials(ctx, multimediaMode);
  lesson.teacherPrep = generateTeacherPrep(ctx);
  lesson.lessonAtAGlance = generateLessonAtAGlance(ctx, lesson);

  // Curriculum source traceability
  if (inputs.curriculumSource) {
    lesson.curriculumSource = inputs.curriculumSource;
  }

  // Section times — guaranteed to sum to lesson duration
  lesson.sectionTimes = computeSectionTimes(ctx.dMin, multimediaMode);

  // Standard plain-language description (when standard is a recognized code)
  if (standard.trim()) {
    const plainText = lookupStandardPlainText(standard.trim());
    if (plainText) lesson.standardPlainText = plainText;
  }

  // Content quality validation — remove double punctuation, trailing spaces, etc.
  sanitizeLesson(lesson);

  // Extract structured blueprint — single source of truth for all companion generators
  lesson.blueprint = extractBlueprint(lesson);

  // Warn when any section still contains generator-placeholder language
  validateLesson(lesson, inputs);

  // Attach structured spec so all companion generators have the full context
  lesson.spec = buildLessonSpec(inputs);

  return lesson;
}

// ── AI lesson assembler ───────────────────────────────────────────────────────

/**
 * Assembles a GeneratedLesson from AI-generated content + LessonInputs.
 *
 * Called after a successful /api/lesson/generate response. Applies exactly the
 * same post-processing pipeline as generateLesson() — section times, at-a-glance,
 * curriculum source, standard plain text, content sanitization, blueprint
 * extraction, and validation — so companion generators (activity sheet,
 * assessment, presentation) work identically with AI-generated lessons.
 */
export function assembleAiLesson(
  inputs: LessonInputs,
  ai: Record<string, unknown>,
): GeneratedLesson {
  const ctx = makeCtx(inputs);
  const { grade, gradeLabel, subject, standard, obj } = ctx;
  const { skillToTeach, duration, tieredPractice: wantTiered, multimediaMode = 'none' } = inputs;

  const str = (key: string, fallback = ''): string =>
    typeof ai[key] === 'string' ? (ai[key] as string) : fallback;

  const lesson: GeneratedLesson = {
    title:               str('title', ctx.title),
    gradeLabel,
    grade,
    subject,
    topic:               str('topic', ctx.topic),
    skillToTeach,                           // passed through UNCHANGED
    standard:            standard.trim(),
    duration,
    objective:           str('objective', `Students will be able to ${obj}.`),
    successCriteria:     str('successCriteria'),
    materials:           str('materials'),
    vocabulary:          str('vocabulary'),
    warmUp:              str('warmUp'),
    directInstruction:   str('directInstruction'),
    guidedPractice:      str('guidedPractice'),
    independentPractice: str('independentPractice'),
    formativeAssessment: str('formativeAssessment'),
    exitTicket:          str('exitTicket'),
    differentiation:     str('differentiation'),
    extensionActivity:   str('extensionActivity'),
    homework:            str('homework'),
    reflectionNotes:     str('reflectionNotes'),
    teacherPrep:         str('teacherPrep'),
    requiredMaterials:   str('requiredMaterials'),
    optionalMaterials:   str('optionalMaterials'),
  };

  // Tiered practice (when requested)
  if (wantTiered) {
    const tp = ai['tieredPractice'] as Record<string, unknown> | undefined;
    lesson.tieredPractice = {
      belowLevel: tp ? String(tp['belowLevel'] ?? '') : '',
      onLevel:    tp ? String(tp['onLevel'] ?? '') : '',
      aboveLevel: tp ? String(tp['aboveLevel'] ?? '') : '',
    };
  }

  // Multimedia (supplements — generated locally; not AI content)
  if (multimediaMode && multimediaMode !== 'none') {
    lesson.multimediaResources = generateMultimediaResources(ctx, multimediaMode);
  }

  // Curriculum source traceability
  if (inputs.curriculumSource) lesson.curriculumSource = inputs.curriculumSource;

  // Section times (pure arithmetic — same as generateLesson)
  lesson.sectionTimes = computeSectionTimes(ctx.dMin, multimediaMode);

  // Standard plain-language description
  const plainText = lookupStandardPlainText(standard.trim());
  if (plainText) lesson.standardPlainText = plainText;

  // Content quality sanitization (strips double punctuation, trailing spaces, etc.)
  sanitizeLesson(lesson);

  // Lesson at a glance (derived from AI-generated section content)
  lesson.lessonAtAGlance = generateLessonAtAGlance(ctx, lesson);

  // Blueprint extraction — single source of truth for all companion generators.
  // Parses the AI-generated numbered lists and worked examples into structured
  // items (studentContent + answer) so activity sheets, assessments, and
  // presentations all get on-target, skill-specific content.
  lesson.blueprint = extractBlueprint(lesson);

  // Post-generation validation (logs warnings; does not block)
  lesson.validationReport = validateLesson(lesson, inputs);

  // Attach structured spec — consumed by all companion generators
  lesson.spec = buildLessonSpec(inputs);

  // Attach AI alignment validation results from the server response
  if (typeof ai['_validationPassed'] === 'boolean') {
    lesson.alignmentValidated = ai['_validationPassed'] as boolean;
  }
  const regenSections = ai['_regeneratedSections'];
  if (Array.isArray(regenSections)) {
    lesson.regeneratedSections = regenSections as string[];
  }

  // Blueprint completeness gate results — surfaced in LessonPlanner as a warning banner
  if (typeof ai['_blueprintIncomplete'] === 'boolean') {
    lesson.blueprintIncomplete = ai['_blueprintIncomplete'] as boolean;
  }
  const incompleteFields = ai['_incompleteFields'];
  if (Array.isArray(incompleteFields)) {
    lesson.incompleteFields = incompleteFields as string[];
  }

  // Build generation trace — consumed by the developer report panel in the UI
  const bpSections = lesson.blueprint
    ? [
        lesson.blueprint.warmUp, lesson.blueprint.directInstruction,
        lesson.blueprint.guidedPractice, lesson.blueprint.independentPractice,
        lesson.blueprint.exitTicket, lesson.blueprint.challenge,
        lesson.blueprint.homework, lesson.blueprint.belowLevel,
        lesson.blueprint.onLevel, lesson.blueprint.aboveLevel,
      ]
    : [];
  lesson.trace = {
    lessonSource: 'ai-endpoint',
    specPresent: !!lesson.spec,
    blueprintExtracted: !!lesson.blueprint,
    blueprintItemCount: bpSections.reduce((n, s) => n + (s?.items.length ?? 0), 0),
    validationResult: lesson.alignmentValidated === true  ? 'passed'
                    : lesson.alignmentValidated === false ? 'corrected'
                    : 'skipped',
    sectionsRegenerated: lesson.regeneratedSections ?? [],
    // generationTimeMs stamped in LessonPlanner after the outer API call returns
  };

  return lesson;
}

// ── Plain-text serialiser ─────────────────────────────────────────────────────

export function lessonToText(l: GeneratedLesson): string {
  const dMin = parseInt(l.duration, 10) || 45;
  const t = (pct: number) => Math.round(dMin * pct);
  const hr = '─'.repeat(60);
  const scopeLabel = (s: string) =>
    ({ my: 'My Curriculum', team: 'Team / PLC Curriculum', school: 'School Curriculum', district: 'District Curriculum' })[s] ?? s;

  const lines: string[] = [
    // ── Curriculum Source (curriculum-based lessons only) ──────────────────
    ...(l.curriculumSource ? [
      'CURRICULUM SOURCE', hr,
      `Curriculum: ${l.curriculumSource.title}`,
      ...(l.curriculumSource.publisher   ? [`Publisher:  ${l.curriculumSource.publisher}`]   : []),
      ...(l.curriculumSource.gradeLevel  ? [`Grade:      ${l.curriculumSource.gradeLevel}`]   : []),
      ...(l.curriculumSource.unitInfo    ? [`Unit/Module: ${l.curriculumSource.unitInfo}`]    : []),
      ...(l.curriculumSource.fileName    ? [`Source File: ${l.curriculumSource.fileName}`]    : []),
      ...(l.curriculumSource.sourceUrl   ? [`Source URL:  ${l.curriculumSource.sourceUrl}`]   : []),
      `Category:   ${scopeLabel(l.curriculumSource.scope)}`,
      `Status:     Enhanced by TeachBud`,
      '', '',
    ] : []),
    // ── Lesson at a Glance ─────────────────────────────────────────────────
    ...(l.lessonAtAGlance ? [
      'LESSON AT A GLANCE', hr,
      `Objective: ${l.lessonAtAGlance.objective}`,
      `Duration:  ${l.lessonAtAGlance.duration}`,
      '',
      'Student Tasks:',
      ...l.lessonAtAGlance.mainStudentTasks.map(s => `  • ${s}`),
      '',
      'Teacher Actions:',
      ...l.lessonAtAGlance.mainTeacherActions.map(a => `  • ${a}`),
      '',
      'Key Resources:',
      ...l.lessonAtAGlance.requiredResources.map(r => `  • ${r}`),
      '', '',
    ] : []),
    // ── Full Lesson Plan ───────────────────────────────────────────────────
    'LESSON PLAN', hr,
    `Title:    ${l.title}`,
    `Grade:    ${l.gradeLabel}`,
    `Subject:  ${l.subject}`,
    `Topic:    ${l.topic}`,
    ...(l.standard ? [
      `Standard: ${l.standard}`,
      ...(l.standardPlainText ? [`          ${l.standardPlainText}`] : []),
    ] : []),
    `Duration: ${l.duration}`,
    '',
    'LEARNING OBJECTIVE',
    l.objective,
    '',
    'SUCCESS CRITERIA',
    l.successCriteria,
    '',
    // Materials — split view if new fields exist, legacy view otherwise
    ...(l.requiredMaterials ? [
      'REQUIRED MATERIALS',
      l.requiredMaterials,
      '',
      'OPTIONAL / SUPPLEMENTAL MATERIALS',
      l.optionalMaterials ?? '',
      '',
    ] : [
      'MATERIALS',
      l.materials,
      '',
    ]),
    'VOCABULARY',
    l.vocabulary,
    '',
    // Teacher Preparation
    ...(l.teacherPrep ? [
      'TEACHER PREPARATION (BEFORE CLASS)',
      l.teacherPrep,
      '',
    ] : []),
    `WARM-UP / ANTICIPATORY SET  (${l.sectionTimes ? l.sectionTimes.warmUp : t(0.155)} min)`,
    l.warmUp,
    '',
    `DIRECT INSTRUCTION  (${l.sectionTimes ? l.sectionTimes.directInstruction : t(0.267)} min)`,
    l.directInstruction,
    '',
    `GUIDED PRACTICE  (${l.sectionTimes ? l.sectionTimes.guidedPractice : t(0.200)} min)`,
    l.guidedPractice,
    '',
    ...(l.tieredPractice ? [
      `INDEPENDENT PRACTICE — BELOW LEVEL  (${l.sectionTimes ? Math.round(l.sectionTimes.independentPractice / 3) : t(0.089)} min)`,
      l.tieredPractice.belowLevel,
      '',
      `INDEPENDENT PRACTICE — ON LEVEL  (${l.sectionTimes ? Math.round(l.sectionTimes.independentPractice / 3) : t(0.089)} min)`,
      l.tieredPractice.onLevel,
      '',
      `INDEPENDENT PRACTICE — ABOVE LEVEL  (${l.sectionTimes ? Math.round(l.sectionTimes.independentPractice / 3) : t(0.089)} min)`,
      l.tieredPractice.aboveLevel,
    ] : [
      `INDEPENDENT PRACTICE  (${l.sectionTimes ? l.sectionTimes.independentPractice : t(0.267)} min)`,
      l.independentPractice,
    ]),
    '',
    'FORMATIVE ASSESSMENT (DURING LESSON)',
    l.formativeAssessment,
    '',
    `EXIT TICKET  (${l.sectionTimes ? l.sectionTimes.exitTicket : t(0.111)} min)`,
    l.exitTicket,
    '',
    'DIFFERENTIATION RESOURCES',
    'Note: Primary scaffolding and extension are embedded throughout this lesson\'s activities (progressive task sequencing, embedded questioning, and low-floor/high-ceiling task design). The strategies below provide additional targeted support for individual student needs.',
    l.differentiation,
    '',
    'EXTENSION ACTIVITY',
    l.extensionActivity,
    '',
    'HOMEWORK',
    l.homework,
    '',
    ...(l.multimediaResources ? [
      'MULTIMEDIA RESOURCES',
      l.multimediaResources,
      '',
    ] : []),
    'TEACHER REFLECTION NOTES',
    l.reflectionNotes,
    '',
    hr,
    `Generated by TeachBud  ·  ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
  ];
  return lines.join('\n');
}
